import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  TrendingUp,
  PieChart,
  Send,
  ArrowDownToLine,
  ShoppingCart,
  ReceiptText,
  UserCircle,
  CreditCard,
  Wallet,
  Gift,
  RotateCcw,
  Banknote,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Download,
  Sparkles,
  ClipboardList,
  BarChart3,
  FolderOpen,
  Tag,
  FileText,
  Store,
  Star,
  User,
  Clock,
  Search,
  Eye,
  Activity,
  PackageMinus,
  DollarSign,
  ArrowUpDown,
  Milk,
  Percent,
  Save,
  Check,
  X,
  Settings,
  Users,
  History,
  Truck,
  ArrowDown,
  ArrowUp,
  Pencil,
  Scale,
  SearchX,
  ArrowLeftRight,
  Ticket,
  Receipt,
  Package,
} from 'lucide-react';
import { downloadCsv, reportFilename } from '@/lib/report-csv';
import { reportingTodayInput } from '@/lib/report-timezone';
import { dailyInputToDate, dateToDailyInput, defaultDailyRangeWithTime, initialDailyRange } from '@/lib/report-range';
import { idFilterParam } from '@/lib/report-filters';
import { PosSalesTrendsReport } from './reports/sales-trends-report';
import { cn, formatDateTime } from '@/lib/utils';
import { PageHeader } from '@/components/shared/page-header';
import { EmptyState } from '@/components/shared/empty-state';
import { LocationBadge } from '@/components/shared/location-badge';
import { StatCard } from '@/components/shared/stat-card';
import { StatusBadge } from '@/components/shared/status-badge';
import { useModules } from '@/hooks/use-modules';
import { useFillViewportHeight } from '@/hooks/use-fill-viewport-height';
import { useLocationStore } from '@/stores/location-store';
import { useProductsPosConfig } from '@/hooks/use-pos-config';
import { useProducts, useProductPriceHistory } from '@/hooks/use-products';
import { useUsers } from '@/hooks/use-users';
import { useLocations } from '@/hooks/use-locations';
import { useSPDashboard, useSPPayments, type SPDashboardData, type SPPayment } from '@/hooks/use-send-payments';
import { useRPDashboard, useRPPayments, type RPDashboardData, type RPListSummary } from '@/hooks/use-receive-payments';
import { useSaleTransactions } from '@/hooks/use-sale-transactions';
import { ExpandedTransactionDetail } from './dashboard/collections-page';
import {
  usePosTransactions,
  usePosCustomers,
  usePosCustomersByProduct,
  usePosVendorByProduct,
  usePosPurchaseHistoryCustomers,
  usePosCustomerPurchaseHistory,
  usePosCashMovements,
  usePosCouponUsageCoupons,
  usePosCouponUsageRedemptions,
  usePosExtraItems,
  usePosVariableItems,
  usePosReportSummary,
  usePosPaymentsPerStation,
  usePosTotalDailySales,
  usePosTotalDailyDepartmentSales,
  usePosReportDepartments,
  usePosTotalDailyCategorySales,
  usePosReportCategories,
  usePosReportTags,
  usePosInventoryMovement,
  usePosDailyItemMovements,
  usePosInventoryValuation,
  usePosPriceUpdates,
  usePosPriceChanges,
  type PosPriceChangeRow,
  usePosCardAttempts,
  useMarkCardAttemptRefunded,
  type PosCardAttemptRow,
  usePosInventoryTrail,
  usePosTaxReport,
  usePosTopItems,
  usePosSalesByVendorVendors,
  usePosSalesByVendorProducts,
  usePosReportVendors,
  usePosOrderDetail,
  usePosRefunds,
  usePosStations,
  usePosCashReconciliations,
  type PosCashReconciliationRow,
  usePosStoreCredits,
  usePosStoreCreditDetail,
  usePosReportEmployees,
  usePosWorkTimeEmployees,
  usePosWorkTimeDays,
  usePosSalesByCashierCashiers,
  usePosSalesByCashierOrders,
  usePosDiscountReport,
  usePosPromotionReport,
  usePosDigitalTransactions,
  usePosTills,
  withLocalDateRange,
  type PosDigitalTransactionRow,
  type PosDigitalTxnSortBy,
  type PosTillRow,
  type PosTillSortBy,
  type PosRefundRow,
  type PosRefundsTab,
  type PosRefundsParams,
  type PosRefundsTotals,
  type PosRefundsMethodTotal,
  type PosSalesByCashierCashier,
  type PosSalesByCashierOrder,
  type PosWorkTimeEmployee,
  type PosWorkTimeDay,
  type PosWorkTimePunch,
  type PosStoreCreditRow,
  type PosStoreCreditAuditRow,
  type PosDiscountRow,
  type PosDiscountType,
  type PosDiscountSortBy,
  type PosPromotionUsageRow,
  type PosPromotionSortBy,
  type PosTransaction,
  type PosCustomer,
  type PosCustomersByProductRow,
  type PosProductVendor,
  type PosPurchaseHistoryCustomer,
  type PosPurchaseHistoryData,
  type PosPurchaseHistoryOrder,
  type PosCashMovementRow,
  type PosCouponUsageCoupon,
  type PosCouponUsageKind,
  type PosCouponUsageRedemption,
  type PosExtraItemRow,
  type PosVariableItemRow,
  type PosReportSummary,
  type PosPaymentsPerStationRow,
  type PosTotalDailySalesRow,
  type PosDepartmentSalesRow,
  type PosCategorySalesRow,
  type PosInventoryMovementRow,
  type PosDailyItemMovementRow,
  type PosInventorySortBy,
  type PosInventoryValuationRow,
  type PosInventoryValuationSortBy,
  type PosPriceUpdateRow,
  type PosPriceUpdatesSortBy,
  type PosInventoryTrailRow,
  type PosInventoryTrailSortBy,
  type PosTaxDetailRow,
  type PosFeeDetailRow,
  type PosTopItemRow,
  type PaginatedResponse,
  type PosSalesByVendorVendor,
  type PosSalesByVendorProduct,
  type PosOrderItem,
} from '@/hooks/use-pos-reports';
import { getReportingTimeZone, reportingDayBoundaryIso } from '@/lib/report-timezone';
import { useReportingTimeZone } from '@/hooks/use-reporting-timezone';
import { ReportingTimeZoneBar } from '@/pages/reports/reporting-timezone-bar';
import { Modal } from '@/components/ui/modal';
import { MultiSelect } from '@/components/ui/multi-select';
import { Badge } from '@/components/ui/badge';
import { cashNet, formatMDY, NOTE_VARIANT, systemNote } from '@/lib/cash-reconciliation';
import { SupernovaDatePicker, type DateRange } from '@/components/ui/date-picker';
import { DateInput } from '@/components/ui/date-input';
import { MonthInput } from '@/components/ui/month-input';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { PosCreditAccountsTab } from './reports/credit-accounts-tab';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api-client';
import { usePersistentTab, useHashParam } from '@/hooks/use-persistent-tab';
import { useAuthStore } from '@/stores/auth-store';
import { toast } from '@/stores/toast-store';
import { usePermissions } from '@/hooks/use-permissions';
import type { PermissionKey } from '@supernova/shared';
import {
  useEffectiveReportHidden,
  useReportVisibilityMatrix,
  useUpdateReportVisibility,
} from '@/hooks/use-report-visibility';

type ReportTab = 'send' | 'receive' | 'pos';
const REPORT_TABS: readonly ReportTab[] = ['receive', 'pos', 'send'];

/** The View that opens each report tab. */
const REPORT_TAB_VIEW: Record<ReportTab, PermissionKey> = {
  receive: 'reports.rp.view',
  pos: 'reports.pos.view',
  send: 'reports.sp.view',
};

/** Renders an export or download control only for holders of POS reports' Export. */
function PosExportOnly({ children }: { children: React.ReactNode }) {
  return usePermissions().has('reports.pos.export') ? <>{children}</> : null;
}

// POS-report date formatters. Both render in the business reporting timezone
// (user override → browser zone → UTC) so a late-night order lands on the
// correct local day, and so the on-screen tables and their CSV exports always
// show the identical value. Keep the display format in sync between the two.
function formatPosDateTime(value: string | number | Date | null | undefined, tz?: string): string {
  if (value == null || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: tz ?? getReportingTimeZone(),
  });
}

function formatPosDate(value: string | number | Date | null | undefined, tz?: string): string {
  if (value == null || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: tz ?? getReportingTimeZone(),
  });
}

/** Wall-clock time only, e.g. "9:05 AM", in the reporting zone. */
function formatPosTime(value: string | number | Date | null | undefined, tz?: string): string {
  if (value == null || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz ?? getReportingTimeZone(),
  });
}

/** Render a business-local calendar day ('YYYY-MM-DD', already computed in the
 *  reporting zone by the API) as "Aug 3, 2026" WITHOUT any timezone conversion.
 *  Parsing a bare 'YYYY-MM-DDThh:mm' string would bind it to the browser's zone
 *  and then shift the day when re-displayed in another zone — so we anchor to
 *  UTC noon and format in UTC, keeping the label identical to the stored day. */
function formatCalendarDate(ymd: string): string {
  const d = new Date(`${ymd}T12:00:00Z`);
  if (isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** Format a duration in seconds as "Xh Ym" (e.g. 27900 → "7h 45m"). Rounds to
 *  the nearest minute; sub-minute durations show "0m". Used by the Employee
 *  Work Time report for worked/break totals. */
function formatWorkDuration(totalSeconds: number): string {
  const totalMinutes = Math.round((totalSeconds ?? 0) / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  const abs = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // Negatives read "- $10.80" (sign before the $), never "$-10.80".
  return num < 0 ? `- $${abs}` : `$${abs}`;
}

// Money with an explicit +/− prefix and no doubled sign, e.g.
// formatSignedCurrency(-16.48, '-') → "- $16.48". Used in the summary cards
// where the sign conveys direction (tax/tips add, discount/refund deduct) so a
// raw "$-16.48" never leaks through — the magnitude is always shown positive.
function formatSignedCurrency(value: number, sign: '+' | '-'): string {
  return `${sign} ${formatCurrency(Math.abs(value))}`;
}

// Friendly labels for pos.cash_drawer_events.type. "Cash Collected" is the POS
// "cash_sale" event; the raw type name is prettified for anything unmapped.
const CASH_DRAWER_LABELS: Record<string, string> = {
  open_drawer: 'Cash Open',
  cash_open: 'Cash Open',
  cash_sale: 'Cash Collected',
  change_given: 'Change Given',
  cash_in: 'Cash In',
  cash_out: 'Cash Out',
  paid_in: 'Paid In',
  paid_out: 'Paid Out',
  bank_drop: 'Bank Drop',
  drop: 'Bank Drop',
};
function cashDrawerLabel(type: string): string {
  return CASH_DRAWER_LABELS[type] ?? type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function ChartPlaceholder({ title, icon: Icon }: { title: string; icon: React.ElementType }) {
  return (
    <div className="rounded-xl border border-border bg-white p-6">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <div className="mt-4 flex h-48 flex-col items-center justify-center gap-2 text-muted-foreground">
        <Icon className="h-8 w-8 opacity-30" />
        <p className="text-sm">Chart coming soon</p>
      </div>
    </div>
  );
}

function InactiveModuleBanner() {
  return (
    <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-2.5">
      <p className="text-sm font-medium text-yellow-800">Module inactive — showing historical data</p>
    </div>
  );
}

// Clickable card that opens the Transactions History on its own page,
// matching the POS reports card pattern.
function TransactionsHistoryNavCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-start gap-3 rounded-xl border border-border bg-white p-4 text-left transition-colors hover:border-primary/40 hover:bg-primary/5"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <History className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Transactions History</h3>
          <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          View, filter, and search the full transaction history.
        </p>
      </div>
    </button>
  );
}

/** `report` deep-link value that opens a payments module's Transactions History. */
const TXN_HISTORY_REPORT_ID = 'transactions-history';

function SendPaymentsReport({
  sp,
  isLoading,
  isActive,
}: {
  sp: SPDashboardData | undefined;
  isLoading: boolean;
  isActive: boolean;
}) {
  // Lives in the URL (`#/reports?tab=send&report=transactions-history`) so
  // the Send Payments → Transactions info banner can open it directly.
  const [reportId, setReportId] = useHashParam('report');
  const showTxnHistory = reportId === TXN_HISTORY_REPORT_ID;

  if (!sp) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {['Active Payees', 'PO Value', 'Total Payments', 'Payment Amount'].map((title) => (
          <StatCard key={title} title={title} value="..." subtitle="Loading..." />
        ))}
      </div>
    );
  }

  if (showTxnHistory) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Transactions History"
          description="View, filter, and search the full transaction history."
          onBack={() => setReportId(null)}
        />
        <SPTransactionsHistoryCard hideHeading />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!isActive && <InactiveModuleBanner />}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Active Payees"
          value={isLoading ? '...' : String(sp.payees.active)}
          subtitle={`${sp.payees.total} total payees`}
        />
        <StatCard
          title="PO Value"
          value={isLoading ? '...' : formatCurrency(sp.purchaseOrders.totalValue)}
          subtitle={`${sp.purchaseOrders.total} purchase orders`}
        />
        <StatCard
          title="Total Payments"
          value={isLoading ? '...' : String(sp.payments.total)}
          subtitle="Vendor payments"
        />
        <StatCard
          title="Payment Amount"
          value={isLoading ? '...' : formatCurrency(sp.payments.totalAmount)}
          subtitle="Total paid"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-white p-6">
          <h3 className="mb-4 text-sm font-semibold text-foreground">Payments by Status</h3>
          {sp.payments.byStatus.length === 0 ? (
            <EmptyState
              icon={CreditCard}
              title="No payment data yet"
              description="Payments will be grouped by status once they are sent."
              className="py-8"
            />
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border text-left text-xs font-medium text-muted-foreground">
                  <th className="pb-2">Status</th>
                  <th className="pb-2 text-right">Count</th>
                  <th className="pb-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {sp.payments.byStatus.map((row) => (
                  <tr key={row.status} className="border-b border-border/50 last:border-0">
                    <td className="py-2.5">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="py-2.5 text-right text-muted-foreground">{row.count}</td>
                    <td className="py-2.5 text-right font-medium">{formatCurrency(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="rounded-xl border border-border bg-white p-6">
          <h3 className="mb-4 text-sm font-semibold text-foreground">Top Vendors</h3>
          {sp.topVendors.length === 0 ? (
            <EmptyState
              icon={Truck}
              title="No vendor data yet"
              description="The vendors you pay most will appear here."
              className="py-8"
            />
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border text-left text-xs font-medium text-muted-foreground">
                  <th className="pb-2">Vendor</th>
                  <th className="pb-2 text-right">Payments</th>
                  <th className="pb-2 text-right">Total Paid</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {sp.topVendors.map((vendor) => (
                  <tr key={vendor.payeeId} className="border-b border-border/50 last:border-0">
                    <td className="py-2.5 font-medium">{vendor.name}</td>
                    <td className="py-2.5 text-right text-muted-foreground">{vendor.paymentCount}</td>
                    <td className="py-2.5 text-right font-medium">{formatCurrency(vendor.totalPaid)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {sp.payments.byMethod.length > 0 && (
        <div className="rounded-xl border border-border bg-white p-6">
          <h3 className="mb-4 text-sm font-semibold text-foreground">Payments by Method</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {sp.payments.byMethod.map((row) => (
              <div key={row.method} className="flex items-center justify-between rounded-lg bg-muted/50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium uppercase">{row.method}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{row.count} payments</p>
                </div>
                <p className="text-sm font-semibold">{formatCurrency(row.amount)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <TransactionsHistoryNavCard onClick={() => setReportId(TXN_HISTORY_REPORT_ID)} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartPlaceholder title="Payment Trends" icon={TrendingUp} />
        <ChartPlaceholder title="Spend by Vendor" icon={PieChart} />
      </div>
    </div>
  );
}

function SPDetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <div className="text-muted-foreground">{label}:</div>
      <div className="break-words">{value}</div>
    </>
  );
}

function SPExpandedTransactionDetail({ payment }: { payment: SPPayment }) {
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold">Payment Details</h4>
      <div className="grid grid-cols-2 gap-2 text-sm max-w-2xl">
        <SPDetailField label="Reference" value={<span className="font-mono">{payment.referenceNumber}</span>} />
        <SPDetailField label="Payee" value={payment.payee?.name || '—'} />
        {payment.payee?.company && <SPDetailField label="Company" value={payment.payee.company} />}
        <SPDetailField
          label="From Account"
          value={payment.fundingAccount?.nickname || payment.fundingAccount?.bankName || '—'}
        />
        <SPDetailField
          label="Amount"
          value={
            <span className="font-medium">
              {formatCurrency(payment.amount)} {payment.currency}
            </span>
          }
        />
        <SPDetailField label="Method" value={<span className="uppercase">{payment.paymentMethod}</span>} />
        <SPDetailField label="Status" value={<StatusBadge status={payment.status} />} />
        {payment.statusMessage && (
          <SPDetailField label="Status Note" value={<span className="text-red-600">{payment.statusMessage}</span>} />
        )}
        {payment.memo && <SPDetailField label="Memo" value={payment.memo} />}
        <SPDetailField label="Created" value={formatDateTime(payment.createdAt)} />
        {payment.completedAt && <SPDetailField label="Completed" value={formatDateTime(payment.completedAt)} />}
      </div>
    </div>
  );
}

function SPTransactionsHistoryCard({ hideHeading = false }: { hideHeading?: boolean }) {
  const { selectedLocationId } = useLocationStore();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [month, setMonth] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const { data, isLoading } = useSPPayments({
    status: statusFilter || undefined,
    locationId: selectedLocationId || undefined,
  });

  const resetPage = () => setPage(1);

  const payments = data?.data ?? [];

  const filtered = useMemo(() => {
    // Anchor the day window to the business reporting timezone (not the
    // browser's) so client-side payment filtering matches the POS reports.
    const fromIso = dateFrom ? reportingDayBoundaryIso(dateFrom, false) : null;
    const toIso = dateTo ? reportingDayBoundaryIso(dateTo, true) : null;
    const fromTime = fromIso ? new Date(fromIso).getTime() : null;
    const toTime = toIso ? new Date(toIso).getTime() : null;
    const query = search.trim().toLowerCase();

    return payments
      .filter((p) => {
        const time = new Date(p.createdAt).getTime();
        if (!Number.isNaN(time)) {
          if (fromTime !== null && time < fromTime) return false;
          if (toTime !== null && time > toTime) return false;
          if (month && p.createdAt.slice(0, 7) !== month) return false;
        }
        if (statusFilter && p.status.toLowerCase() !== statusFilter.toLowerCase()) return false;
        if (query) {
          const haystack =
            `${p.payee?.name ?? ''} ${p.payee?.company ?? ''} ${p.referenceNumber ?? ''} ${p.fundingAccount?.nickname ?? ''}`.toLowerCase();
          if (!haystack.includes(query)) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [payments, dateFrom, dateTo, month, statusFilter, search]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(() => filtered.slice((page - 1) * perPage, page * perPage), [filtered, page, perPage]);

  const hasFilters = Boolean(dateFrom || dateTo || month || statusFilter || search);
  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setMonth('');
    setStatusFilter('');
    setSearch('');
    resetPage();
  };

  return (
    <div className="rounded-xl border border-border bg-white p-6">
      {!hideHeading && <h3 className="mb-4 text-sm font-semibold text-foreground">Transactions History</h3>}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">From</label>
          <DateInput
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => {
              setDateFrom(e.target.value);
              resetPage();
            }}
            className="h-9 w-40 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">To</label>
          <DateInput
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => {
              setDateTo(e.target.value);
              resetPage();
            }}
            className="h-9 w-40 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Month</label>
          <MonthInput
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              resetPage();
            }}
            className="h-9 w-40 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              resetPage();
            }}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="">All Statuses</option>
            <option value="sent">Sent</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="submitted">Submitted</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="returned">Returned</option>
          </select>
        </div>
        <div className="flex flex-1 min-w-[180px] flex-col gap-1">
          <label className="text-xs text-muted-foreground">Search</label>
          <input
            type="text"
            placeholder="Payee, reference, or account..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetPage();
            }}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="h-9 rounded-md border border-border px-3 text-sm text-muted-foreground hover:bg-muted"
          >
            Clear
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[700px] text-sm">
          <thead className="bg-gray-50">
            <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wide text-gray-700">
              <th className="w-10 px-3 py-3" aria-label="Expand" />
              <th className="px-4 py-3">Reference</th>
              <th className="px-4 py-3">Payee</th>
              <th className="px-4 py-3">Method</th>
              <th className="px-4 py-3">From Account</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-sm text-muted-foreground">
                  Loading...
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <EmptyState
                    icon={hasFilters ? SearchX : ArrowLeftRight}
                    title={hasFilters ? 'No matching transactions' : 'No transactions found'}
                    description={
                      hasFilters
                        ? 'Try changing the filters or date range.'
                        : 'Transactions will appear here once payments are made.'
                    }
                  />
                </td>
              </tr>
            ) : (
              pageRows.map((payment) => {
                const rowKey = payment.id;
                const isExpanded = expandedRows.has(rowKey);
                return (
                  <Fragment key={rowKey}>
                    <tr
                      className="cursor-pointer hover:bg-muted/30 transition-colors"
                      onClick={() => toggleRow(rowKey)}
                    >
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-medium">{payment.referenceNumber || '—'}</td>
                      <td className="px-4 py-3">
                        {payment.payee?.name || 'Unknown'}
                        {payment.payee?.company && (
                          <span className="ml-1 text-xs text-muted-foreground">({payment.payee.company})</span>
                        )}
                      </td>
                      <td className="px-4 py-3 uppercase">{payment.paymentMethod || '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{payment.fundingAccount?.nickname || '—'}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={payment.status} />
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">{formatCurrency(payment.amount)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {formatDateTime(payment.createdAt)}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} className="bg-muted/20 px-6 py-4">
                          <SPExpandedTransactionDetail payment={payment} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
        <DataTablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          onPageChange={setPage}
          perPageOptions={[10, 25, 50, 100]}
          onPerPageChange={(n) => {
            setPerPage(n);
            resetPage();
          }}
        />
      </div>
    </div>
  );
}

/** Statuses still in flight, rolled into the Pending card. */
const RP_PENDING_STATUSES = new Set(['pending', 'processing', 'pending_authorization', 'reprocessing']);

/** Adds the invoice-payment and sale summaries into one, merging statuses. */
function mergeRPSummaries(parts: RPListSummary[]): RPListSummary {
  const byStatus = new Map<string, { status: string; count: number; cents: number }>();
  for (const part of parts) {
    for (const row of part.byStatus) {
      const cur = byStatus.get(row.status) ?? { status: row.status, count: 0, cents: 0 };
      cur.count += row.count;
      cur.cents += Math.round(row.amount * 100);
      byStatus.set(row.status, cur);
    }
  }
  const rows = [...byStatus.values()]
    .map((r) => ({ status: r.status, count: r.count, amount: r.cents / 100 }))
    .sort((a, b) => b.amount - a.amount);
  return {
    count: rows.reduce((n, r) => n + r.count, 0),
    totalAmount: rows.reduce((c, r) => c + Math.round(r.amount * 100), 0) / 100,
    byStatus: rows,
  };
}

/** "Sep 1, 2026 – Sep 21, 2026", "From Sep 1, 2026", or "All dates". */
function rangeLabel(from: string, to: string): string {
  if (from && to)
    return from === to ? formatCalendarDate(from) : `${formatCalendarDate(from)} – ${formatCalendarDate(to)}`;
  if (from) return `From ${formatCalendarDate(from)}`;
  if (to) return `Through ${formatCalendarDate(to)}`;
  return 'All dates';
}

function RPTransactionsSummary({
  summary,
  isLoading,
  label,
  loadedCount,
  searchActive,
}: {
  summary: RPListSummary | null;
  isLoading: boolean;
  label: string;
  loadedCount: number;
  searchActive: boolean;
}) {
  const sumWhere = (match: (status: string) => boolean) =>
    (summary?.byStatus ?? [])
      .filter((r) => match(r.status))
      .reduce((acc, r) => ({ count: acc.count + r.count, cents: acc.cents + Math.round(r.amount * 100) }), {
        count: 0,
        cents: 0,
      });
  const completed = sumWhere((st) => st === 'completed');
  const pending = sumWhere((st) => RP_PENDING_STATUSES.has(st));
  // An older receivepayment without the summary field: show no totals rather
  // than cards stuck on "...".
  if (!isLoading && !summary) return null;
  const value = (v: string) => (isLoading || !summary ? '...' : v);
  const plural = (n: number) => `${n} transaction${n === 1 ? '' : 's'}`;

  return (
    <div className="mb-4 space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="text-sm font-semibold text-foreground">Summary</h4>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          className="p-4"
          title="Transactions"
          value={value(String(summary?.count ?? 0))}
          subtitle="Invoice payments and sales"
        />
        <StatCard
          className="p-4"
          title="Total Amount"
          value={value(formatCurrency(summary?.totalAmount ?? 0))}
          subtitle="All statuses"
        />
        <StatCard
          className="p-4"
          title="Completed"
          value={value(formatCurrency(completed.cents / 100))}
          subtitle={plural(completed.count)}
        />
        <StatCard
          className="p-4"
          title="Pending"
          value={value(formatCurrency(pending.cents / 100))}
          subtitle={`${plural(pending.count)} in progress`}
        />
      </div>
      {!isLoading && summary && summary.byStatus.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {summary.byStatus.map((row) => (
            <div key={row.status} className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm">
              <StatusBadge status={row.status} />
              <span className="text-muted-foreground">{row.count}</span>
              <span className="font-semibold">{formatCurrency(row.amount)}</span>
            </div>
          ))}
        </div>
      )}
      {!isLoading && summary && (summary.count > loadedCount || searchActive) && (
        <p className="text-xs text-muted-foreground">
          {summary.count > loadedCount &&
            `The table lists the newest ${loadedCount} of ${summary.count} transactions; the totals include all of them. `}
          {searchActive && 'Totals cover the dates, status and location — not the search box.'}
        </p>
      )}
    </div>
  );
}

export function RPTransactionsHistoryCard({ hideHeading = false }: { hideHeading?: boolean }) {
  const { selectedLocationId } = useLocationStore();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [month, setMonth] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // The picked window as business-local calendar days: From / To, narrowed
  // to the Month when one is set. Sent to the server so the rows and the
  // totals cover the whole range, not just the newest page of each list.
  const range = useMemo(() => {
    let from = dateFrom;
    let to = dateTo;
    if (month) {
      const [y, m] = month.split('-').map(Number);
      const monthStart = `${month}-01`;
      const monthEnd = `${month}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`;
      if (!from || from < monthStart) from = monthStart;
      if (!to || to > monthEnd) to = monthEnd;
    }
    return { from, to, empty: Boolean(from && to && from > to) };
  }, [dateFrom, dateTo, month]);
  const startDate = range.from && !range.empty ? reportingDayBoundaryIso(range.from, false) : undefined;
  const endDate = range.to && !range.empty ? reportingDayBoundaryIso(range.to, true) : undefined;

  const rpStatusFilter = statusFilter ? statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1) : undefined;
  const { data: rpData, isLoading: rpLoading } = useRPPayments({
    status: rpStatusFilter,
    locationId: selectedLocationId || undefined,
    startDate,
    endDate,
  });
  const { data: salesData, isLoading: salesLoading } = useSaleTransactions({
    status: statusFilter || undefined,
    startDate,
    endDate,
  });
  const isLoading = rpLoading || salesLoading;

  // Null when either service predates the summary field, so the cards stay
  // hidden rather than showing a partial total.
  const summary = useMemo((): RPListSummary | null => {
    if (range.empty) return { count: 0, totalAmount: 0, byStatus: [] };
    if (!rpData?.summary || !salesData?.summary) return null;
    return mergeRPSummaries([rpData.summary, salesData.summary]);
  }, [range.empty, rpData, salesData]);

  const resetPage = () => setPage(1);

  const allTransactions = useMemo(() => {
    const rpPayments = (rpData?.data ?? []).map((p) => ({
      id: p.id,
      referenceNumber: p.referenceNumber,
      customerName: p.customer?.name || 'Unknown',
      invoiceNumber: p.invoice?.invoiceNumber || null,
      amount: p.amount,
      paymentMethod: p.paymentMethod,
      status: p.status,
      date: p.receivedAt,
      source: 'invoice' as const,
      raw: p,
    }));
    const sales = (salesData?.data ?? []).map((s) => ({
      id: s.id,
      referenceNumber: s.referenceNumber,
      customerName: s.firstName && s.lastName ? `${s.firstName} ${s.lastName}` : s.cardHolderName || 'Direct Sale',
      invoiceNumber: null as string | null,
      amount: s.amount,
      paymentMethod: s.paymentMethod,
      status: s.status,
      date: s.createdAt,
      source: 'sale' as const,
      raw: s,
    }));
    return [...rpPayments, ...sales];
  }, [rpData, salesData]);

  const filtered = useMemo(() => {
    // Anchor the day window to the business reporting timezone (not the
    // browser's) so client-side payment filtering matches the POS reports.
    // Same window the server filtered on, so the table and the totals agree.
    if (range.empty) return [];
    const fromTime = startDate ? new Date(startDate).getTime() : null;
    const toTime = endDate ? new Date(endDate).getTime() : null;
    const query = search.trim().toLowerCase();

    return allTransactions
      .filter((txn) => {
        const time = new Date(txn.date).getTime();
        if (!Number.isNaN(time)) {
          if (fromTime !== null && time < fromTime) return false;
          if (toTime !== null && time > toTime) return false;
        }
        if (statusFilter && txn.status.toLowerCase() !== statusFilter.toLowerCase()) return false;
        if (query) {
          const haystack = `${txn.customerName} ${txn.referenceNumber ?? ''} ${txn.invoiceNumber ?? ''}`.toLowerCase();
          if (!haystack.includes(query)) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [allTransactions, range.empty, startDate, endDate, statusFilter, search]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(() => filtered.slice((page - 1) * perPage, page * perPage), [filtered, page, perPage]);

  const hasFilters = Boolean(dateFrom || dateTo || month || statusFilter || search);
  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setMonth('');
    setStatusFilter('');
    setSearch('');
    resetPage();
  };

  return (
    <div className="rounded-xl border border-border bg-white p-6">
      {!hideHeading && <h3 className="mb-4 text-sm font-semibold text-foreground">Transactions History</h3>}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">From</label>
          <DateInput
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => {
              setDateFrom(e.target.value);
              resetPage();
            }}
            className="h-9 w-40 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">To</label>
          <DateInput
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => {
              setDateTo(e.target.value);
              resetPage();
            }}
            className="h-9 w-40 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Month</label>
          <MonthInput
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              resetPage();
            }}
            className="h-9 w-40 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              resetPage();
            }}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="pending_authorization">Pending Authorization</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="returned">Returned</option>
            <option value="cancelled">Cancelled</option>
            <option value="partial refund processing">Partial Refund Processing</option>
            <option value="refund processing">Refund Processing</option>
            <option value="partially refunded">Partially Refunded</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        <div className="flex flex-1 min-w-[180px] flex-col gap-1">
          <label className="text-xs text-muted-foreground">Search</label>
          <input
            type="text"
            placeholder="Customer, reference, or invoice..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetPage();
            }}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="h-9 rounded-md border border-border px-3 text-sm text-muted-foreground hover:bg-muted"
          >
            Clear
          </button>
        )}
      </div>

      <RPTransactionsSummary
        summary={summary}
        isLoading={isLoading}
        label={rangeLabel(range.from, range.to)}
        loadedCount={allTransactions.length}
        searchActive={Boolean(search.trim())}
      />

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[700px] text-sm">
          <thead className="bg-gray-50">
            <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wide text-gray-700">
              <th className="w-10 px-3 py-3" aria-label="Expand" />
              <th className="px-4 py-3">Reference</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Invoice</th>
              <th className="px-4 py-3">Method</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-sm text-muted-foreground">
                  Loading...
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <EmptyState
                    icon={hasFilters ? SearchX : ArrowLeftRight}
                    title={hasFilters ? 'No matching transactions' : 'No transactions found'}
                    description={
                      hasFilters
                        ? 'Try changing the filters or date range.'
                        : 'Transactions will appear here once payments are made.'
                    }
                  />
                </td>
              </tr>
            ) : (
              pageRows.map((txn) => {
                const rowKey = `${txn.source}-${txn.id}`;
                const isExpanded = expandedRows.has(rowKey);
                return (
                  <Fragment key={rowKey}>
                    <tr
                      className="cursor-pointer hover:bg-muted/30 transition-colors"
                      onClick={() => toggleRow(rowKey)}
                    >
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-medium">{txn.referenceNumber || '—'}</td>
                      <td className="px-4 py-3">{txn.customerName}</td>
                      <td className="px-4 py-3 text-muted-foreground">{txn.invoiceNumber || '—'}</td>
                      <td className="px-4 py-3 uppercase">{txn.paymentMethod || '—'}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={txn.status} />
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">{formatCurrency(txn.amount)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{formatDateTime(txn.date)}</td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} className="bg-muted/20 px-6 py-4">
                          <ExpandedTransactionDetail txn={txn} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
        <DataTablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          onPageChange={setPage}
          perPageOptions={[10, 25, 50, 100]}
          onPerPageChange={(n) => {
            setPerPage(n);
            resetPage();
          }}
        />
      </div>
    </div>
  );
}

function ReceivePaymentsReport({
  rp,
  isLoading,
  isActive,
}: {
  rp: RPDashboardData | undefined;
  isLoading: boolean;
  isActive: boolean;
}) {
  // Lives in the URL (`#/reports?tab=receive&report=transactions-history`) so
  // the Receive Payments → Transactions info banner can open it directly.
  const [reportId, setReportId] = useHashParam('report');
  const showTxnHistory = reportId === TXN_HISTORY_REPORT_ID;

  if (!rp) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {['Total Invoices', 'Total Receivable', 'Collected', 'Overdue'].map((title) => (
          <StatCard key={title} title={title} value="..." subtitle="Loading..." />
        ))}
      </div>
    );
  }

  if (showTxnHistory) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Transactions History"
          description="View, filter, and search the full transaction history."
          onBack={() => setReportId(null)}
        />
        <RPTransactionsHistoryCard hideHeading />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!isActive && <InactiveModuleBanner />}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Invoices"
          value={isLoading ? '...' : String(rp.invoices.total)}
          subtitle={`${rp.customers.active} active customers`}
        />
        <StatCard
          title="Total Receivable"
          value={isLoading ? '...' : formatCurrency(rp.invoices.totalReceivable)}
          subtitle="Outstanding balance"
        />
        <StatCard
          title="Collected"
          value={isLoading ? '...' : formatCurrency(rp.invoices.collected)}
          changeType="positive"
          subtitle="Payments received"
        />
        <StatCard
          title="Overdue"
          value={isLoading ? '...' : formatCurrency(rp.invoices.overdue)}
          changeType="negative"
          subtitle="Past due date"
        />
      </div>

      {rp.invoices.byStatus.length > 0 && (
        <div className="rounded-xl border border-border bg-white p-6">
          <h3 className="mb-4 text-sm font-semibold text-foreground">Invoices by Status</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {rp.invoices.byStatus.map((row) => (
              <div key={row.status} className="flex items-center justify-between rounded-lg bg-muted/50 px-4 py-3">
                <div>
                  <StatusBadge status={row.status} />
                  <p className="mt-1 text-xs text-muted-foreground">{row.count} invoices</p>
                </div>
                <p className="text-sm font-semibold">{formatCurrency(row.amount)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {rp.payments.byMethod.length > 0 && (
        <div className="rounded-xl border border-border bg-white p-6">
          <h3 className="mb-4 text-sm font-semibold text-foreground">Received Payments by Method</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {rp.payments.byMethod.map((row) => (
              <div key={row.method} className="flex items-center justify-between rounded-lg bg-muted/50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium uppercase">{row.method}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{row.count} payments</p>
                </div>
                <p className="text-sm font-semibold">{formatCurrency(row.amount)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <TransactionsHistoryNavCard onClick={() => setReportId(TXN_HISTORY_REPORT_ID)} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartPlaceholder title="Collection Trends" icon={TrendingUp} />
        <ChartPlaceholder title="Receivables Aging" icon={PieChart} />
      </div>
    </div>
  );
}

function ClockEventBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    clock_in: 'bg-green-100 text-green-700',
    clock_out: 'bg-gray-100 text-gray-600',
    break_start: 'bg-yellow-100 text-yellow-700',
    break_end: 'bg-blue-100 text-blue-700',
  };
  const labels: Record<string, string> = {
    clock_in: 'Clock In',
    clock_out: 'Clock Out',
    break_start: 'Break Start',
    break_end: 'Break End',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        styles[type] ?? 'bg-gray-100 text-gray-600',
      )}
    >
      {labels[type] ?? type}
    </span>
  );
}

// Clock event types for the Employee Work Time report's Event Type filter.
const CLOCK_EVENT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'clock_in', label: 'Clock In' },
  { value: 'clock_out', label: 'Clock Out' },
  { value: 'break_start', label: 'Break Start' },
  { value: 'break_end', label: 'Break End' },
];

const CLOCK_EVENT_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  CLOCK_EVENT_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

/** Punches on a day narrowed to the active Event Type filter ('' = all). */
function punchesOfType(punches: PosWorkTimePunch[], eventType: string): PosWorkTimePunch[] {
  return eventType ? punches.filter((p) => p.type === eventType) : punches;
}

/** Distinct event types recorded on a day, in the canonical filter order so
 *  badges read Clock In → Clock Out → Break Start → Break End. */
function dayEventTypes(punches: PosWorkTimePunch[], eventType: string): string[] {
  const seen = new Set(punchesOfType(punches, eventType).map((p) => p.type));
  const known = CLOCK_EVENT_TYPE_OPTIONS.map((o) => o.value).filter((v) => seen.has(v));
  // Any type the POS sends that isn't in the known list still shows, after them.
  return [...known, ...[...seen].filter((t) => !known.includes(t))];
}

/** Distinct register names a day's punches were made on. A day can span more
 *  than one register when the employee clocked in and out on different tills. */
function dayRegisterNames(punches: PosWorkTimePunch[], eventType: string): string[] {
  const names = punchesOfType(punches, eventType)
    .map((p) => p.registerName?.trim())
    .filter((n): n is string => !!n);
  return [...new Set(names)];
}

/** Drill-down modal listing the raw clock in/out + break punches for a single
 *  employee-day. Data is already loaded with the day row — no extra request. */
function WorkTimeDayDetailModal({
  open,
  employeeName,
  day,
  tz,
  eventType,
  onClose,
}: {
  open: boolean;
  employeeName: string;
  day: PosWorkTimeDay | null;
  tz: string;
  eventType: string; // '' = all; otherwise a clock event type to show only
  onClose: () => void;
}) {
  // The event-type filter narrows the punch list only — worked/break totals in
  // the summary strip stay computed from ALL punches (filtering to a single
  // type would break clock_in/out pairing).
  const visiblePunches = day ? (eventType ? day.punches.filter((p) => p.type === eventType) : day.punches) : [];
  return (
    <Modal open={open} onClose={onClose} title="Work Time Details" widthClass="max-w-[720px]">
      {!day ? (
        <EmptyState icon={FileText} title="No details" description="Details for this day are not available." />
      ) : (
        <div className="space-y-4">
          {/* Summary strip */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-foreground">{employeeName}</div>
              <div className="text-xs text-muted-foreground">{formatCalendarDate(day.date)}</div>
            </div>
            <div className="flex gap-6 text-right">
              <div>
                <div className="text-xs text-muted-foreground">Break</div>
                <div className="text-sm font-medium text-foreground">{formatWorkDuration(day.breakSeconds)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Worked</div>
                <div className="text-sm font-semibold text-foreground">{formatWorkDuration(day.workedSeconds)}</div>
              </div>
            </div>
          </div>

          {day.openShift && (
            <div className="rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
              This day has a clock-in with no matching clock-out — worked time may be incomplete.
            </div>
          )}

          {/* Punch list */}
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                  <th className="px-4 py-2.5 font-medium">Event</th>
                  <th className="px-4 py-2.5 font-medium">Time</th>
                  <th className="px-4 py-2.5 font-medium">Register</th>
                  <th className="px-4 py-2.5 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody>
                {visiblePunches.length === 0 ? (
                  <tr>
                    <td colSpan={4}>
                      <EmptyState
                        icon={eventType ? SearchX : Clock}
                        title={eventType ? 'No matching punches' : 'No punches recorded'}
                        description={
                          eventType
                            ? 'No punches of this event type on this day.'
                            : 'Clock-ins, clock-outs and breaks for this day will appear here.'
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  visiblePunches.map((p: PosWorkTimePunch, idx: number) => (
                    <tr
                      key={`${p.type}-${p.timestampUtc}-${idx}`}
                      className="border-b border-border/60 last:border-b-0"
                    >
                      <td className="px-4 py-2.5">
                        <ClockEventBadge type={p.type} />
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">
                        {formatPosTime(p.timestampUtc, tz)}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">{p.registerName ?? '—'}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{p.note?.trim() ? p.note : '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}

function PosEmployeeWorkTimeReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const locationLabel = selectedLocationName || 'All locations';
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string }>({ ...initialRange });
  const [activeEmployeeId, setActiveEmployeeId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  // Register scopes the whole report (backend filter); Event Type is a
  // client-side filter for the punch list in the View Details dialog. Both are
  // live (no Apply) — kept from the previous Work Time report.
  const [registerId, setRegisterId] = useState('');
  const [eventType, setEventType] = useState('');
  const [detailDay, setDetailDay] = useState<PosWorkTimeDay | null>(null);
  const [exporting, setExporting] = useState(false);

  const { data: stationsData } = usePosStations({ locationId: selectedLocationId || undefined });
  const stations = stationsData?.data ?? [];

  const filterParams = {
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    locationId: selectedLocationId || undefined,
    registerId: registerId || undefined,
  };

  const { data: employeesData, isLoading: employeesLoading } = usePosWorkTimeEmployees(filterParams);
  const employees: PosWorkTimeEmployee[] = employeesData?.data ?? [];

  // Auto-select the first employee when the list changes and the current
  // selection is gone (mirrors Sales by Cashier).
  useEffect(() => {
    if (employees.length === 0) {
      if (activeEmployeeId !== null) setActiveEmployeeId(null);
      return;
    }
    if (!activeEmployeeId || !employees.find((e) => e.employeeId === activeEmployeeId)) {
      setActiveEmployeeId(employees[0].employeeId);
      setPage(1);
    }
  }, [employees, activeEmployeeId]);

  const { data: daysData, isLoading: daysLoading } = usePosWorkTimeDays({
    employeeId: activeEmployeeId || undefined,
    page,
    perPage,
    eventType: eventType || undefined,
    ...filterParams,
  });
  const days: PosWorkTimeDay[] = daysData?.data ?? [];
  const total = daysData?.pagination?.total ?? 0;
  const totalPages = daysData?.pagination?.totalPages ?? 1;

  const activeEmployee = employees.find((e) => e.employeeId === activeEmployeeId);

  const handleApply = () => {
    setApplied({ dateFrom: dateFromInput, dateTo: dateToInput });
    setPage(1);
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setApplied({ ...r });
    setRegisterId('');
    setEventType('');
    setPage(1);
  };

  const exportDays = async () => {
    if (!activeEmployee) return;
    setExporting(true);
    try {
      const all: PosWorkTimeDay[] = [];
      const batch = 500;
      for (let p = 1; ; p++) {
        const res = await api.get<PaginatedResponse<PosWorkTimeDay>>(
          '/pos/report/work-time/days',
          withLocalDateRange({
            employeeId: activeEmployee.employeeId,
            page: p,
            perPage: batch,
            eventType: eventType || undefined,
            ...filterParams,
          }) as Record<string, string | number | undefined>,
        );
        all.push(...res.data);
        if (res.data.length < batch || p >= (res.pagination?.totalPages ?? 1)) break;
      }
      const header = [
        'Location',
        'Employee',
        'Date',
        'Event Type',
        'Register',
        'First Clock In',
        'Last Clock Out',
        'Break Duration',
        'Worked Duration',
        'Worked Hours (decimal)',
      ];
      const body = all.map((d) => [
        locationLabel,
        activeEmployee.employeeName,
        d.date,
        dayEventTypes(d.punches, eventType)
          .map((t) => CLOCK_EVENT_TYPE_LABELS[t] ?? t)
          .join(', '),
        dayRegisterNames(d.punches, eventType).join(', '),
        d.firstClockIn ? formatPosTime(d.firstClockIn, reportTz) : '',
        d.lastClockOut ? formatPosTime(d.lastClockOut, reportTz) : '',
        formatWorkDuration(d.breakSeconds),
        formatWorkDuration(d.workedSeconds),
        (d.workedSeconds / 3600).toFixed(2),
      ]);
      const slug = `employee_work_time_${activeEmployee.employeeName}`;
      downloadCsv(reportFilename(slug, selectedLocationName), [header, ...body]);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Employee's Work Time"
        description="How many hours each employee worked, day by day — great for payroll."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={!activeEmployee || exporting} onClick={exportDays}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware) */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Register</label>
            <select
              value={registerId}
              onChange={(e) => {
                setRegisterId(e.target.value);
                setPage(1);
              }}
              className="h-9 w-48 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">All Registers</option>
              {stations.map((s) => (
                <option key={s.registerId} value={s.registerId}>
                  {s.stationName}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Event Type</label>
            <select
              value={eventType}
              onChange={(e) => {
                setEventType(e.target.value);
                setPage(1);
              }}
              className="h-9 w-48 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">All Event Types</option>
              {CLOCK_EVENT_TYPE_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[300px_1fr]">
        {/* Employee rail */}
        <div className="overflow-hidden rounded-xl border border-border bg-white">
          {employeesLoading ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : employees.length === 0 ? (
            <EmptyState
              icon={Clock}
              title="No clock activity"
              description="Try a different date range."
              className="py-8"
            />
          ) : (
            <ul role="tablist" aria-label="Employees">
              {employees.map((e) => {
                const isSelected = e.employeeId === activeEmployeeId;
                return (
                  <li key={e.employeeId} className="border-b border-border/60 last:border-b-0">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={isSelected}
                      onClick={() => {
                        setActiveEmployeeId(e.employeeId);
                        setPage(1);
                      }}
                      className={cn(
                        'flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors',
                        isSelected
                          ? 'border-l-4 border-primary bg-primary/5'
                          : 'border-l-4 border-transparent hover:bg-muted/40',
                      )}
                    >
                      <div>
                        <div className="text-sm font-medium text-foreground">{e.employeeName}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {e.daysWorked.toLocaleString()} {e.daysWorked === 1 ? 'day' : 'days'}
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-foreground">{formatWorkDuration(e.workedSeconds)}</div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Day rows pane */}
        <div className="overflow-hidden rounded-xl border border-border bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-3">
            <div className="text-sm font-semibold text-foreground">
              Employee: <span className="font-normal">{activeEmployee?.employeeName ?? '—'}</span>
            </div>
            {activeEmployee && (
              <div className="text-sm text-muted-foreground">
                Total worked:{' '}
                <span className="font-semibold text-foreground">
                  {formatWorkDuration(activeEmployee.workedSeconds)}
                </span>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Event Type</th>
                  <th className="px-4 py-3 font-medium">Register</th>
                  <th className="px-4 py-3 font-medium">First Clock In</th>
                  <th className="px-4 py-3 font-medium">Last Clock Out</th>
                  <th className="px-4 py-3 font-medium">Break</th>
                  <th className="px-4 py-3 font-medium">Worked</th>
                  <th className="px-4 py-3 font-medium text-right">Details</th>
                </tr>
              </thead>
              <tbody>
                {!activeEmployee ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                      Select an employee to view their work time.
                    </td>
                  </tr>
                ) : daysLoading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                ) : days.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <EmptyState
                        icon={eventType ? SearchX : Clock}
                        title={eventType ? 'No matching events' : 'No work time'}
                        description={
                          eventType
                            ? `No ${CLOCK_EVENT_TYPE_LABELS[eventType] ?? eventType} events for this employee in the current range.`
                            : 'This employee has no work time in the current range.'
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  days.map((d) => (
                    <tr key={d.date} className="border-b border-border/60 last:border-b-0">
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-foreground">
                        {formatCalendarDate(d.date)}
                        {d.openShift && (
                          <span
                            className="ml-2 inline-flex items-center rounded-full bg-yellow-100 px-1.5 py-0.5 text-[10px] font-medium text-yellow-700"
                            title="A clock-in has no matching clock-out"
                          >
                            open
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {dayEventTypes(d.punches, eventType).map((t) => (
                            <ClockEventBadge key={t} type={t} />
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {dayRegisterNames(d.punches, eventType).join(', ') || '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {d.firstClockIn ? formatPosTime(d.firstClockIn, reportTz) : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {d.lastClockOut ? formatPosTime(d.lastClockOut, reportTz) : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {formatWorkDuration(d.breakSeconds)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-semibold text-foreground">
                        {formatWorkDuration(d.workedSeconds)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setDetailDay(d)}
                          className="font-medium text-primary hover:underline"
                        >
                          View details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div className="border-t border-border">
              <DataTablePagination
                page={page}
                totalPages={totalPages}
                total={total}
                perPage={perPage}
                onPageChange={setPage}
                perPageOptions={[10, 25, 50, 100]}
                onPerPageChange={(n) => {
                  setPerPage(n);
                  setPage(1);
                }}
              />
            </div>
          )}
        </div>
      </div>

      <WorkTimeDayDetailModal
        open={detailDay !== null}
        employeeName={activeEmployee?.employeeName ?? ''}
        day={detailDay}
        tz={reportTz}
        eventType={eventType}
        onClose={() => setDetailDay(null)}
      />
    </div>
  );
}

/** Human labels for the coupon discount types and reward program types the
 *  POS stores (see the CHECK constraints on pos.coupons / pos.reward_programs). */
const COUPON_SUBTYPE_LABELS: Record<string, string> = {
  amount_off: 'Amount Off',
  percent_off: 'Percent Off',
  free_product: 'Free Product',
  points_per_product: 'Points per Product',
  points_per_dollar: 'Points per Dollar',
  points_per_visit: 'Points per Visit',
};

/** The coupon's headline value, rendered the way its type means it: percent_off
 *  is a percentage, everything else is money. */
function couponValueLabel(c: PosCouponUsageCoupon): string {
  if (c.subtype === 'percent_off') return `${c.value}% off`;
  if (c.subtype === 'free_product') return 'Free product';
  if (c.subtype === 'amount_off') return `${formatCurrency(c.value)} off`;
  return c.value ? `${formatCurrency(c.value)} / point` : '—';
}

/**
 * Coupon/Rewards Usage — Reports → Customers.
 *
 * Master-detail, the same shape as Employee's Work Time: the rail lists every
 * coupon (or reward program) with its usage roll-up for the range, and picking
 * one lists each redemption with the customer and the order it was used on.
 */
function PosCouponUsageReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const locationLabel = selectedLocationName || 'All locations';
  const f = useItemReportFilters();

  const [kind, setKind] = useState<PosCouponUsageKind>('coupon');
  const [activeCouponId, setActiveCouponId] = useState<string | null>(null);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const scope = {
    kind,
    locationId: selectedLocationId || undefined,
    dateFrom: f.applied.dateFrom || undefined,
    dateTo: f.applied.dateTo || undefined,
  };

  // Search filters the rail (coupon name/code); the detail table is scoped by
  // the selected coupon, so it deliberately does not take the rail's search.
  const { data: couponsData, isLoading: couponsLoading } = usePosCouponUsageCoupons({
    ...scope,
    search: f.appliedSearch || undefined,
  });
  const coupons: PosCouponUsageCoupon[] = couponsData?.data ?? [];

  // Keep a valid selection as the rail changes (range, search or kind switch).
  useEffect(() => {
    if (coupons.length === 0) {
      if (activeCouponId !== null) setActiveCouponId(null);
      return;
    }
    if (!activeCouponId || !coupons.find((c) => c.id === activeCouponId)) {
      setActiveCouponId(coupons[0].id);
      f.setPage(1);
    }
    // f is a stable-enough filter bag; only the rail contents drive re-selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupons, activeCouponId]);

  const { data: redemptionsData, isLoading: redemptionsLoading } = usePosCouponUsageRedemptions({
    ...scope,
    couponId: activeCouponId || undefined,
    page: f.page,
    perPage: f.perPage,
  });
  const redemptions: PosCouponUsageRedemption[] = redemptionsData?.data ?? [];
  const pagination = redemptionsData?.pagination;

  const activeCoupon = coupons.find((c) => c.id === activeCouponId);
  const isReward = kind === 'reward';

  const handleKindChange = (next: PosCouponUsageKind) => {
    if (next === kind) return;
    setKind(next);
    setActiveCouponId(null);
    f.setPage(1);
  };

  const handleExport = async () => {
    if (!activeCoupon) return;
    setExporting(true);
    try {
      // Page through the whole selection so the CSV isn't just the visible page.
      const all: PosCouponUsageRedemption[] = [];
      const batch = 500;
      for (let p = 1; ; p++) {
        const res = await api.get<PaginatedResponse<PosCouponUsageRedemption>>(
          '/pos/report/coupon-usage/redemptions',
          withLocalDateRange({
            ...scope,
            couponId: activeCoupon.id,
            page: p,
            perPage: batch,
          }) as Record<string, string | number | undefined>,
        );
        all.push(...res.data);
        if (res.data.length < batch || p >= (res.pagination?.totalPages ?? 1)) break;
      }
      const header = [
        'Location',
        isReward ? 'Reward Program' : 'Coupon Name',
        ...(isReward ? [] : ['Coupon Code']),
        'Customer Name',
        'Phone',
        'Order ID',
        'Order Date',
        'Order Total ($)',
        'Discount Applied ($)',
        ...(isReward ? ['Points Used'] : []),
        'Redeemed At',
        'Employee Name',
      ];
      const body = all.map((r) => [
        r.locationName || locationLabel,
        activeCoupon.name,
        ...(isReward ? [] : [activeCoupon.code]),
        r.customerName,
        r.customerPhone,
        r.orderNumber,
        r.orderDate ? formatDateTime(r.orderDate) : '',
        r.orderTotal === null ? '' : r.orderTotal.toFixed(2),
        r.discountApplied.toFixed(2),
        ...(isReward ? [r.pointsDelta === null ? '' : String(Math.abs(r.pointsDelta))] : []),
        formatDateTime(r.redeemedAt),
        r.employeeName,
      ]);
      const slug = isReward ? 'reward_usage' : 'coupon_usage';
      downloadCsv(reportFilename(slug, selectedLocationName), [header, ...body]);
    } finally {
      setExporting(false);
    }
  };

  // +1 for the actions column that opens the shared order popup.
  const columnCount = isReward ? 8 : 7;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Coupon/Rewards Usage"
        description="See which customers redeemed each coupon or reward, and on which orders."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={!activeCoupon || exporting} onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      <ReportDateRangeCard
        reportTz={f.reportTz}
        reportTzLabel={f.reportTzLabel}
        dateFromInput={f.dateFromInput}
        dateToInput={f.dateToInput}
        onFromChange={f.setDateFromInput}
        onToChange={f.setDateToInput}
        onSubmit={f.handleSubmit}
        onReset={f.handleReset}
      />

      {/* Coupons / Rewards tabs — separate POS features, one report. */}
      <div className="border-b border-border">
        <div className="flex w-full min-w-0" role="tablist" aria-label="Coupon or reward usage">
          {(
            [
              { id: 'coupon', label: 'Coupons' },
              { id: 'reward', label: 'Rewards' },
            ] as const
          ).map((t) => {
            const isSelected = kind === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => handleKindChange(t.id)}
                className={cn(
                  'relative shrink-0 px-4 py-2.5 text-sm font-medium transition-colors',
                  isSelected ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                )}
                aria-selected={isSelected}
                role="tab"
              >
                {t.label}
                {isSelected && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="inline-flex max-w-sm items-center gap-2 rounded-md border border-input bg-white px-2.5 py-1.5 text-sm">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          value={f.search}
          onChange={(e) => f.setSearch(e.target.value)}
          placeholder={isReward ? 'Search reward program' : 'Search coupon name or code'}
          className="w-72 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
        {/* Coupon / program rail */}
        <div className="overflow-hidden rounded-xl border border-border bg-white">
          {couponsLoading ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : coupons.length === 0 ? (
            <EmptyState
              icon={isReward ? Gift : Ticket}
              title={isReward ? 'No reward programs found' : 'No coupons found'}
              description="Try a different date range."
              className="py-8"
            />
          ) : (
            <ul role="tablist" aria-label={isReward ? 'Reward programs' : 'Coupons'}>
              {coupons.map((c) => {
                const isSelected = c.id === activeCouponId;
                return (
                  <li key={c.id} className="border-b border-border/60 last:border-b-0">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={isSelected}
                      onClick={() => {
                        setActiveCouponId(c.id);
                        f.setPage(1);
                      }}
                      className={cn(
                        'flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition-colors',
                        isSelected
                          ? 'border-l-4 border-primary bg-primary/5'
                          : 'border-l-4 border-transparent hover:bg-muted/40',
                      )}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-foreground">{c.name || '—'}</span>
                          {!c.isActive && (
                            <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                              inactive
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-muted-foreground">
                          {c.code ? `${c.code} · ` : ''}
                          {COUPON_SUBTYPE_LABELS[c.subtype] ?? c.subtype}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {c.redemptionCount.toLocaleString()} {c.redemptionCount === 1 ? 'use' : 'uses'} ·{' '}
                          {c.uniqueCustomers.toLocaleString()} {c.uniqueCustomers === 1 ? 'customer' : 'customers'}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-sm font-semibold text-foreground">{formatCurrency(c.totalDiscount)}</div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">discount</div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Redemption pane */}
        <div className="overflow-hidden rounded-xl border border-border bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-3">
            <div className="text-sm font-semibold text-foreground">
              {isReward ? 'Reward' : 'Coupon'}: <span className="font-normal">{activeCoupon?.name ?? '—'}</span>
              {activeCoupon?.code ? (
                <span className="ml-2 rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs font-normal text-muted-foreground">
                  {activeCoupon.code}
                </span>
              ) : null}
            </div>
            {activeCoupon && (
              <div className="flex gap-5 text-sm text-muted-foreground">
                <span>
                  Value: <span className="font-medium text-foreground">{couponValueLabel(activeCoupon)}</span>
                </span>
                <span>
                  Total discount:{' '}
                  <span className="font-semibold text-foreground">{formatCurrency(activeCoupon.totalDiscount)}</span>
                </span>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Phone</th>
                  <th className="px-4 py-3 font-medium">Order ID</th>
                  <th className="px-4 py-3 font-medium">Order Date</th>
                  <th className="px-4 py-3 text-right font-medium">Order Total ($)</th>
                  {isReward && <th className="px-4 py-3 text-right font-medium">Points Used</th>}
                  <th className="px-4 py-3 text-right font-medium">Discount ($)</th>
                  <th className="px-4 py-3 text-right font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {!activeCoupon ? (
                  <tr>
                    <td colSpan={columnCount} className="px-4 py-10 text-center text-muted-foreground">
                      {isReward
                        ? 'Select a reward program to view its redemptions.'
                        : 'Select a coupon to view who used it.'}
                    </td>
                  </tr>
                ) : redemptionsLoading ? (
                  <tr>
                    <td colSpan={columnCount} className="px-4 py-10 text-center text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                ) : redemptions.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount}>
                      <EmptyState
                        icon={isReward ? Gift : Ticket}
                        title="No redemptions"
                        description={`Nothing was redeemed for this ${isReward ? 'reward program' : 'coupon'} in the current range.`}
                      />
                    </td>
                  </tr>
                ) : (
                  redemptions.map((r) => (
                    <tr key={r.redemptionId} className="border-b border-border/60 last:border-b-0">
                      <td className="px-4 py-2.5 font-medium text-foreground">{r.customerName || 'Walk-in'}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">{r.customerPhone || '—'}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{r.orderNumber || '—'}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">
                        {/* Falls back to the redemption time — reward points can
                            be redeemed without an order attached. */}
                        {formatDateTime(r.orderDate ?? r.redeemedAt)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-muted-foreground">
                        {r.orderTotal === null ? '—' : formatCurrency(r.orderTotal)}
                      </td>
                      {isReward && (
                        <td className="px-4 py-2.5 text-right text-muted-foreground">
                          {r.pointsDelta === null ? '—' : Math.abs(r.pointsDelta).toLocaleString()}
                        </td>
                      )}
                      <td className="px-4 py-2.5 text-right font-semibold text-foreground">
                        {formatCurrency(r.discountApplied)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {/* Reward points can be redeemed without a sale, and
                            those rows have no order to open. */}
                        {r.transactionId ? (
                          <button
                            type="button"
                            onClick={() => setDetailOrderId(r.transactionId)}
                            aria-label="View order details"
                            className="rounded-md border border-border p-1.5 text-primary hover:bg-primary/5"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total > 0 && (
            <div className="border-t border-border">
              <DataTablePagination
                page={f.page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                perPage={f.perPage}
                onPageChange={f.setPage}
                perPageOptions={[10, 25, 50, 100]}
                onPerPageChange={(n) => {
                  f.setPerPage(n);
                  f.setPage(1);
                }}
              />
            </div>
          )}
        </div>
      </div>

      <OrderDetailsModal
        key={detailOrderId ?? 'closed'}
        open={detailOrderId !== null}
        orderId={detailOrderId}
        onClose={() => setDetailOrderId(null)}
      />
    </div>
  );
}

/**
 * Customer list for one segment. Reports → Customers splits this into "Credit
 * Customers" (credit/wholesale accounts) and "Retail Customers" (everyone
 * else); the segment is filtered server-side so paging and counts stay correct.
 */
function PosCustomersTab({ segment }: { segment: 'credit' | 'retail' }) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState('');

  const { data, isLoading } = usePosCustomers({
    page,
    perPage,
    search: search || undefined,
    segment,
  });

  const customers: PosCustomer[] = data?.data ?? [];
  const pagination = data?.pagination;

  function resetPage() {
    setPage(1);
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          placeholder="Search by name or phone..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetPage();
          }}
          className="h-9 w-64 rounded-md border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-border bg-white">
        <table className="w-full min-w-[700px] text-sm">
          <thead className="bg-gray-50">
            <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wide text-gray-700">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3 text-right">Loyalty Points</th>
              <th className="px-4 py-3 text-right">Total Visits</th>
              <th className="px-4 py-3 text-right">Total Spent</th>
              <th className="px-4 py-3">Signup Date</th>
              <th className="px-4 py-3">Last Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">
                  Loading...
                </td>
              </tr>
            ) : customers.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <EmptyState
                    icon={Users}
                    title="No customers found"
                    description="Try a different search, or check back once customers make purchases."
                  />
                </td>
              </tr>
            ) : (
              customers.map((c) => (
                <tr key={c.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.phone}</td>
                  <td className="px-4 py-3 text-right font-mono">{c.loyaltyPoints.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-mono">{c.totalVisits.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{formatCurrency(c.totalSpent)}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {c.signupDate ? new Date(c.signupDate).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{formatDateTime(c.updatedAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination && (
        <DataTablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          perPage={pagination.perPage}
          onPageChange={setPage}
          perPageOptions={[10, 25, 50, 100]}
          onPerPageChange={(val) => {
            setPerPage(val);
            resetPage();
          }}
        />
      )}
    </div>
  );
}

// ── POS Summary Tab ──

const PAYMENT_METHOD_META: Record<string, { label: string; icon: React.ElementType }> = {
  cash: { label: 'Cash', icon: Banknote },
  card: { label: 'Card', icon: CreditCard },
  credit: { label: 'Credit Acc', icon: CreditCard },
  debit: { label: 'Debit', icon: CreditCard },
  ebt: { label: 'EBT', icon: Wallet },
  gift_card: { label: 'Gift Card', icon: Wallet },
  store_credit: { label: 'Store Credit', icon: Wallet },
  check: { label: 'Check', icon: ReceiptText },
};

function paymentMethodLabel(key: string) {
  return PAYMENT_METHOD_META[key]?.label ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function paymentMethodIcon(key: string): React.ElementType {
  return PAYMENT_METHOD_META[key]?.icon ?? Wallet;
}

// Card brands as the terminals report them. Colours come from the theme tokens
// rather than each network's own branding, so the card sits in the same palette
// as everything else on the page (and follows a tenant re-theme).
const CARD_BRAND_META: Record<string, { label: string; color: string }> = {
  visa: { label: 'VISA', color: 'var(--color-info)' },
  mastercard: { label: 'MASTERCARD', color: 'var(--color-warning)' },
  amex: { label: 'AMEX', color: 'var(--color-primary)' },
  discover: { label: 'DISCOVER', color: 'var(--color-success)' },
};

function cardBrandLabel(brand: string) {
  return CARD_BRAND_META[brand]?.label ?? brand.replace(/_/g, ' ').toUpperCase();
}

function cardBrandColor(brand: string) {
  return CARD_BRAND_META[brand]?.color ?? 'var(--color-muted-foreground)';
}

/**
 * Card sales split by brand: one stacked bar over the amounts, then a row per
 * brand with its transaction count and amount.
 *
 * Amounts use the same allocation as "Total Payment Collected by Payment
 * Method" — each order's net total shared across its tenders — so these rows
 * sum to the card slice of that card, not to a larger separate number. Counts
 * are transactions, so an order split across two cards of one brand counts once
 * and the counts here can be lower than the tender counts shown there.
 */
function CardBrandCard({ summary }: { summary: PosReportSummary }) {
  const brands = summary.cardBrands;
  const total = summary.cardBrandsTotal;
  const transactions = brands.reduce((sum, b) => sum + b.count, 0);
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-center justify-between gap-3 border-b border-border pb-3">
        <h3 className="text-sm font-semibold text-foreground">Card Sales by Brand</h3>
        {brands.length > 0 && (
          <span className="text-xs text-muted-foreground">
            {transactions.toLocaleString()} transaction{transactions === 1 ? '' : 's'}
          </span>
        )}
      </div>
      {brands.length === 0 ? (
        <EmptyState
          icon={CreditCard}
          title="No card payments"
          description="Try a different date range."
          className="py-8"
        />
      ) : (
        <>
          <div className="mt-4 flex h-3 gap-0.5 overflow-hidden rounded-full">
            {brands.map((b) => (
              <div
                key={b.brand}
                style={{ width: `${Math.max(0.5, b.percent)}%`, background: cardBrandColor(b.brand) }}
                title={`${cardBrandLabel(b.brand)}: ${formatCurrency(b.total)}`}
              />
            ))}
          </div>
          <ul className="space-y-3 pt-4">
            {brands.map((b) => (
              <li key={b.brand} className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: cardBrandColor(b.brand) }} />
                  <span className="truncate text-sm font-medium text-foreground">{cardBrandLabel(b.brand)}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">({b.count.toLocaleString()})</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-foreground">{formatCurrency(b.total)}</div>
                  <div className="text-xs text-muted-foreground">{b.percent.toFixed(1)}%</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex items-baseline justify-between gap-3 border-t border-border pt-3">
            <span className="text-sm font-semibold text-foreground">Total</span>
            <span className="text-base font-bold text-primary">{formatCurrency(total)}</span>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryStat({
  title,
  value,
  subtitle,
  icon: Icon,
  tone,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ElementType;
  tone: 'green' | 'yellow' | 'orange' | 'red';
}) {
  // Full-card tinted backgrounds matching the Credit Accounts aging cards
  // (Reports → POS → Customers → Credit Accounts).
  const cardTone = {
    green: 'bg-green-50 border-green-200 text-green-900',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-900',
    orange: 'bg-orange-50 border-orange-200 text-orange-900',
    red: 'bg-red-50 border-red-200 text-red-900',
  }[tone];
  return (
    <div className={cn('rounded-xl border p-5', cardTone)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide opacity-80">{title}</p>
          <p className="mt-2 text-2xl font-semibold">{value}</p>
          {subtitle && <p className="mt-1 text-xs opacity-70">{subtitle}</p>}
        </div>
        <Icon className="h-5 w-5 shrink-0 opacity-70" />
      </div>
    </div>
  );
}

function DetailRow({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2">
      <div>
        <span className="text-sm text-muted-foreground">{label}</span>
        {hint && <span className="ml-2 text-xs text-muted-foreground/70">{hint}</span>}
      </div>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}

// Sales Details — merchandise-revenue waterfall, all pre-tax / pre-tips:
//   Gross Sales (subtotal, before discount) → − Discount → Net Sales
//   → − Refunds → Net Sales After Refunds. Tips & Tax are shown as
//   informational add-ons, then folded into Total Payment Collected.
// Gift cards sold are not sales (the API leaves them out of every sales
// figure), but their money was collected, so they are added back for it.
// Subtotal is pre-tax AND pre-discount (total = subtotal + tax − discount),
// so Gross = subtotal and Net = subtotal − discount. Shared by the Sales
// Details card, the Overview "Net Sales" card, and the CSV export so all
// three agree on every figure.
function salesDetailFigures(summary: PosReportSummary) {
  const grossSales = summary.sales.subtotal;
  const netSales = grossSales - summary.sales.discount;
  const refunds = Math.abs(summary.refunds.total);
  const netSalesAfterRefunds = netSales - refunds;
  // Credit Account (Pay-by-Credit / house account) sales are already inside Net
  // Sales but are deferred A/R, not yet collected. Accrual total backs them out.
  const creditAccounts = summary.creditAccountSummary.creditAccountSales;
  const totalAccrual = netSalesAfterRefunds - creditAccounts;
  const giftCardsSold = summary.giftCardsSold.amount;
  const totalPaymentCollected = netSalesAfterRefunds + summary.sales.tips + summary.sales.tax + giftCardsSold;
  return {
    grossSales,
    netSales,
    refunds,
    netSalesAfterRefunds,
    creditAccounts,
    totalAccrual,
    giftCardsSold,
    totalPaymentCollected,
  };
}

function SalesDetailsCard({ summary }: { summary: PosReportSummary }) {
  const {
    grossSales,
    netSales,
    refunds,
    netSalesAfterRefunds,
    creditAccounts,
    totalAccrual,
    giftCardsSold,
    totalPaymentCollected,
  } = salesDetailFigures(summary);

  return (
    <div className="rounded-xl border border-border bg-white p-5 lg:order-2">
      <div className="flex items-center justify-between border-b border-border pb-3">
        <h3 className="text-sm font-semibold text-foreground">Sales Details - Information</h3>
        <div className="flex flex-col items-end text-xs text-muted-foreground">
          <span>Net Sales = Gross − Discount</span>
          <span>Collected = Net − Refunds + Tips + Tax + Gift Cards</span>
        </div>
      </div>
      <div className="divide-y divide-border">
        <div className="flex items-baseline justify-between gap-3 pt-3">
          <div>
            <span className="text-sm font-semibold text-foreground">Gross Sales</span>
            <span className="ml-2 text-xs font-normal text-muted-foreground/70">
              Excl. Tax &amp; Tips, Incl. Discounts
            </span>
          </div>
          <span className="text-base font-semibold text-foreground">{formatCurrency(grossSales)}</span>
        </div>
        <DetailRow label="Discount" value={formatSignedCurrency(summary.sales.discount, '-')} />
        <div className="flex items-baseline justify-between gap-3 py-2">
          <div>
            <span className="text-sm font-semibold text-foreground">Net Sales</span>
            <span className="ml-2 text-xs font-normal text-muted-foreground/70">Excl. Discounts</span>
          </div>
          <span className="text-base font-semibold text-foreground">{formatCurrency(netSales)}</span>
        </div>
        <DetailRow label="Refunds" value={formatSignedCurrency(refunds, '-')} />
        <div className="flex items-baseline justify-between gap-3 pt-3">
          <span className="text-sm font-semibold text-primary">Net Sales - After Refunds</span>
          <span className="text-base font-semibold text-primary">{formatCurrency(netSalesAfterRefunds)}</span>
        </div>
        {/* Credit Account (deferred A/R) sales backed out to reach the accrual total. */}
        <DetailRow label="Credit Accounts" value={formatSignedCurrency(creditAccounts, '-')} />
        <div className="flex items-baseline justify-between gap-3 pt-3">
          <span className="text-sm font-semibold text-primary">Net Sales - After Refunds - (Cash Accounting)</span>
          <span className="text-base font-semibold text-foreground">{formatCurrency(totalAccrual)}</span>
        </div>
        {/* Tips & Tax are collected on top of net sales — shown for info, then added below. */}
        <DetailRow label="Tips" hint="Information" value={formatSignedCurrency(summary.sales.tips, '+')} />
        <DetailRow label="Tax" hint="Information" value={formatSignedCurrency(summary.sales.tax, '+')} />
        {/* Gift cards sold: money collected, but not a sale, so not in the figures above. */}
        <DetailRow
          label="Gift Cards Sold"
          hint={`${summary.giftCardsSold.count} ${summary.giftCardsSold.count === 1 ? 'card' : 'cards'} · not a sale`}
          value={formatSignedCurrency(giftCardsSold, '+')}
        />
        <div className="flex items-baseline justify-between gap-3 pt-3">
          <span className="text-sm font-semibold text-foreground">Net Payment Collected (incl. Tips, Tax)</span>
          <span className="text-base font-semibold text-foreground">{formatCurrency(totalPaymentCollected)}</span>
        </div>
      </div>
    </div>
  );
}

function PosSummaryTab() {
  // Today, full day (00:00 → 23:59) in the reporting timezone — unless a deep
  // link carried a range in, which the home page's Payment Types and Sales
  // Breakdown cards both do.
  const initialRange = initialDailyRange();

  const [dateFrom, setDateFrom] = useState(initialRange.dateFrom);
  const [dateTo, setDateTo] = useState(initialRange.dateTo);
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  // Follows the global reporting timezone (ReportingTimeZoneBar) so the picker
  // display and the query boundaries resolve to the same zone.
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const { data, isLoading } = usePosReportSummary({
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    locationId: selectedLocationId || undefined,
  });
  const summary: PosReportSummary | undefined = data?.data;

  // Export the full page — Overview, the Sales & Refunds matrix, Sales Details,
  // both payment-method breakdowns, the Refunds split, the Cash Drawer, the
  // Electronic Settlement card, and the per-station breakdown. Section order
  // mirrors the on-screen layout.
  const handleExport = () => {
    if (!summary) return;
    const sd = salesDetailFigures(summary);
    const salesByCol = bucketByPaymentColumn(summary.paymentMethods);
    const refundsByCol = bucketByPaymentColumn(summary.refundPaymentMethods);
    const arByCol = bucketByPaymentColumn(summary.creditAccountSummary.paymentsByMethod);
    // Net row mirrors the on-screen table: Sales − Refunds + A/R collected.
    const netByCol = Object.fromEntries(
      PAYMENT_MATRIX_COLUMNS.map((c) => [c, salesByCol[c] - Math.abs(refundsByCol[c]) + arByCol[c]]),
    ) as Record<PaymentMatrixColumn, number>;
    // Numeric cells for one matrix row: the immediate-tender cells, then the
    // three totals (Cash Counting · Credit Acc · Accrual).
    const matrixCsvCells = (
      byCol: Record<PaymentMatrixColumn, number>,
      arByCol?: Record<PaymentMatrixColumn, number>,
    ) => {
      const t = matrixRowTotals(byCol, arByCol);
      return [
        ...CASH_COUNTING_COLUMNS.map((c) => byCol[c].toFixed(2)),
        t.cashCounting.toFixed(2),
        t.creditAcc.toFixed(2),
        t.accrual.toFixed(2),
      ];
    };
    const matrixCsvRow = (
      label: string,
      byCol: Record<PaymentMatrixColumn, number>,
      arByCol?: Record<PaymentMatrixColumn, number>,
    ) => [label, ...matrixCsvCells(byCol, arByCol)];

    // Electronic Settlement — Card + EBT net of refunds (mirrors the on-screen card).
    const esCard = summary.paymentMethods
      .filter((m) => paymentMatrixColumnFor(m.method) === 'Card')
      .reduce((s, m) => s + m.total, 0);
    const esEbt = summary.paymentMethods
      .filter((m) => paymentMatrixColumnFor(m.method) === 'EBT')
      .reduce((s, m) => s + m.total, 0);
    // Only Card/EBT (electronically-settled) refunds net here — not cash/cheque/
    // store-credit refunds. Totals are negative, so adding reduces the deposit.
    const esCardRefunds = summary.refundPaymentMethods
      .filter((m) => paymentMatrixColumnFor(m.method) === 'Card')
      .reduce((s, m) => s + m.total, 0);
    const esEbtRefunds = summary.refundPaymentMethods
      .filter((m) => paymentMatrixColumnFor(m.method) === 'EBT')
      .reduce((s, m) => s + m.total, 0);
    // Card-terminal A/R collections deposit through the processor — include them.
    const esCardAr = summary.creditAccountSummary.paymentsByMethod
      .filter((m) => paymentMatrixColumnFor(m.method) === 'Card')
      .reduce((s, m) => s + m.total, 0);
    const esFinal = esCard + esEbt + esCardAr + esCardRefunds + esEbtRefunds;

    // Sales by POS Station — grand net totals per column across every station.
    const stationGrandByCol = Object.fromEntries(PAYMENT_MATRIX_COLUMNS.map((c) => [c, 0])) as Record<
      PaymentMatrixColumn,
      number
    >;
    // Grand A/R bucket — excluded from the All Stations (Net) accrual total.
    const stationGrandArByCol = Object.fromEntries(PAYMENT_MATRIX_COLUMNS.map((c) => [c, 0])) as Record<
      PaymentMatrixColumn,
      number
    >;
    for (const st of summary.stationBreakdown) {
      const s = bucketByPaymentColumn(st.sales);
      const r = bucketByPaymentColumn(st.refunds);
      const a = bucketByPaymentColumn(st.arPayments);
      for (const c of PAYMENT_MATRIX_COLUMNS) {
        stationGrandByCol[c] += s[c] - Math.abs(r[c]) + a[c];
        stationGrandArByCol[c] += a[c];
      }
    }

    // Plain CSV can't carry bold, so section titles are UPPERCASED and wrapped in
    // brackets, each preceded by a blank line, so they stand out in the grid.
    // IMPORTANT: never start a cell with '=' (also '+' / '@') — Google Sheets and
    // Excel parse those as formulas and show an error on import. Brackets keep the
    // section headers spreadsheet-safe; most clients open these in Google Sheets.
    const section = (t: string): (string | number)[][] => [[], [`[ ${t.toUpperCase()} ]`]];

    const rows: (string | number)[][] = [
      ['SALES SUMMARY FOR CURRENT LOCATION'],
      ['Date Range', `${dateFrom || 'All'} → ${dateTo || 'All'}`],
      ['Location', selectedLocationName || 'All locations'],
      ...section('Overview'),
      ['Net Sales (After Refund)', sd.netSalesAfterRefunds.toFixed(2)],
      ['Total Transactions', summary.analytics.totalTransactions],
      ['Avg Per Transaction', summary.analytics.avgPerTransaction.toFixed(2)],
      ['Refunds', summary.refunds.total.toFixed(2)],
      ['Returns', summary.refunds.fromOrders.count + summary.refunds.withoutOrder.count],
      ['Bank Drop', summary.cashDrawer.bankDropTotal.toFixed(2)],
      // Surfaced right after Overview to match the on-screen order (top of page).
      ...section('Payments & Refunds by Payment Method'),
      ['', ...CASH_COUNTING_COLUMNS, 'Total (Cash Accounting)', 'Credit Acc', 'Total (Accrual Accounting)'],
      matrixCsvRow('Payments - Collected', salesByCol),
      matrixCsvRow('Refunds - Issued', refundsByCol),
      matrixCsvRow('A/R - Collected', arByCol, arByCol),
      matrixCsvRow('Net Payments - Collected', netByCol, arByCol),
      ...section('Sales Details - Information'),
      ['Gross Sales (Excl. Tax & Tips, Incl. Discounts)', sd.grossSales.toFixed(2)],
      ['Discount', (-summary.sales.discount).toFixed(2)],
      ['Net Sales (Excl. Discounts)', sd.netSales.toFixed(2)],
      ['Refunds', (-sd.refunds).toFixed(2)],
      ['Net Sales - After Refunds', sd.netSalesAfterRefunds.toFixed(2)],
      ['Credit Accounts', (-sd.creditAccounts).toFixed(2)],
      ['Total (Accrual Accounting)', sd.totalAccrual.toFixed(2)],
      ['Tips', summary.sales.tips.toFixed(2)],
      ['Tax', summary.sales.tax.toFixed(2)],
      ['Gift Cards Sold', sd.giftCardsSold.toFixed(2), `${summary.giftCardsSold.count} cards`],
      ['Net Payment Collected (incl. Tips, Tax)', sd.totalPaymentCollected.toFixed(2)],
      ...section('Total Payment Collected by Payment Method'),
      ['Method', 'Amount ($)', 'Count', 'Percent (%)'],
      ...summary.paymentMethods.map((p) => [
        paymentMethodLabel(p.method),
        p.total.toFixed(2),
        p.count,
        p.percent.toFixed(1),
      ]),
      ['Total', summary.paymentMethodsTotal.toFixed(2)],
      ...section('Total Refunds by Payment Method'),
      ['Method', 'Amount ($)', 'Count', 'Percent (%)'],
      ...summary.refundPaymentMethods.map((p) => [
        paymentMethodLabel(p.method),
        p.total.toFixed(2),
        p.count,
        p.percent.toFixed(1),
      ]),
      ['Total', summary.refundPaymentMethodsTotal.toFixed(2)],
      ...section('Refunds'),
      [
        'Refunds from Orders',
        summary.refunds.fromOrders.total.toFixed(2),
        `${summary.refunds.fromOrders.count} returns`,
      ],
      [
        'Refunds without Order',
        summary.refunds.withoutOrder.total.toFixed(2),
        `${summary.refunds.withoutOrder.count} returns`,
      ],
      ['Total Refunded', summary.refunds.total.toFixed(2)],
      ...section('Card Sales by Brand'),
      ['Brand', 'Amount ($)', 'Transactions', 'Percent (%)'],
      ...summary.cardBrands.map((b) => [cardBrandLabel(b.brand), b.total.toFixed(2), b.count, b.percent.toFixed(1)]),
      ['Total', summary.cardBrandsTotal.toFixed(2)],
      ...section('Cash Drawer'),
      ['Type', 'Amount ($)', 'Events'],
      ...summary.cashDrawer.byType.map((d) => [d.type, d.total.toFixed(2), d.count]),
      ['Bank Drop Total', summary.cashDrawer.bankDropTotal.toFixed(2)],
      ...section('Electronic Settlement'),
      ['Card Transactions', esCard.toFixed(2)],
      ['EBT Transactions', esEbt.toFixed(2)],
      ['Card A/R Collected', esCardAr.toFixed(2)],
      ['Card Refunds', esCardRefunds.toFixed(2)],
      ['Final Settlement', esFinal.toFixed(2)],
      ...section('Store Credit Summary'),
      ['Store Credit Issued', summary.storeCreditSummary.issued.toFixed(2)],
      ['Store Credit Redeemed', summary.storeCreditSummary.redeemed.toFixed(2)],
      ['Opening Liability', summary.storeCreditSummary.openingLiability.toFixed(2)],
      ['Ending Liability', summary.storeCreditSummary.endingLiability.toFixed(2)],
      ...section('Gift Card Summary'),
      ['Gift Card Issued', summary.giftCardSummary.issued.toFixed(2)],
      ['Gift Card Redeemed', summary.giftCardSummary.redeemed.toFixed(2)],
      ['Opening Liability', summary.giftCardSummary.openingLiability.toFixed(2)],
      ['Ending Liability', summary.giftCardSummary.endingLiability.toFixed(2)],
      ...section('Credit Accounts Receivable'),
      ['Opening A/R Balance', summary.creditAccountSummary.openingARBalance.toFixed(2)],
      ['Credit Account Sales', summary.creditAccountSummary.creditAccountSales.toFixed(2)],
      ['Credit Account Paid', summary.creditAccountSummary.paymentsReceived.toFixed(2)],
      ['Ending A/R Balance', summary.creditAccountSummary.endingARBalance.toFixed(2)],
      ...section('Sales by POS Station'),
      [
        'Station',
        'Type',
        ...CASH_COUNTING_COLUMNS,
        'Total (Cash Accounting)',
        'Credit Acc',
        'Total (Accrual Accounting)',
      ],
      ...summary.stationBreakdown.flatMap((st) => {
        const s = bucketByPaymentColumn(st.sales);
        const r = bucketByPaymentColumn(st.refunds);
        const a = bucketByPaymentColumn(st.arPayments);
        const net = Object.fromEntries(PAYMENT_MATRIX_COLUMNS.map((c) => [c, s[c] - Math.abs(r[c]) + a[c]])) as Record<
          PaymentMatrixColumn,
          number
        >;
        return [
          [st.stationName, 'Sales', ...matrixCsvCells(s)],
          ['', 'Refunds', ...matrixCsvCells(r)],
          ['', 'A / R', ...matrixCsvCells(a, a)],
          ['', 'Total', ...matrixCsvCells(net, a)],
          ['', 'Tips', ...CASH_COUNTING_COLUMNS.map(() => ''), '', '', st.tips.toFixed(2)],
        ];
      }),
      ['All Stations (Net)', '', ...matrixCsvCells(stationGrandByCol, stationGrandArByCol)],
    ];
    downloadCsv(reportFilename('sales_summary', selectedLocationName, 'location'), rows);
  };

  // Reset restores the default single-day range (today, 00:00–23:59 in the
  // reporting zone) — matching the Reset behaviour on the other report pages —
  // rather than clearing the dates to an empty (all-time) range.
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFrom(r.dateFrom);
    setDateTo(r.dateTo);
  };

  return (
    <div className="space-y-5">
      {/* Date filters — single reusable DatePicker in range mode, preset-first
          (reporting-zone aware). Replaces the former two From/To pickers; the
          state (dateFrom/dateTo strings) and reporting-zone conversion are
          unchanged so the query/export/reset behave identically. */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFrom}
            dateToInput={dateTo}
            onFromChange={setDateFrom}
            onToChange={setDateTo}
          />
          <div className="ml-auto flex items-center gap-3">
            <PosExportOnly>
              <Button variant="primary" size="sm" disabled={!summary} onClick={handleExport}>
                <Download className="h-3.5 w-3.5" />
                Export
              </Button>
            </PosExportOnly>
            <Button size="sm" variant="destructive-outline" onClick={handleReset}>
              Reset
            </Button>
          </div>
        </div>
      </div>

      {isLoading || !summary ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading summary...</div>
      ) : (
        <>
          {/* Top stat cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryStat
              title="Bank Drop"
              value={formatCurrency(summary.cashDrawer.bankDropTotal)}
              subtitle={summary.cashDrawer.bankDropTotal > 0 ? 'Cash deposited' : 'No drops in range'}
              icon={Banknote}
              tone="green"
            />
            <SummaryStat
              title="Avg Per Transaction"
              value={formatCurrency(summary.analytics.avgPerTransaction)}
              icon={ReceiptText}
              tone="yellow"
            />
            <SummaryStat
              title="Refunds"
              value={formatCurrency(summary.refunds.total)}
              subtitle={`${(summary.refunds.fromOrders.count + summary.refunds.withoutOrder.count).toLocaleString()} returns`}
              icon={RotateCcw}
              tone="orange"
            />
            <SummaryStat
              title="Net Sales"
              value={formatCurrency(salesDetailFigures(summary).netSalesAfterRefunds)}
              subtitle={`After refunds · ${summary.analytics.totalTransactions.toLocaleString()} transactions`}
              icon={TrendingUp}
              tone="red"
            />
          </div>

          {/* Sales & Refunds by Payment Method matrix */}
          <SalesRefundsByMethodTable
            sales={summary.paymentMethods}
            refunds={summary.refundPaymentMethods}
            arPayments={summary.creditAccountSummary.paymentsByMethod}
          />

          {/* Sales Details + Payment Methods */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Sales Details */}
            <SalesDetailsCard summary={summary} />

            {/* Payment Methods */}
            <div className="rounded-xl border border-border bg-white p-5 lg:order-1">
              <div className="flex items-center justify-between border-b border-border pb-3">
                <h3 className="text-sm font-semibold text-foreground">Total Payment Collected by Payment Method</h3>
              </div>
              {summary.paymentMethods.length === 0 ? (
                <EmptyState
                  icon={CreditCard}
                  title="No payments"
                  description="Try a different date range."
                  className="py-8"
                />
              ) : (
                <>
                  <ul className="space-y-3 pt-3">
                    {summary.paymentMethods.map((p) => {
                      const Icon = paymentMethodIcon(p.method);
                      return (
                        <li key={p.method} className="space-y-1.5">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm font-medium text-foreground">
                                {paymentMethodLabel(p.method)}
                              </span>
                              <span className="text-xs text-muted-foreground">({p.count})</span>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold text-foreground">{formatCurrency(p.total)}</div>
                              <div className="text-xs text-muted-foreground">{p.percent.toFixed(1)}%</div>
                            </div>
                          </div>
                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full bg-primary transition-all"
                              style={{ width: `${Math.min(100, p.percent)}%` }}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="mt-3 flex items-baseline justify-between gap-3 border-t border-border pt-3">
                    <span className="text-sm font-semibold text-foreground">Total</span>
                    <span className="text-base font-bold text-primary">
                      {formatCurrency(summary.paymentMethodsTotal)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Refunds + Net Refunds by Payment Method */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-border bg-white p-5 lg:order-2">
              <div className="flex items-center justify-between border-b border-border pb-3">
                <h3 className="text-sm font-semibold text-foreground">Refunds</h3>
              </div>
              <div className="divide-y divide-border">
                <DetailRow
                  label="Refunds from Orders"
                  hint={`${summary.refunds.fromOrders.count} returns`}
                  value={formatSignedCurrency(summary.refunds.fromOrders.total, '-')}
                />
                <DetailRow
                  label="Refunds without Order"
                  hint={`${summary.refunds.withoutOrder.count} returns`}
                  value={formatSignedCurrency(summary.refunds.withoutOrder.total, '-')}
                />
                <div className="flex items-baseline justify-between gap-3 pt-3">
                  <span className="text-sm font-semibold text-foreground">Total Refunded</span>
                  <span className="text-base font-semibold text-foreground">
                    {formatSignedCurrency(summary.refunds.total, '-')}
                  </span>
                </div>
              </div>
            </div>

            {/* Net Refunds by Payment Method */}
            <div className="rounded-xl border border-border bg-white p-5 lg:order-1">
              <div className="flex items-center justify-between border-b border-border pb-3">
                <h3 className="text-sm font-semibold text-foreground">Total Refunds by Payment Method</h3>
              </div>
              {summary.refundPaymentMethods.length === 0 ? (
                <EmptyState
                  icon={RotateCcw}
                  title="No refunds"
                  description="Try a different date range."
                  className="py-8"
                />
              ) : (
                <>
                  <ul className="space-y-3 pt-3">
                    {summary.refundPaymentMethods.map((p) => {
                      const Icon = paymentMethodIcon(p.method);
                      return (
                        <li key={p.method} className="space-y-1.5">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm font-medium text-foreground">
                                {paymentMethodLabel(p.method)}
                              </span>
                              <span className="text-xs text-muted-foreground">({p.count})</span>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold text-foreground">{formatCurrency(p.total)}</div>
                              <div className="text-xs text-muted-foreground">{p.percent.toFixed(1)}%</div>
                            </div>
                          </div>
                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full bg-primary transition-all"
                              style={{ width: `${Math.min(100, p.percent)}%` }}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="mt-3 flex items-baseline justify-between gap-3 border-t border-border pt-3">
                    <span className="text-sm font-semibold text-foreground">Total</span>
                    <span className="text-base font-bold text-primary">
                      {formatCurrency(summary.refundPaymentMethodsTotal)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Card Sales by Brand */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <CardBrandCard summary={summary} />
          </div>

          {/* Cash Drawer */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-xl border border-border bg-white p-5">
              <div className="flex items-center justify-between border-b border-border pb-3">
                <h3 className="text-sm font-semibold text-foreground">Cash Drawer</h3>
              </div>
              {summary.cashDrawer.byType.length === 0 ? (
                <EmptyState
                  icon={Banknote}
                  title="No cash drawer activity"
                  description="Pay-ins, pay-outs and drops will appear here."
                  className="py-8"
                />
              ) : (
                (() => {
                  const byType = summary.cashDrawer.byType;
                  const totalOf = (t: string) => byType.find((d) => d.type === t)?.total ?? 0;
                  // Cash in hand = cash collected from sales less change handed back.
                  const totalCashInHand = totalOf('cash_sale') - totalOf('change_given');
                  // Show the primary cash-flow rows first (Cash Open · Cash
                  // Collected · Change Given), then any other drawer events.
                  const primaryOrder = ['open_drawer', 'cash_open', 'cash_sale', 'change_given'];
                  const rank = (t: string) => {
                    const i = primaryOrder.indexOf(t);
                    return i === -1 ? primaryOrder.length : i;
                  };
                  const ordered = [...byType].sort((a, b) => rank(a.type) - rank(b.type));
                  return (
                    <div className="divide-y divide-border">
                      {ordered.map((d) => (
                        <DetailRow
                          key={d.type}
                          label={cashDrawerLabel(d.type)}
                          hint={`${d.count} events`}
                          value={formatCurrency(d.total)}
                        />
                      ))}
                      <div className="flex items-baseline justify-between gap-3 pt-3">
                        <span className="text-sm font-semibold text-primary">Total Cash In Hand</span>
                        <span className="text-base font-bold text-primary">{formatCurrency(totalCashInHand)}</span>
                      </div>
                    </div>
                  );
                })()
              )}
            </div>

            {/* Electronic Settlement — Card + EBT net of refunds */}
            <ElectronicSettlementCard summary={summary} />

            {/* Store Credit Summary — liability, computed off the store-credit ledger */}
            <StoreCreditSummaryCard summary={summary} />

            {/* Gift Card Summary — gift-card slice of the same ledger, mirrors Store Credit */}
            <GiftCardSummaryCard summary={summary} />

            {/* Credit Accounts Receivable — A/R rollup: credit sales create A/R, payments reduce it */}
            <CreditAccountsReceivableCard summary={summary} />
          </div>

          {/* Sales by POS Station — per-register matrix, same columns as above */}
          <SalesByStationTable stations={summary.stationBreakdown} />
        </>
      )}
    </div>
  );
}

// Columns for the Sales & Refunds matrix. Shared by the on-screen table and the
// Summary CSV export so both stay in sync. The customer Credit Account
// (Pay-by-Credit) tender has its own 'Credit Acc' column; Store Credit and any
// unrecognized tender fold into 'Others'.
const PAYMENT_MATRIX_COLUMNS = ['Cash', 'Card', 'EBT', 'Cheque', 'Others', 'Gift Card Redeemed', 'Credit Acc'] as const;
type PaymentMatrixColumn = (typeof PAYMENT_MATRIX_COLUMNS)[number];

function paymentMatrixColumnFor(method: string): PaymentMatrixColumn {
  const m = method.toLowerCase();
  if (m === 'cash') return 'Cash';
  if (['card', 'debit'].includes(m)) return 'Card';
  if (m === 'ebt') return 'EBT';
  if (['cheque', 'check'].includes(m)) return 'Cheque';
  // Customer Credit Account (Pay-by-Credit) — its own column, not Others.
  if (m === 'credit') return 'Credit Acc';
  if (['gift', 'gift_card', 'gift_cards'].includes(m)) return 'Gift Card Redeemed';
  // Store Credit and any unrecognized tender fall into Others.
  return 'Others';
}

// Renders a payment-matrix column header. Columns ending in "Redeemed" (Gift
// Card) are long, so split the trailing word onto a second "(Redeemed)" line to
// keep the column narrow. The header row applies `uppercase`, so both lines follow.
function MatrixColumnHeader({ label }: { label: PaymentMatrixColumn }) {
  const suffix = ' Redeemed';
  if (label.endsWith(suffix)) {
    return (
      <span className="inline-flex flex-col items-end leading-tight">
        <span>{label.slice(0, -suffix.length)}</span>
        <span className="text-muted-foreground/70">(Redeemed)</span>
      </span>
    );
  }
  return <>{label}</>;
}

function bucketByPaymentColumn(rows: { method: string; total: number }[]): Record<PaymentMatrixColumn, number> {
  const acc = Object.fromEntries(PAYMENT_MATRIX_COLUMNS.map((c) => [c, 0])) as Record<PaymentMatrixColumn, number>;
  for (const r of rows) acc[paymentMatrixColumnFor(r.method)] += r.total;
  return acc;
}

// The immediate ("cash counting") tenders — every column except Credit Acc.
// These are money collected at the time of sale, so they sum into
// Total (Cash Counting). The customer Credit Account is deferred revenue and is
// reported separately, then added back for Total (Accrual Accounting).
const CASH_COUNTING_COLUMNS = PAYMENT_MATRIX_COLUMNS.filter((c) => c !== 'Credit Acc');

// Given a per-column bucket, derive the three trailing totals of the matrix:
//   Total (Cash Counting)      = sum of the immediate tenders (excludes Credit Acc)
//   Credit Acc                 = the deferred Credit Account tender on its own
//   Total (Accrual Accounting) = Cash Counting + Credit Acc − A/R collected
//
// A/R collections settle credit-account sales whose revenue was ALREADY
// recognised (in the Credit Acc column) at the time of the original sale. They
// arrive on an immediate tender (cash/card/etc.), so they legitimately sit in
// Total (Cash Accounting) — but re-adding them to the accrual total would
// double-count that revenue. Pass the row's A/R bucket via `arByCol` so its
// immediate-tender portion is removed from the accrual total; rows without A/R
// pass nothing and are unaffected.
function matrixRowTotals(byCol: Record<PaymentMatrixColumn, number>, arByCol?: Record<PaymentMatrixColumn, number>) {
  const cashCounting = CASH_COUNTING_COLUMNS.reduce((s, c) => s + byCol[c], 0);
  const creditAcc = byCol['Credit Acc'];
  const arImmediate = arByCol ? CASH_COUNTING_COLUMNS.reduce((s, c) => s + arByCol[c], 0) : 0;
  return { cashCounting, creditAcc, accrual: cashCounting + creditAcc - arImmediate };
}

// Column headers for the Sales & Refunds matrix, matching the CSV order:
// the immediate tenders, then the three trailing accounting columns.
const MATRIX_TRAILING_HEADERS = ['Total (Cash Accounting)', 'Credit Acc', 'Total (Accrual Accounting)'] as const;

// Renders a header that may carry a parenthetical qualifier — e.g.
// "Total (Cash Counting)" — stacked on a second line to keep the column narrow,
// mirroring the "Gift Card (Redeemed)" treatment. Plain labels render as-is.
function StackedHeader({ label }: { label: string }) {
  const idx = label.indexOf(' (');
  if (idx === -1) return <>{label}</>;
  return (
    <span className="inline-flex flex-col items-end leading-tight">
      <span>{label.slice(0, idx)}</span>
      <span className="text-muted-foreground/70">{label.slice(idx + 1)}</span>
    </span>
  );
}

// Electronic Settlement — the electronically-settled tenders (Card + EBT) net of
// refunds, i.e. the amount the processor deposits for the range. Card/EBT totals
// reuse the same bucketing as the Sales & Refunds matrix so the figures line up
// across the page.
function ElectronicSettlementCard({ summary }: { summary: PosReportSummary }) {
  const cardTotal = summary.paymentMethods
    .filter((m) => paymentMatrixColumnFor(m.method) === 'Card')
    .reduce((sum, m) => sum + m.total, 0);
  const ebtTotal = summary.paymentMethods
    .filter((m) => paymentMatrixColumnFor(m.method) === 'EBT')
    .reduce((sum, m) => sum + m.total, 0);
  // Only electronically-settled tenders net against the processor deposit — Card
  // (and EBT) refunds, NOT cash / cheque / store-credit refunds, which never hit
  // the card processor. Previously this subtracted summary.refunds.total (every
  // tender's refunds), overstating what the "Card Refunds" line reversed.
  // refundPaymentMethods totals are negative, so ADDING them reduces settlement.
  const cardRefundTotal = summary.refundPaymentMethods
    .filter((m) => paymentMatrixColumnFor(m.method) === 'Card')
    .reduce((sum, m) => sum + m.total, 0);
  const ebtRefundTotal = summary.refundPaymentMethods
    .filter((m) => paymentMatrixColumnFor(m.method) === 'EBT')
    .reduce((sum, m) => sum + m.total, 0);
  // A/R settlements collected on the card terminal (credit_account_payments,
  // method 'card') hit the card processor just like a regular sale, so they must
  // be part of the deposit. Cash/check/ach A/R collections never touch the
  // processor and are excluded. Mirrors the "A/R - Collected" Card cell of the
  // Payments & Refunds by Payment Method card.
  const cardArTotal = summary.creditAccountSummary.paymentsByMethod
    .filter((m) => paymentMatrixColumnFor(m.method) === 'Card')
    .reduce((sum, m) => sum + m.total, 0);
  const finalTotal = cardTotal + ebtTotal + cardArTotal + cardRefundTotal + ebtRefundTotal;

  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-center justify-between border-b border-border pb-3">
        <h3 className="text-sm font-semibold text-foreground">Electronic Settlement</h3>
        <CreditCard className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="divide-y divide-border">
        <DetailRow label="Card Transactions" value={formatCurrency(cardTotal)} />
        <DetailRow label="EBT Transactions" value={formatCurrency(ebtTotal)} />
        <DetailRow label="Card A/R Collected" value={formatCurrency(cardArTotal)} />
        <DetailRow label="Card Refunds" value={formatSignedCurrency(cardRefundTotal, '-')} />
        <div className="flex items-baseline justify-between gap-3 pt-3">
          <span className="text-sm font-semibold text-primary">Final Settlement</span>
          <span className="text-base font-bold text-primary">{formatCurrency(finalTotal)}</span>
        </div>
      </div>
    </div>
  );
}

// Store Credit Summary — high-level liability view for the range. Issuance is a
// liability (not revenue) and redemption reduces that liability; both come from
// the store-credit ledger (summary.storeCreditSummary), independent of sales.
// Ending Liability = Opening + Issued − Redeemed.
function StoreCreditSummaryCard({ summary }: { summary: PosReportSummary }) {
  const sc = summary.storeCreditSummary;
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-center justify-between border-b border-border pb-3">
        <h3 className="text-sm font-semibold text-foreground">Store Credit Summary</h3>
        <Wallet className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="divide-y divide-border">
        <DetailRow label="Opening Liability" value={formatCurrency(sc.openingLiability)} />
        <DetailRow label="Store Credit Issued" value={formatCurrency(sc.issued)} />
        <DetailRow label="Store Credit Redeemed" value={formatCurrency(sc.redeemed)} />
        <div className="flex items-baseline justify-between gap-3 pt-3">
          <span className="text-sm font-semibold text-primary">Ending Liability</span>
          <span className="text-base font-bold text-primary">{formatCurrency(sc.endingLiability)}</span>
        </div>
      </div>
    </div>
  );
}

// Gift Card Summary — high-level liability view for the range, mirroring Store
// Credit. Gift cards live on the same ledger (summary.giftCardSummary), split by
// payment_type. Issuance (cards sold) is a liability, not revenue; redemption
// reduces it. Ending Liability = Opening + Issued − Redeemed.
function GiftCardSummaryCard({ summary }: { summary: PosReportSummary }) {
  const gc = summary.giftCardSummary;
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-center justify-between border-b border-border pb-3">
        <h3 className="text-sm font-semibold text-foreground">Gift Card Summary</h3>
        <Gift className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="divide-y divide-border">
        <DetailRow label="Opening Liability" value={formatCurrency(gc.openingLiability)} />
        <DetailRow label="Gift Card Issued" value={formatCurrency(gc.issued)} />
        <DetailRow label="Gift Card Redeemed" value={formatCurrency(gc.redeemed)} />
        <div className="flex items-baseline justify-between gap-3 pt-3">
          <span className="text-sm font-semibold text-primary">Ending Liability</span>
          <span className="text-base font-bold text-primary">{formatCurrency(gc.endingLiability)}</span>
        </div>
      </div>
    </div>
  );
}

// Credit Accounts Receivable — high-level A/R view for the range. Credit Account
// Sales (credit-tendered POS sales) create A/R and are already in Gross/Net Sales;
// Payments Received are collections against prior balances and only reduce A/R.
// Ending A/R = Opening + Credit Account Sales − Payments Received. All values come
// from summary.creditAccountSummary; transaction-level detail is a future report.
function CreditAccountsReceivableCard({ summary }: { summary: PosReportSummary }) {
  const ar = summary.creditAccountSummary;
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-center justify-between border-b border-border pb-3">
        <h3 className="text-sm font-semibold text-foreground">Credit Accounts Receivable</h3>
        <ReceiptText className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="divide-y divide-border">
        <DetailRow label="Opening A/R Balance" value={formatCurrency(ar.openingARBalance)} />
        <DetailRow label="Credit Account Sales" value={formatCurrency(ar.creditAccountSales)} />
        <div className="flex items-baseline justify-between gap-3 py-2">
          <span className="text-sm font-semibold text-primary">Credit Account Paid</span>
          <span className="text-sm font-semibold text-primary">{formatCurrency(ar.paymentsReceived)}</span>
        </div>
        <div className="flex items-baseline justify-between gap-3 pt-3">
          <span className="text-sm font-semibold text-primary">Ending A/R Balance</span>
          <span className="text-base font-bold text-primary">{formatCurrency(ar.endingARBalance)}</span>
        </div>
      </div>
    </div>
  );
}

// Matrix view: payment methods across columns, with Sales / Refunds / Total
// (Sales − Refunds) rows. Sources the same per-method data as the two
// payment-method breakdown cards.
function SalesRefundsByMethodTable({
  sales,
  refunds,
  arPayments,
}: {
  sales: { method: string; total: number }[];
  refunds: { method: string; total: number }[];
  arPayments: { method: string; total: number }[];
}) {
  const salesByCol = bucketByPaymentColumn(sales);
  const refundsByCol = bucketByPaymentColumn(refunds);
  const arByCol = bucketByPaymentColumn(arPayments);
  // Net row = money actually taken in per column: Sales − Refunds + A/R collected.
  const netByCol = Object.fromEntries(
    PAYMENT_MATRIX_COLUMNS.map((c) => [c, salesByCol[c] - Math.abs(refundsByCol[c]) + arByCol[c]]),
  ) as Record<PaymentMatrixColumn, number>;

  const rows: {
    label: string;
    hint?: string;
    byCol: Record<PaymentMatrixColumn, number>;
    // A/R bucket to exclude from this row's accrual total (see matrixRowTotals):
    // set on the A/R row itself and the Net row that folds A/R in.
    arContext?: Record<PaymentMatrixColumn, number>;
    emphasize?: boolean;
  }[] = [
    { label: 'Payments - Collected', byCol: salesByCol },
    { label: 'Refunds - Issued', byCol: refundsByCol },
    { label: 'A/R - Collected', byCol: arByCol, arContext: arByCol },
    { label: 'Net Payments - Collected', byCol: netByCol, arContext: arByCol, emphasize: true },
  ];

  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-center justify-between border-b border-border pb-3">
        <h3 className="text-sm font-semibold text-foreground">Payments &amp; Refunds by Payment Method</h3>
        <span className="text-xs text-muted-foreground">Net Payments = Payments − Refunds + A/R</span>
      </div>
      <div className="overflow-x-auto pt-3">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
              <th className="px-3 py-2 font-medium" />
              {CASH_COUNTING_COLUMNS.map((c) => (
                <th key={c} className="px-3 py-2 text-right font-medium whitespace-nowrap align-bottom">
                  <MatrixColumnHeader label={c} />
                </th>
              ))}
              {MATRIX_TRAILING_HEADERS.map((h, i) => (
                <th
                  key={h}
                  className={`px-3 py-2 text-right whitespace-nowrap align-bottom ${
                    i === 0 ? 'border-l border-border' : ''
                  } ${h === 'Credit Acc' ? 'font-medium' : 'font-semibold text-foreground'}`}
                >
                  <StackedHeader label={h} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const t = matrixRowTotals(row.byCol, row.arContext);
              const cellTone = row.emphasize ? 'font-semibold text-primary' : 'text-foreground';
              const totalTone = row.emphasize ? 'text-primary' : 'text-foreground';
              return (
                <tr key={row.label} className={row.emphasize ? '' : 'border-b border-border/60'}>
                  <td
                    className={`px-3 py-2 font-medium ${row.emphasize ? 'font-semibold text-primary' : 'text-foreground'}`}
                  >
                    {row.label}
                    {row.hint && (
                      <span className="ml-1.5 text-xs font-normal text-muted-foreground/70">{row.hint}</span>
                    )}
                  </td>
                  {CASH_COUNTING_COLUMNS.map((c) => (
                    <td key={c} className={`px-3 py-2 text-right ${cellTone}`}>
                      {formatCurrency(row.byCol[c])}
                    </td>
                  ))}
                  <td className={`border-l border-border px-3 py-2 text-right font-semibold ${totalTone}`}>
                    {formatCurrency(t.cashCounting)}
                  </td>
                  <td className={`px-3 py-2 text-right ${cellTone}`}>{formatCurrency(t.creditAcc)}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${totalTone}`}>{formatCurrency(t.accrual)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Per-station matrix: each POS register (station) is a grouped section with a
// Sales row and a Refunds row across the same columns as the Sales & Refunds
// matrix, plus a net Total (Sales − Refunds) row and station total. Sourced from
// summary.stationBreakdown.
function SalesByStationTable({
  stations,
}: {
  stations: {
    registerId: string | null;
    stationName: string;
    sales: { method: string; total: number }[];
    refunds: { method: string; total: number }[];
    arPayments: { method: string; total: number }[];
    salesTotal: number;
    refundsTotal: number;
    tips: number;
  }[];
}) {
  const colCount = CASH_COUNTING_COLUMNS.length + 4; // Station label + methods + 3 totals

  // Grand totals across every station (footer row): Sales − Refunds + A/R per
  // column, matching the Sales & Refunds by Payment Method card's Total.
  const grandByCol = Object.fromEntries(PAYMENT_MATRIX_COLUMNS.map((c) => [c, 0])) as Record<
    PaymentMatrixColumn,
    number
  >;
  // Grand A/R bucket — excluded from the All Stations (Net) accrual total so A/R
  // collections aren't double-counted there (see matrixRowTotals).
  const grandArByCol = Object.fromEntries(PAYMENT_MATRIX_COLUMNS.map((c) => [c, 0])) as Record<
    PaymentMatrixColumn,
    number
  >;
  for (const st of stations) {
    const salesByCol = bucketByPaymentColumn(st.sales);
    const refundsByCol = bucketByPaymentColumn(st.refunds);
    const arByCol = bucketByPaymentColumn(st.arPayments);
    for (const c of PAYMENT_MATRIX_COLUMNS) {
      grandByCol[c] += salesByCol[c] - Math.abs(refundsByCol[c]) + arByCol[c];
      grandArByCol[c] += arByCol[c];
    }
  }

  // Trailing cells (Total Cash Counting · Credit Acc · Total Accrual) for a row.
  // `cellTone` styles the immediate-tender + Credit Acc cells; `totalTone` the two
  // bold total cells. Pass `arByCol` on rows carrying A/R (the A/R row and any Net
  // row) so its immediate portion is kept out of the accrual total.
  const trailingCells = (
    byCol: Record<PaymentMatrixColumn, number>,
    cellTone: string,
    totalTone: string,
    arByCol?: Record<PaymentMatrixColumn, number>,
  ) => {
    const t = matrixRowTotals(byCol, arByCol);
    return (
      <>
        <td className={`border-l border-border px-3 py-2 text-right font-semibold ${totalTone}`}>
          {formatCurrency(t.cashCounting)}
        </td>
        <td className={`px-3 py-2 text-right ${cellTone}`}>{formatCurrency(t.creditAcc)}</td>
        <td className={`px-3 py-2 text-right font-semibold ${totalTone}`}>{formatCurrency(t.accrual)}</td>
      </>
    );
  };

  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-center justify-between border-b border-border pb-3">
        <h3 className="text-sm font-semibold text-foreground">Sales by POS Station</h3>
        <span className="text-xs text-muted-foreground">Per station · Total = Sales − Refunds + A/R</span>
      </div>
      <div className="overflow-x-auto pt-3">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
              <th className="px-3 py-2 font-medium">Station</th>
              {CASH_COUNTING_COLUMNS.map((c) => (
                <th key={c} className="px-3 py-2 text-right font-medium whitespace-nowrap align-bottom">
                  <MatrixColumnHeader label={c} />
                </th>
              ))}
              {MATRIX_TRAILING_HEADERS.map((h, i) => (
                <th
                  key={h}
                  className={`px-3 py-2 text-right whitespace-nowrap align-bottom ${
                    i === 0 ? 'border-l border-border' : ''
                  } ${h === 'Credit Acc' ? 'font-medium' : 'font-semibold text-foreground'}`}
                >
                  <StackedHeader label={h} />
                </th>
              ))}
            </tr>
          </thead>
          {stations.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={colCount}>
                  <EmptyState icon={Store} title="No station activity" description="Try a different date range." />
                </td>
              </tr>
            </tbody>
          ) : (
            stations.map((st) => {
              const salesByCol = bucketByPaymentColumn(st.sales);
              const refundsByCol = bucketByPaymentColumn(st.refunds);
              const arByCol = bucketByPaymentColumn(st.arPayments);
              const netByCol = Object.fromEntries(
                PAYMENT_MATRIX_COLUMNS.map((c) => [c, salesByCol[c] - Math.abs(refundsByCol[c]) + arByCol[c]]),
              ) as Record<PaymentMatrixColumn, number>;
              return (
                <tbody key={st.registerId ?? st.stationName} className="border-b-4 border-border/40">
                  {/* Station group header — with tips collected at this station */}
                  <tr className="bg-muted/40">
                    <td colSpan={colCount} className="px-3 py-2 text-sm font-semibold text-foreground">
                      <div className="flex items-center justify-between gap-3">
                        <span>{st.stationName}</span>
                        {st.tips > 0 && (
                          <span className="text-xs font-medium text-muted-foreground">
                            Tips: {formatCurrency(st.tips)}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Sales row */}
                  <tr className="border-b border-border/60">
                    <td className="px-3 py-2 pl-6 font-medium text-foreground">Sales</td>
                    {CASH_COUNTING_COLUMNS.map((c) => (
                      <td key={c} className="px-3 py-2 text-right">
                        {formatCurrency(salesByCol[c])}
                      </td>
                    ))}
                    {trailingCells(salesByCol, 'text-foreground', 'text-foreground')}
                  </tr>
                  {/* Refunds row */}
                  <tr className="border-b border-border/60">
                    <td className="px-3 py-2 pl-6 font-medium text-foreground">Refunds</td>
                    {CASH_COUNTING_COLUMNS.map((c) => (
                      <td key={c} className="px-3 py-2 text-right">
                        {formatCurrency(refundsByCol[c])}
                      </td>
                    ))}
                    {trailingCells(refundsByCol, 'text-foreground', 'text-foreground')}
                  </tr>
                  {/* A/R row — settlement payments collected at this station,
                       matching the global Sales & Refunds by Payment Method card. */}
                  <tr className="border-b border-border/60">
                    <td className="px-3 py-2 pl-6 font-medium text-foreground">A / R</td>
                    {CASH_COUNTING_COLUMNS.map((c) => (
                      <td key={c} className="px-3 py-2 text-right">
                        {formatCurrency(arByCol[c])}
                      </td>
                    ))}
                    {trailingCells(arByCol, 'text-foreground', 'text-foreground', arByCol)}
                  </tr>
                  {/* Net total row — per-station subtotal, indigo to distinguish
                       it from the teal All Stations (Net) grand total below. */}
                  <tr>
                    <td className="px-3 py-2 pl-6 font-semibold text-indigo-600">Total</td>
                    {CASH_COUNTING_COLUMNS.map((c) => (
                      <td key={c} className="px-3 py-2 text-right font-semibold text-indigo-600">
                        {formatCurrency(netByCol[c])}
                      </td>
                    ))}
                    {trailingCells(netByCol, 'font-semibold text-indigo-600', 'text-indigo-600', arByCol)}
                  </tr>
                </tbody>
              );
            })
          )}
          {stations.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-border">
                <td className="px-3 py-2 font-semibold text-primary">All Stations (Net)</td>
                {CASH_COUNTING_COLUMNS.map((c) => (
                  <td key={c} className="px-3 py-2 text-right font-semibold text-primary">
                    {formatCurrency(grandByCol[c])}
                  </td>
                ))}
                {trailingCells(grandByCol, 'font-semibold text-primary', 'text-primary', grandArByCol)}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function PosPaymentsPerStationRowCard({ row }: { row: PosPaymentsPerStationRow }) {
  // Two-column layout matching the reference UI: primary methods on the left,
  // tips / check on the right. Keep every row visible (even at $0.00) so the
  // user gets a consistent picture of the station's takings.
  const leftRows: Array<{ label: string; value: number }> = [
    { label: 'Card', value: row.methods.card },
    { label: 'Cash', value: row.methods.cash },
    { label: 'EBT', value: row.methods.ebt },
    { label: 'Gift Card / Store Credit', value: row.methods.giftCardOrStoreCredit },
    { label: 'Other', value: row.methods.other },
  ];
  const rightRows: Array<{ label: string; value: number; note?: string }> = [
    { label: 'Credit Acc', value: row.methods.creditAccount, note: 'Purchases charged to a customer credit account' },
    { label: 'Check', value: row.methods.check },
    {
      label: 'Tips',
      value: row.methods.tips,
      note: 'Tips are not yet captured by the POS sync',
    },
  ];

  return (
    <div className="rounded-lg border border-border bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
        <h3 className="text-sm font-semibold">Station Name : {row.stationName}</h3>
        <span className="text-sm font-semibold">Net Total: {formatCurrency(row.netTotal)}</span>
      </div>
      <div className="grid grid-cols-1 gap-x-8 gap-y-1 px-6 py-4 md:grid-cols-2">
        <div className="space-y-1">
          {leftRows.map((r) => (
            <div
              key={r.label}
              className="flex items-center justify-between border-b border-border/60 py-2 last:border-0"
            >
              <span className="text-sm text-muted-foreground">{r.label}</span>
              <span className="text-sm font-semibold font-mono">{formatCurrency(r.value)}</span>
            </div>
          ))}
        </div>
        <div className="space-y-1">
          {rightRows.map((r) => (
            <div
              key={r.label}
              className="flex items-center justify-between border-b border-border/60 py-2 last:border-0"
            >
              <span className="text-sm text-muted-foreground" title={r.note}>
                {r.label}
              </span>
              <span className="text-sm font-semibold font-mono">{formatCurrency(r.value)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PosPaymentsPerStationTab() {
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();
  const initialRange = initialDailyRange(reportTz);
  const [dateFrom, setDateFrom] = useState(initialRange.dateFrom);
  const [dateTo, setDateTo] = useState(initialRange.dateTo);
  const { selectedLocationId } = useLocationStore();

  const { data, isLoading } = usePosPaymentsPerStation({
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    locationId: selectedLocationId || undefined,
  });

  const stations = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFrom}
            dateToInput={dateTo}
            onFromChange={setDateFrom}
            onToChange={setDateTo}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-border bg-white p-12 text-center text-sm text-muted-foreground">
          Loading...
        </div>
      ) : stations.length === 0 ? (
        <div className="rounded-lg border border-border bg-white">
          <EmptyState icon={CreditCard} title="No payments recorded" description="Try a different date range." />
        </div>
      ) : (
        <div className="space-y-4">
          {stations.map((row) => (
            <PosPaymentsPerStationRowCard key={row.registerId ?? 'unassigned'} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

type PosReportCategory =
  | 'most-popular'
  | 'sales'
  | 'inventory'
  | 'customers'
  | 'products'
  | 'employees'
  | 'orders'
  | 'promotions'
  | 'gift-cards'
  | 'refunds'
  | 'tills';

export type PosReportCard = {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  isNew?: boolean;
  /**
   * Hard role floor for reports carrying sensitive data (e.g. vendor cost
   * prices). Omitted = every role may see the card, subject to the per-location
   * visibility matrix. super_admin always passes. Mirrored server-side by
   * `requireReportRole` — this only hides the card, the API is the real gate.
   */
  roles?: readonly string[];
  /** Shown only when the location's Reconciliation module is on (see PosReport). */
  requiresReconciliation?: boolean;
};

const POS_REPORT_CATEGORIES: { id: PosReportCategory; label: string }[] = [
  { id: 'sales', label: 'Sales' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'customers', label: 'Customers' },
  { id: 'products', label: 'Products' },
  { id: 'employees', label: 'Employees' },
  { id: 'orders', label: 'Orders' },
  { id: 'promotions', label: 'Promotions' },
  { id: 'refunds', label: 'Refunds' },
  { id: 'tills', label: 'Tills' },
];

/** One-shot read of the `report` deep-link param, used only to seed the
 *  category so the correct tab is active on first paint. */
function readInitialReportId(): string | null {
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  const q = hash.indexOf('?');
  if (q < 0) return null;
  return new URLSearchParams(hash.slice(q + 1)).get('report');
}

const POS_REPORT_CARDS: Record<PosReportCategory, PosReportCard[]> = {
  'most-popular': [],
  sales: [
    {
      id: 'sales-trends',
      title: 'Sales Trends',
      description: 'How sales moved across a period, by hour, day, week or month, against the period before.',
      icon: TrendingUp,
      isNew: true,
    },
    {
      id: 'total-daily-sales',
      title: 'Total Daily Sales',
      description: 'View the total amount of money your store made each day.',
      icon: BarChart3,
    },
    {
      id: 'total-daily-department-sales',
      title: 'Total Daily Department Sales',
      description: 'See how much each product Department (like dairy or snacks) sold daily.',
      icon: FolderOpen,
    },
    {
      id: 'total-daily-category-sales',
      title: 'Total Daily Categories Sale',
      description: 'See how much each product category (like dairy or snacks) sold daily.',
      icon: Tag,
    },
    {
      id: 'daily-sales-by-payment-and-station',
      title: 'Daily Sales by Payment Type & POS Station',
      description: 'Daily sales breakdown by payment type and POS station.',
      icon: CreditCard,
    },
    {
      id: 'tax-report',
      title: 'Tax Report',
      description: 'Track how much tax was collected on all sales for accurate filing.',
      icon: FileText,
    },
    {
      id: 'sales-summary-current-location',
      title: 'Sales Summary for Current Location',
      description: 'View total sales from a specific store to compare performance.',
      icon: Store,
    },
    {
      id: 'top-items-by-sales-volume',
      title: 'Top Items by Sales & Volume',
      description: 'Discover which products made you the most revenue.',
      icon: Star,
    },
    {
      id: 'sales-by-vendor',
      title: 'Sales By Vendor',
      description: 'Track detailed product revenue and stock levels for each vendor.',
      icon: User,
    },
  ],
  inventory: [
    {
      id: 'inventory-movement-profitability',
      title: 'Inventory Movement & Profitability',
      description: 'See which items are selling, being restocked, and making you profit.',
      icon: TrendingUp,
    },
    {
      id: 'daily-item-movements',
      title: 'Daily Item Movements',
      description: 'What sold and what came back on one day, item by item, with discounts, net sales and cost.',
      icon: Package,
    },
    {
      id: 'inventory-valuation-summary',
      title: 'Inventory Valuation & Summary',
      description: 'Get a snapshot of all items currently in stock.',
      icon: FileText,
    },
    {
      id: 'inventory-trail',
      title: 'Inventory Trail',
      description: 'Track how products are moving in and out of your store.',
      icon: Activity,
    },
    {
      id: 'shrinkage',
      title: 'Shrinkage',
      description: 'Find out which items were lost, expired, or damaged.',
      icon: PackageMinus,
    },
    {
      id: 'price-updates',
      title: 'Price Updates',
      description: 'View changes made to product prices recently.',
      icon: DollarSign,
    },
  ],
  customers: [
    {
      id: 'credit-customers',
      title: 'Credit Customers',
      description: 'Customers on a credit or wholesale account, with loyalty points, visits and total spend.',
      icon: CreditCard,
      isNew: true,
    },
    {
      id: 'retail-customers',
      title: 'Retail Customers',
      description: 'Customers without a credit account, with loyalty points, visits and total spend.',
      icon: UserCircle,
      isNew: true,
    },
    {
      id: 'credit-accounts',
      title: 'Credit Accounts',
      description: 'Credit and wholesale customers with outstanding balances or recorded credit charges.',
      icon: CreditCard,
    },
    {
      id: 'coupon-usage',
      title: 'Coupon/Rewards Usage',
      description: 'Pick a coupon or reward to see which customers redeemed it and on which orders.',
      icon: Gift,
      isNew: true,
    },
    {
      id: 'customer-purchase-history',
      title: 'Customer Purchase History',
      description: 'Search a customer by name, email or phone to see their orders, savings and order details.',
      icon: History,
      isNew: true,
    },
  ],
  products: [
    {
      id: 'customers-by-product',
      title: 'Customers by Product',
      description: 'See which customers purchased each product, with quantities and total spend.',
      icon: Users,
      isNew: true,
    },
    {
      id: 'vendor-by-product',
      title: 'Vendor by Product',
      description: 'Search a product to see every vendor supplying it, with SKU code, cost price and last update.',
      icon: Truck,
      isNew: true,
      roles: ['owner', 'admin', 'manager'],
    },
    {
      id: 'price-history',
      title: 'Price History',
      description: 'Search a product to see its full cost and selling price change history.',
      icon: History,
      isNew: true,
    },
  ],
  employees: [
    {
      id: 'employee-work-time',
      title: "Employee's Work Time",
      description: 'Check how many hours each employee has worked — great for payroll.',
      icon: Clock,
    },
    {
      id: 'sales-by-cashier',
      title: 'Sales by Cashier',
      description: 'View how much each cashier has sold — helps identify your top performers.',
      icon: User,
    },
  ],
  orders: [
    {
      id: 'all-orders',
      title: 'All Orders',
      description: 'See a full list of every order placed in your store.',
      icon: ClipboardList,
    },
    {
      id: 'pos-card-attempts',
      title: 'Card Attempts to Check',
      description: 'Card payments the terminal could not confirm — including duplicate charges to refund.',
      icon: CreditCard,
      isNew: true,
    },
    {
      id: 'pos-price-changes',
      title: 'Price Changes',
      description: 'Line-price overrides and product price updates made on the POS, and who made them.',
      icon: DollarSign,
      isNew: true,
    },
    {
      id: 'discount-report',
      title: 'Discount Report',
      description: 'Monitor all discounts applied — by item, order, or cashier.',
      icon: Percent,
    },
    {
      id: 'digital-transactions-report',
      title: 'Electronic Statement Report',
      description: 'Track digital payments and refunds processed through your terminals.',
      icon: CreditCard,
    },
    {
      id: 'extra-item',
      title: 'MISC Item',
      description: 'Log any manually added items during checkout.',
      icon: Pencil,
      isNew: true,
    },
    {
      id: 'variable-item',
      title: 'Variable Item',
      description: 'Track sales of items flagged Variable Price.',
      icon: Scale,
      isNew: true,
    },
  ],
  promotions: [
    {
      id: 'promotion-usage',
      title: 'Promotion Usage',
      description:
        'See how often each promotion is firing, how many orders use it, and how much discount it has given away.',
      icon: Sparkles,
      isNew: true,
    },
  ],
  'gift-cards': [],
  refunds: [
    {
      id: 'refunds',
      title: 'Refunds',
      description: 'Track all refunds across your store, with or without associated customer orders.',
      icon: ReceiptText,
    },
    {
      id: 'store-credit',
      title: 'Store Credit',
      description: 'Track refunds issued as store credit instead of cash.',
      icon: CreditCard,
    },
    {
      id: 'bottle-deposit-refund',
      title: 'Bottle Deposit Refund',
      description: 'Record refunds given for returned bottles or containers.',
      icon: Milk,
    },
  ],
  tills: [
    {
      id: 'tills-report',
      title: 'Tills',
      description: 'Review cash-drawer sessions per POS station — starting/closing amounts, bank drops, and variance.',
      icon: Wallet,
      isNew: true,
    },
    {
      id: 'cash-in',
      title: 'Cash In',
      description: 'Record of times when extra cash was added to the till, by station and cashier.',
      icon: ArrowDown,
      isNew: true,
    },
    {
      id: 'cash-out',
      title: 'Cash Out',
      description: 'Track when cash was taken out of the till (e.g. for supplier payments).',
      icon: ArrowUp,
      isNew: true,
    },
    {
      id: 'cash-records',
      title: 'Reconciled Cash Records',
      description: 'Daily cash counts per POS station — system-reported cash, the amount counted, and the difference.',
      icon: Banknote,
      isNew: true,
      requiresReconciliation: true,
    },
  ],
};

function formatCurrencyPlain(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDateLong(iso: string): string {
  // iso is a YYYY-MM-DD string from the API; render as "11 May, 2026"
  const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return iso;
  const date = new Date(Date.UTC(y, m - 1, d));
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  return `${day} ${month}, ${date.getUTCFullYear()}`;
}

/**
 * Fetch every page of a paginated report endpoint, for CSV export.
 *
 * Report tables render one page at a time, so an export built from the rows
 * held in state silently truncates to that page — the user asks for a date
 * range and gets 25 rows of it. This re-runs the same query page by page and
 * returns the whole filtered result.
 *
 * Paging is driven by the server's `totalPages`, deliberately NOT by "this
 * page came back shorter than I asked for": the report routes clamp perPage
 * (200 on the hand-rolled ones, 1000 via parsePagination), so an over-large
 * batch comes back short on page one and that test would end the walk
 * immediately — reintroducing the very bug this exists to fix.
 *
 * `zonedDates` mirrors the report's own hook: most wrap their params in
 * withLocalDateRange, but the date-less ones (inventory valuation) must not,
 * or they would send a spurious range the table never applied.
 */
async function fetchAllReportPages<T>(
  path: string,
  params: Record<string, unknown>,
  opts: { tz?: string; zonedDates?: boolean } = {},
): Promise<T[]> {
  const { tz, zonedDates = true } = opts;
  const batch = 200;
  const all: T[] = [];
  for (let p = 1; ; p++) {
    const query = { ...params, page: p, perPage: batch };
    const res = await api.get<PaginatedResponse<T>>(
      path,
      (zonedDates ? withLocalDateRange(query as { dateFrom?: string; dateTo?: string }, tz) : query) as Record<
        string,
        string | number | undefined
      >,
    );
    all.push(...res.data);
    if (res.data.length === 0 || p >= (res.pagination?.totalPages ?? 1)) break;
  }
  return all;
}

/**
 * The standard date filter for every report page: one preset-first range picker
 * — Today / Yesterday / This Week / This Month / Last Month / Custom Range … —
 * in place of separate Start Date and End Date fields, matching the Dashboard.
 *
 * This is a thin wrapper over the shared SupernovaDatePicker (the same component
 * the Dashboard renders), adding only the bridge between the Date instants the
 * picker exchanges and the wall-clock `YYYY-MM-DDTHH:mm` strings the reports
 * keep in state. Every report gets identical presets, formatting and popover
 * behaviour from one place instead of repeating the prop set 20 times.
 *
 * The picker's own Apply writes the draft range only; the page's Submit button
 * still runs the query, so *when* a report refetches is unchanged. Reports that
 * filter live (no Submit) pass `autoApply`.
 */
function ReportRangeDatePicker({
  reportTz,
  dateFromInput,
  dateToInput,
  onFromChange,
  onToChange,
  label = 'Date range',
  className = 'w-[300px] max-w-full',
  autoApply = false,
  clearable = false,
}: {
  reportTz: string;
  dateFromInput: string;
  dateToInput: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  label?: string;
  className?: string;
  autoApply?: boolean;
  clearable?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      <SupernovaDatePicker
        mode="range"
        presetFirst
        optionalTime
        portal
        // Compact prose ("16 Aug - 2 Sep 2026") so the trigger never clips the
        // range; ticking "Set Specific Times" restores the full format.
        compactDisplay
        autoApply={autoApply}
        timezone={reportTz}
        clearable={clearable}
        className={className}
        value={{
          start: dailyInputToDate(dateFromInput, reportTz),
          end: dailyInputToDate(dateToInput, reportTz),
        }}
        onApply={(v) => {
          const r = (v as DateRange | null) ?? { start: null, end: null };
          onFromChange(dateToDailyInput(r.start, reportTz));
          onToChange(dateToDailyInput(r.end, reportTz));
        }}
      />
    </div>
  );
}

function TotalsBreakdownRow({ label, value, marker }: { label: string; value: string; marker?: string }) {
  return (
    <div className="flex items-center justify-between border-b border-border/60 px-4 py-2.5 text-sm last:border-b-0">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <span>{label}</span>
        {marker && <span className="text-xs text-muted-foreground/70">({marker})</span>}
      </div>
      <span className="font-mono text-foreground">{value}</span>
    </div>
  );
}

function PosTotalDailySalesReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const locationLabel = selectedLocationName || 'All locations';

  const initialRange = initialDailyRange();
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string }>(initialRange);
  const [showDetails, setShowDetails] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  // Reporting timezone via the shared, reactive hook — the picker's display, the
  // query boundary math (withLocalDateRange), and the label below all resolve to
  // this same zone, so nothing ever renders in raw UTC.
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const { data, isLoading } = usePosTotalDailySales({
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    locationId: selectedLocationId || undefined,
  });

  const report = data?.data;
  const totals = report?.totals;
  const daily = report?.daily ?? [];

  const totalRows = daily.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
  const start = (page - 1) * perPage;
  const visible = daily.slice(start, start + perPage);

  const handleApply = () => {
    setApplied({ dateFrom: dateFromInput, dateTo: dateToInput });
    setPage(1);
  };

  const handleReset = () => {
    const r = defaultDailyRangeWithTime();
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setApplied(r);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <PageHeader
        title="Total Daily Sales"
        description="View the total amount of money your store made each day."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button
            variant="primary"
            size="sm"
            disabled={daily.length === 0}
            onClick={() => {
              const header = [
                'Location',
                'Date',
                'No. of Orders',
                'Sales ($)',
                'Tax ($)',
                'Fees ($)',
                'Discounts ($)',
                'Tips ($)',
                'Gift Cards Sold ($)',
                'Payments ($)',
                'Refunded Amount ($)',
              ];
              const body = daily.map((r) => [
                locationLabel,
                formatDateLong(r.date),
                r.ordersCount,
                r.sales.toFixed(2),
                r.tax.toFixed(2),
                r.fees.toFixed(2),
                r.discounts.toFixed(2),
                r.tips.toFixed(2),
                r.giftCards.toFixed(2),
                r.payments.toFixed(2),
                r.refundedAmount.toFixed(2),
              ]);
              downloadCsv(reportFilename('total_daily_sales_report', selectedLocationName), [header, ...body]);
            }}
          >
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware) */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {/* Top stats */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <div className="text-xs text-muted-foreground">
              Total Billed Amount <span className="text-[10px]">(A+B+C+D-E-F+G)</span>
            </div>
            <div className="mt-1 text-2xl font-semibold text-foreground">
              ${totals ? formatCurrencyPlain(totals.totalBilledAmount) : '0.00'}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Total No. of Transactions</div>
            <div className="mt-1 text-2xl font-semibold text-foreground">
              {totals ? totals.totalTransactions.toLocaleString() : '0'}
            </div>
          </div>
        </div>

        {showDetails && (
          <div className="mt-4 grid grid-cols-1 gap-x-8 lg:grid-cols-2">
            <div>
              <TotalsBreakdownRow
                label="Total Sales"
                marker="A"
                value={`$${formatCurrencyPlain(totals?.totalSales ?? 0)}`}
              />
              <TotalsBreakdownRow
                label="Total Tax"
                marker="B"
                value={`$${formatCurrencyPlain(totals?.totalTax ?? 0)}`}
              />
              <TotalsBreakdownRow
                label="Total Fees"
                marker="C"
                value={`$${formatCurrencyPlain(totals?.totalFees ?? 0)}`}
              />
              <TotalsBreakdownRow
                label="Total Tip"
                marker="D"
                value={`$${formatCurrencyPlain(totals?.totalTip ?? 0)}`}
              />
              <TotalsBreakdownRow
                label="Total Discount"
                marker="E"
                value={`$${formatCurrencyPlain(totals?.totalDiscount ?? 0)}`}
              />
              <TotalsBreakdownRow
                label="Total Cash Discount"
                marker="F"
                value={`$${formatCurrencyPlain(totals?.totalCashDiscount ?? 0)}`}
              />
              {/* Gift cards sold are billed and paid for, but are not sales (not in A). */}
              <TotalsBreakdownRow
                label="Total Gift Cards Sold"
                marker="G"
                value={`$${formatCurrencyPlain(totals?.totalGiftCards ?? 0)}`}
              />
            </div>
            <div>
              <TotalsBreakdownRow
                label="Total Refunded Amount With Order"
                value={`$${formatCurrencyPlain(totals?.totalRefundedAmountWithOrder ?? 0)}`}
              />
              <TotalsBreakdownRow
                label="Total Refunded Tax With Order"
                value={`$${formatCurrencyPlain(totals?.totalRefundedTaxWithOrder ?? 0)}`}
              />
              <TotalsBreakdownRow
                label="Total Refunded Fees With Order"
                value={`$${formatCurrencyPlain(totals?.totalRefundedFeesWithOrder ?? 0)}`}
              />
              <TotalsBreakdownRow
                label="Total Refunded Tips"
                value={`$${formatCurrencyPlain(totals?.totalRefundedTips ?? 0)}`}
              />
              <TotalsBreakdownRow
                label="Total Refunded Amount Without Order"
                value={`$${formatCurrencyPlain(totals?.totalRefundedAmountWithoutOrder ?? 0)}`}
              />
              <TotalsBreakdownRow
                label="Total Refunded Tax Without Order"
                value={`$${formatCurrencyPlain(totals?.totalRefundedTaxWithoutOrder ?? 0)}`}
              />
              <TotalsBreakdownRow
                label="Total Refunded Fees Without Order"
                value={`$${formatCurrencyPlain(totals?.totalRefundedFeesWithoutOrder ?? 0)}`}
              />
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          className="mt-3 flex w-full items-center justify-center gap-1 rounded-md bg-primary/5 py-2 text-sm font-medium text-primary hover:bg-primary/10"
        >
          {showDetails ? 'Hide Details' : 'Show Details'}
          {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {/* Daily table */}
      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">No. of Orders</th>
                <th className="px-4 py-3 font-medium">Sales ($)</th>
                <th className="px-4 py-3 font-medium">Tax ($)</th>
                <th className="px-4 py-3 font-medium">Fees ($)</th>
                <th className="px-4 py-3 font-medium">Discounts ($)</th>
                <th className="px-4 py-3 font-medium">Tips ($)</th>
                <th className="px-4 py-3 font-medium">Gift Cards Sold ($)</th>
                <th className="px-4 py-3 font-medium">Payments ($)</th>
                <th className="px-4 py-3 font-medium">Refunded Amount ($)</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={10}>
                    <EmptyState icon={Receipt} title="No sales" description="Try a different date range." />
                  </td>
                </tr>
              ) : (
                visible.map((row: PosTotalDailySalesRow) => (
                  <tr key={row.date} className="border-b border-border/60 last:border-b-0">
                    <td className="px-4 py-3">{formatDateLong(row.date)}</td>
                    <td className="px-4 py-3">{row.ordersCount}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.sales)}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.tax)}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.fees)}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.discounts)}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.tips)}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.giftCards)}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.payments)}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.refundedAmount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalRows > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={page}
              totalPages={totalPages}
              total={totalRows}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Filter value for the "No department" / "Uncategorised" row (matches the API). */
const NO_GROUP_ID = '__none__';

function PosTotalDailyDepartmentSalesReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const locationLabel = selectedLocationName || 'All locations';
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();
  const { data: deptsData } = usePosReportDepartments();
  const allDepartments = deptsData?.data ?? [];
  const deptOptions = [
    ...allDepartments.map((d) => ({ value: d.id, label: d.name })),
    { value: NO_GROUP_ID, label: 'No department' },
  ];

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [selectedDeptIds, setSelectedDeptIds] = useState<string[]>([]);
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string; departmentIds: string[] }>({
    ...initialRange,
    departmentIds: [],
  });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  // Auto-select all departments on first load.
  const [hasInitDepts, setHasInitDepts] = useState(false);
  if (!hasInitDepts && allDepartments.length > 0) {
    const allIds = deptOptions.map((o) => o.value);
    setSelectedDeptIds(allIds);
    setApplied((prev) => ({ ...prev, departmentIds: allIds }));
    setHasInitDepts(true);
  }

  const { data, isLoading } = usePosTotalDailyDepartmentSales({
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    locationId: selectedLocationId || undefined,
    departmentIds: applied.departmentIds.length > 0 ? applied.departmentIds.join(',') : undefined,
  });

  const rows: PosDepartmentSalesRow[] = data?.data ?? [];
  const totals = data?.totals;
  const totalRows = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
  const start = (page - 1) * perPage;
  const visible = rows.slice(start, start + perPage);

  const handleApply = () => {
    setApplied({
      dateFrom: dateFromInput,
      dateTo: dateToInput,
      departmentIds: selectedDeptIds,
    });
    setPage(1);
  };

  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    const allIds = deptOptions.map((o) => o.value);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setSelectedDeptIds(allIds);
    setApplied({ ...r, departmentIds: allIds });
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Total Daily Department Sales"
        description="See how much each product Department (like dairy or snacks) sold daily."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button
            variant="primary"
            size="sm"
            disabled={rows.length === 0}
            onClick={() => {
              const header = [
                'Location',
                'Department Name',
                'Quantity Sold',
                'Free Quantity',
                'Net Quantity Sold',
                'Gross Sale ($)',
                'Percentage (%)',
                'Cost of Sale ($)',
              ];
              const body = rows.map((r) => [
                locationLabel,
                r.departmentName,
                r.quantitySold.toFixed(2),
                r.freeQuantity.toFixed(2),
                r.netQuantitySold.toFixed(2),
                r.grossSale.toFixed(2),
                r.percentage.toFixed(2),
                r.costOfSale.toFixed(2),
              ]);
              downloadCsv(reportFilename('total_daily_department_sales_report', selectedLocationName), [
                header,
                ...body,
              ]);
            }}
          >
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware) */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <div className="min-w-[200px]">
            <MultiSelect
              options={deptOptions}
              selected={selectedDeptIds}
              onChange={setSelectedDeptIds}
              triggerLabel="Departments"
              searchPlaceholder="Find something..."
              emptyMessage="No departments"
            />
          </div>
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Department Name</th>
                <th className="px-4 py-3 font-medium">Quantity Sold</th>
                <th className="px-4 py-3 font-medium">Free Quantity</th>
                <th className="px-4 py-3 font-medium">Net Quantity Sold</th>
                <th className="px-4 py-3 font-medium">Gross Sale ($)</th>
                <th className="px-4 py-3 font-medium">Percentage (%)</th>
                <th className="px-4 py-3 font-medium">Cost of Sale ($)</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      icon={FolderOpen}
                      title="No department sales"
                      description="Try a different date range."
                    />
                  </td>
                </tr>
              ) : (
                visible.map((row) => (
                  <tr
                    key={row.departmentId ?? row.departmentName}
                    className="border-b border-border/60 last:border-b-0"
                  >
                    <td className="px-4 py-3">{row.departmentName}</td>
                    <td className="px-4 py-3 font-mono">{row.quantitySold.toFixed(2)}</td>
                    <td className="px-4 py-3 font-mono">{row.freeQuantity.toFixed(2)}</td>
                    <td className="px-4 py-3 font-mono">{row.netQuantitySold.toFixed(2)}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.grossSale)}</td>
                    <td className="px-4 py-3 font-mono">{row.percentage.toFixed(2)}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.costOfSale)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {totals && rows.length > 0 && (
              <tfoot>
                <tr className="border-t border-border bg-muted/30 font-semibold">
                  <td className="px-4 py-3">Total sales</td>
                  <td className="px-4 py-3 font-mono">{totals.quantitySold.toFixed(2)}</td>
                  <td className="px-4 py-3 font-mono">0.00</td>
                  <td className="px-4 py-3 font-mono">{totals.quantitySold.toFixed(2)}</td>
                  <td className="px-4 py-3 font-mono">{formatCurrencyPlain(totals.grossSale)}</td>
                  <td className="px-4 py-3 font-mono">100.00</td>
                  <td className="px-4 py-3 font-mono">{formatCurrencyPlain(totals.costOfSale)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {rows.length > 0 && (
          <div className="space-y-1 border-t border-border px-4 py-3 text-xs text-muted-foreground">
            {totals && totals.quantityWithoutCost > 0 && (
              <p>
                Cost of Sale uses each product&apos;s current cost at the sale&apos;s location.{' '}
                {totals.quantityWithoutCost.toFixed(2)} unit{totals.quantityWithoutCost === 1 ? '' : 's'} sold have no
                cost set and count as $0.
              </p>
            )}
          </div>
        )}
        {totalRows > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={page}
              totalPages={totalPages}
              total={totalRows}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function TaxSummaryCell({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-semibold text-foreground">${formatCurrencyPlain(value)}</div>
    </div>
  );
}

function TaxBreakdownRow({ label, marker, value }: { label: string; marker: string; value: number }) {
  return (
    <div className="flex items-center justify-between py-2 text-sm">
      <div className="text-muted-foreground">
        {label} <span className="text-xs">({marker})</span>
      </div>
      <span className="font-mono text-foreground">${formatCurrencyPlain(value)}</span>
    </div>
  );
}

function PosTaxReportScreen({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const locationLabel = selectedLocationName || 'All locations';

  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();
  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string }>(initialRange);
  const [showDetails, setShowDetails] = useState(true);
  const [subTab, setSubTab] = useState<'tax' | 'fee'>('tax');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const { data, isLoading } = usePosTaxReport({
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    locationId: selectedLocationId || undefined,
  });

  const report = data?.data;
  const sales = report?.sales;
  const b = report?.breakdown;
  const taxRows: PosTaxDetailRow[] = report?.taxDetails ?? [];
  const feeRows: PosFeeDetailRow[] = report?.feeDetails ?? [];
  const activeRows = subTab === 'tax' ? taxRows : feeRows;
  const totalRows = activeRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
  const start = (page - 1) * perPage;
  const visibleTax = subTab === 'tax' ? (taxRows.slice(start, start + perPage) as PosTaxDetailRow[]) : [];
  const visibleFee = subTab === 'fee' ? (feeRows.slice(start, start + perPage) as PosFeeDetailRow[]) : [];

  const handleApply = () => {
    setApplied({ dateFrom: dateFromInput, dateTo: dateToInput });
    setPage(1);
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setApplied(r);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Tax Report"
        description="Track how much tax was collected on all sales for accurate filing."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button
            variant="primary"
            size="sm"
            disabled={!report}
            onClick={() => {
              if (!report || !b || !sales) return;
              const rows: (string | number)[][] = [
                ['Tax Report', `${applied.dateFrom} → ${applied.dateTo}`],
                ['Location', locationLabel],
                [],
                ['Sales Summary'],
                ['Taxable Sales', sales.taxableSales.toFixed(2)],
                ['Non-Taxable Sales', sales.nonTaxableSales.toFixed(2)],
                ['Net Sales', sales.netSales.toFixed(2)],
                [],
                ['Sales & Refund Details'],
                ['Total Payment Collected (C = A + B)', b.totalPaymentCollected.toFixed(2)],
                ['Net Tax Collected (L)', b.netTaxCollected.toFixed(2)],
                ['Net Fee Collected (P)', b.netFeeCollected.toFixed(2)],
                ['Payment Collected (F = C - D - E)', b.paymentCollected.toFixed(2)],
                ['Net Payment Collected (Q = F - J - K - N - O)', b.netPaymentCollected.toFixed(2)],
                ['Payment Amount Collected (A)', b.paymentAmountCollected.toFixed(2)],
                ['Tip Amount Collected (B)', b.tipAmountCollected.toFixed(2)],
                ['Payment Refunded With Order (D)', b.paymentRefundedWithOrder.toFixed(2)],
                ['Payment Refunded Without Order (E)', b.paymentRefundedWithoutOrder.toFixed(2)],
                ['Unpaid Amount (Credit Account) (G)', b.unpaidAmount.toFixed(2)],
                ['Tax Amount Collected (I)', b.taxAmountCollected.toFixed(2)],
                ['Tax Refunded With Order (J)', b.taxRefundedWithOrder.toFixed(2)],
                ['Tax Refunded Without Order (K)', b.taxRefundedWithoutOrder.toFixed(2)],
                ['Fees Amount Collected (M)', b.feesAmountCollected.toFixed(2)],
                ['Fees Refunded With Order (N)', b.feesRefundedWithOrder.toFixed(2)],
                ['Fees Refunded Without Order (O)', b.feesRefundedWithoutOrder.toFixed(2)],
                [],
                ['Tax Details'],
                ['Tax ID', 'Tax Name', 'Tax Eligible Amount ($)', 'Tax Collected ($)'],
                ...taxRows.map((r) => [r.taxId, r.taxName, r.taxEligibleAmount.toFixed(2), r.taxCollected.toFixed(2)]),
                [],
                ['Fee Details'],
                ['Fee ID', 'Fee Name', 'Fee Eligible Amount ($)', 'Fee Collected ($)'],
                ...feeRows.map((r) => [r.feeId, r.feeName, r.feeEligibleAmount.toFixed(2), r.feeCollected.toFixed(2)]),
              ];
              downloadCsv(reportFilename('tax_report', selectedLocationName), rows);
            }}
          >
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        </PosExportOnly>
      </PageHeader>

      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {isLoading || !report || !sales || !b ? (
        <div className="flex h-32 items-center justify-center rounded-xl border border-border bg-white text-muted-foreground">
          Loading…
        </div>
      ) : (
        <>
          {/* Sales Summary */}
          <div className="overflow-hidden rounded-xl border border-border bg-white">
            <div className="bg-muted/50 px-5 py-2.5 text-sm font-semibold text-foreground">Sales Summary</div>
            <div className="grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-3">
              <TaxSummaryCell label="Taxable Sales" value={sales.taxableSales} />
              <TaxSummaryCell label="Non-Taxable Sales" value={sales.nonTaxableSales} />
              <TaxSummaryCell label="Net Sales" value={sales.netSales} />
            </div>
          </div>

          {/* Sales & Refund Details */}
          <div className="overflow-hidden rounded-xl border border-border bg-white">
            <div className="bg-muted/50 px-5 py-2.5 text-sm font-semibold text-foreground">
              Sales &amp; Refund Details
            </div>
            <div className="grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-3">
              <div>
                <div className="text-xs font-medium text-muted-foreground">
                  Total Payment Collected <span className="text-[10px]">(C = A + B)</span>
                </div>
                <div className="mt-1 text-xl font-semibold text-foreground">
                  ${formatCurrencyPlain(b.totalPaymentCollected)}{' '}
                  <span className="text-xs font-normal text-muted-foreground">(Includes Tax + Fee)</span>
                </div>
              </div>
              <TaxSummaryCell label="Net Tax Collected (L)" value={b.netTaxCollected} />
              <TaxSummaryCell label="Net Fee Collected (P)" value={b.netFeeCollected} />
              <TaxSummaryCell label="Payment Collected (F = C - D - E)" value={b.paymentCollected} />
              <TaxSummaryCell label="Net Payment Collected (Q = F - J - K - N - O)" value={b.netPaymentCollected} />
            </div>

            {showDetails && (
              <div className="grid grid-cols-1 gap-x-8 border-t border-border px-5 py-3 lg:grid-cols-2">
                <div className="divide-y divide-border/60">
                  <TaxBreakdownRow label="Payment Amount Collected" marker="A" value={b.paymentAmountCollected} />
                  <TaxBreakdownRow label="Tip Amount Collected" marker="B" value={b.tipAmountCollected} />
                  <TaxBreakdownRow label="Payment Refunded With Order" marker="D" value={b.paymentRefundedWithOrder} />
                  <TaxBreakdownRow
                    label="Payment Refunded Without Order"
                    marker="E"
                    value={b.paymentRefundedWithoutOrder}
                  />
                  <TaxBreakdownRow label="Unpaid Amount (Credit Account)" marker="G" value={b.unpaidAmount} />
                </div>
                <div className="divide-y divide-border/60">
                  <TaxBreakdownRow label="Tax Amount Collected" marker="I" value={b.taxAmountCollected} />
                  <TaxBreakdownRow label="Tax Refunded With Order" marker="J" value={b.taxRefundedWithOrder} />
                  <TaxBreakdownRow label="Tax Refunded Without Order" marker="K" value={b.taxRefundedWithoutOrder} />
                  <TaxBreakdownRow label="Fees Amount Collected" marker="M" value={b.feesAmountCollected} />
                  <TaxBreakdownRow label="Fees Refunded With Order" marker="N" value={b.feesRefundedWithOrder} />
                  <TaxBreakdownRow label="Fees Refunded Without Order" marker="O" value={b.feesRefundedWithoutOrder} />
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              className="flex w-full items-center justify-center gap-1 border-t border-border bg-primary/5 py-2 text-sm font-medium text-primary hover:bg-primary/10"
            >
              {showDetails ? 'Hide Details' : 'Show Details'}
              {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>

          {/* Tax / Fee Details sub-tabs */}
          <div className="overflow-hidden rounded-xl border border-border bg-white">
            <div className="flex border-b border-border" role="tablist" aria-label="Tax / Fee sub-tabs">
              {(['tax', 'fee'] as const).map((id) => {
                const isSelected = subTab === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    onClick={() => {
                      setSubTab(id);
                      setPage(1);
                    }}
                    className={cn(
                      'relative px-4 py-2.5 text-sm font-medium',
                      isSelected ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {id === 'tax' ? 'Tax Details' : 'Fee Details'}
                    {isSelected && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
                  </button>
                );
              })}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                    {subTab === 'tax' ? (
                      <>
                        <th className="px-4 py-3 font-medium">Tax ID</th>
                        <th className="px-4 py-3 font-medium">Tax Name</th>
                        <th className="px-4 py-3 font-medium">Tax Eligible Amount ($)</th>
                        <th className="px-4 py-3 font-medium">Tax Collected ($)</th>
                      </>
                    ) : (
                      <>
                        <th className="px-4 py-3 font-medium">Fee ID</th>
                        <th className="px-4 py-3 font-medium">Fee Name</th>
                        <th className="px-4 py-3 font-medium">Fee Eligible Amount ($)</th>
                        <th className="px-4 py-3 font-medium">Fee Collected ($)</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {totalRows === 0 ? (
                    <tr>
                      <td colSpan={4}>
                        <EmptyState
                          icon={subTab === 'tax' ? Percent : DollarSign}
                          title={subTab === 'tax' ? 'No tax collected' : 'No fees collected'}
                          description="Try a different date range."
                        />
                      </td>
                    </tr>
                  ) : subTab === 'tax' ? (
                    visibleTax.map((row) => (
                      <tr key={row.taxId} className="border-b border-border/60 last:border-b-0">
                        <td className="px-4 py-3">{row.taxId}</td>
                        <td className="px-4 py-3">{row.taxName}</td>
                        <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.taxEligibleAmount)}</td>
                        <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.taxCollected)}</td>
                      </tr>
                    ))
                  ) : (
                    visibleFee.map((row) => (
                      <tr key={row.feeId} className="border-b border-border/60 last:border-b-0">
                        <td className="px-4 py-3">{row.feeId}</td>
                        <td className="px-4 py-3">{row.feeName}</td>
                        <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.feeEligibleAmount)}</td>
                        <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.feeCollected)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalRows > 0 && (
              <div className="border-t border-border">
                <DataTablePagination
                  page={page}
                  totalPages={totalPages}
                  total={totalRows}
                  perPage={perPage}
                  onPageChange={setPage}
                  perPageOptions={[10, 25, 50, 100]}
                  onPerPageChange={(n) => {
                    setPerPage(n);
                    setPage(1);
                  }}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function PosTotalDailyCategorySalesReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const locationLabel = selectedLocationName || 'All locations';
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();
  const { data: categoriesData } = usePosReportCategories();
  const allCategories = categoriesData?.data ?? [];
  const categoryOptions = [
    ...allCategories.map((c) => ({ value: c.id, label: c.name })),
    { value: NO_GROUP_ID, label: 'Uncategorised' },
  ];

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string; categoryIds: string[] }>({
    ...initialRange,
    categoryIds: [],
  });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [hasInitCategories, setHasInitCategories] = useState(false);
  if (!hasInitCategories && allCategories.length > 0) {
    const allIds = categoryOptions.map((o) => o.value);
    setSelectedCategoryIds(allIds);
    setApplied((prev) => ({ ...prev, categoryIds: allIds }));
    setHasInitCategories(true);
  }

  const { data, isLoading } = usePosTotalDailyCategorySales({
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    locationId: selectedLocationId || undefined,
    categoryIds: applied.categoryIds.length > 0 ? applied.categoryIds.join(',') : undefined,
  });

  const rows: PosCategorySalesRow[] = data?.data ?? [];
  const totals = data?.totals;
  const totalRows = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
  const start = (page - 1) * perPage;
  const visible = rows.slice(start, start + perPage);

  const handleApply = () => {
    setApplied({ dateFrom: dateFromInput, dateTo: dateToInput, categoryIds: selectedCategoryIds });
    setPage(1);
  };

  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    const allIds = categoryOptions.map((o) => o.value);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setSelectedCategoryIds(allIds);
    setApplied({ ...r, categoryIds: allIds });
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Total Daily Categories Sale"
        description="See how much each product category (like dairy or snacks) sold daily."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button
            variant="primary"
            size="sm"
            disabled={rows.length === 0}
            onClick={() => {
              const header = [
                'Location',
                'Category Name',
                'Quantity Sold',
                'Free Quantity',
                'Net Quantity Sold',
                'Gross Sale ($)',
                'Percentage (%)',
                'Cost of Sale ($)',
              ];
              const body = rows.map((r) => [
                locationLabel,
                r.categoryName,
                r.quantitySold.toFixed(2),
                r.freeQuantity.toFixed(2),
                r.netQuantitySold.toFixed(2),
                r.grossSale.toFixed(2),
                r.percentage.toFixed(2),
                r.costOfSale.toFixed(2),
              ]);
              downloadCsv(reportFilename('total_daily_category_sales_report', selectedLocationName), [header, ...body]);
            }}
          >
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware) */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <div className="min-w-[200px]">
            <MultiSelect
              options={categoryOptions}
              selected={selectedCategoryIds}
              onChange={setSelectedCategoryIds}
              triggerLabel="Categories"
              searchPlaceholder="Search categories..."
              emptyMessage="No categories"
            />
          </div>
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Category Name</th>
                <th className="px-4 py-3 font-medium">Quantity Sold</th>
                <th className="px-4 py-3 font-medium">Free Quantity</th>
                <th className="px-4 py-3 font-medium">Net Quantity Sold</th>
                <th className="px-4 py-3 font-medium">Gross Sale ($)</th>
                <th className="px-4 py-3 font-medium">Percentage (%)</th>
                <th className="px-4 py-3 font-medium">Cost of Sale ($)</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState icon={Tag} title="No category sales" description="Try a different date range." />
                  </td>
                </tr>
              ) : (
                visible.map((row) => (
                  <tr key={row.categoryId ?? row.categoryName} className="border-b border-border/60 last:border-b-0">
                    <td className="px-4 py-3">{row.categoryName}</td>
                    <td className="px-4 py-3 font-mono">{row.quantitySold.toFixed(2)}</td>
                    <td className="px-4 py-3 font-mono">{row.freeQuantity.toFixed(2)}</td>
                    <td className="px-4 py-3 font-mono">{row.netQuantitySold.toFixed(2)}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.grossSale)}</td>
                    <td className="px-4 py-3 font-mono">{row.percentage.toFixed(2)}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.costOfSale)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {totals && rows.length > 0 && (
              <tfoot>
                <tr className="border-t border-border bg-muted/30 font-semibold">
                  <td className="px-4 py-3">Total sales (each sale once)</td>
                  <td className="px-4 py-3 font-mono">{totals.quantitySold.toFixed(2)}</td>
                  <td className="px-4 py-3 font-mono">0.00</td>
                  <td className="px-4 py-3 font-mono">{totals.quantitySold.toFixed(2)}</td>
                  <td className="px-4 py-3 font-mono">{formatCurrencyPlain(totals.grossSale)}</td>
                  <td className="px-4 py-3 font-mono">100.00</td>
                  <td className="px-4 py-3 font-mono">{formatCurrencyPlain(totals.costOfSale)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {rows.length > 0 && (
          <div className="space-y-1 border-t border-border px-4 py-3 text-xs text-muted-foreground">
            <p>
              A product in several categories counts in each, so the category rows can add up to more than total sales.
              Percentage is each category's share of total sales.
            </p>
            {totals && totals.quantityWithoutCost > 0 && (
              <p>
                Cost of Sale uses each product&apos;s current cost at the sale&apos;s location.{' '}
                {totals.quantityWithoutCost.toFixed(2)} unit{totals.quantityWithoutCost === 1 ? '' : 's'} sold have no
                cost set and count as $0.
              </p>
            )}
          </div>
        )}
        {totalRows > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={page}
              totalPages={totalPages}
              total={totalRows}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Inventory Movement & Profitability ──────────────────────────────────────

const INV_SORT_OPTIONS: { value: PosInventorySortBy; label: string }[] = [
  { value: 'name', label: 'Product Name' },
  { value: 'primaryBarcode', label: 'Primary Barcode' },
  { value: 'inStockQty', label: 'In Stock Quantity' },
  { value: 'quantitySold', label: 'Quantity Sold' },
  { value: 'cashPrice', label: 'Selling Price ($)' },
  { value: 'cost', label: 'Cost ($)' },
  { value: 'totalRevenue', label: 'Total Revenue ($)' },
];

function PosInventoryMovementReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const locationLabel = selectedLocationName || 'All locations';
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const { data: deptsData } = usePosReportDepartments();
  const { data: categoriesData } = usePosReportCategories();
  const departmentOptions = (deptsData?.data ?? []).map((d) => ({ value: d.id, label: d.name }));
  const categoryOptions = (categoriesData?.data ?? []).map((c) => ({ value: c.id, label: c.name }));

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState<PosInventorySortBy>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  // Export walks every page of the filtered result, so it can take a moment —
  // disable the button while it runs rather than let a second click start a
  // duplicate download.
  const [exporting, setExporting] = useState(false);

  const [applied, setApplied] = useState<{
    dateFrom: string;
    dateTo: string;
    categoryIds: string[];
    departmentIds: string[];
    search: string;
  }>({
    ...initialRange,
    categoryIds: [],
    departmentIds: [],
    search: '',
  });

  // Debounce search so each keystroke doesn't fire a new request.
  useEffect(() => {
    const t = setTimeout(() => {
      setApplied((prev) => ({ ...prev, search: searchInput.trim() }));
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading } = usePosInventoryMovement({
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    locationId: selectedLocationId || undefined,
    categoryIds: idFilterParam(applied.categoryIds, categoryOptions),
    departmentIds: idFilterParam(applied.departmentIds, departmentOptions),
    search: applied.search || undefined,
    sortBy,
    sortDir,
    page,
    perPage,
  });

  const rows: PosInventoryMovementRow[] = data?.data ?? [];
  const pagination = data?.pagination;

  const handleApply = () => {
    setApplied((prev) => ({
      ...prev,
      dateFrom: dateFromInput,
      dateTo: dateToInput,
      categoryIds: selectedCategoryIds,
      departmentIds: selectedDepartmentIds,
    }));
    setPage(1);
  };

  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setSelectedCategoryIds([]);
    setSelectedDepartmentIds([]);
    setSearchInput('');
    setSortBy('name');
    setSortDir('asc');
    setApplied({ ...r, categoryIds: [], departmentIds: [], search: '' });
    setPage(1);
  };

  const handleExport = async () => {
    // Export the WHOLE filtered result, not just the page on screen. `rows` is
    // one page (25 by default), so mapping it exported only what was visible.
    // Re-query page by page with the same filters and concatenate.
    setExporting(true);
    try {
      const all = await fetchAllReportPages<PosInventoryMovementRow>(
        '/pos/report/inventory-movement-profitability',
        {
          locationId: selectedLocationId || undefined,
          dateFrom: applied.dateFrom || undefined,
          dateTo: applied.dateTo || undefined,
          categoryIds: idFilterParam(applied.categoryIds, categoryOptions),
          departmentIds: idFilterParam(applied.departmentIds, departmentOptions),
          search: applied.search || undefined,
          sortBy,
          sortDir,
        },
        { tz: reportTz },
      );

      const header = [
        'Location',
        'Product Name',
        'Primary Barcode',
        'Department',
        'Categories',
        'In Stock Quantity',
        'Cost ($)',
        'Selling Price ($)',
        'Quantity Sold',
        'Total Cost ($)',
        'Total Discount ($)',
        'Total Revenue ($)',
        'Profit Margin (%)',
      ];
      const body = all.map((r) => {
        const margin = r.totalRevenue > 0 ? ((r.totalRevenue - r.totalCost) / r.totalRevenue) * 100 : 0;
        return [
          locationLabel,
          r.name,
          r.primaryBarcode ?? '',
          r.departmentName,
          r.categoryNames,
          r.inStockQty.toFixed(2),
          r.cost == null ? '' : r.cost.toFixed(2),
          r.cashPrice == null ? '' : r.cashPrice.toFixed(2),
          r.quantitySold.toFixed(2),
          r.totalCost.toFixed(2),
          r.totalDiscount.toFixed(2),
          r.totalRevenue.toFixed(2),
          margin.toFixed(2),
        ];
      });
      downloadCsv(reportFilename('inventory_movement_profitability_report', selectedLocationName), [header, ...body]);
    } catch {
      toast.error('Could not export the report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const hasActiveSort = sortBy !== 'name' || sortDir !== 'asc';

  return (
    <div className="space-y-4">
      <PageHeader
        title="Inventory Movement and Profitability"
        description="See which items are selling, being restocked, and making you profit."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={rows.length === 0 || exporting} onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware) */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <div className="min-w-[180px]">
            <MultiSelect
              options={categoryOptions}
              selected={selectedCategoryIds}
              onChange={setSelectedCategoryIds}
              triggerLabel="Categories"
              searchPlaceholder="Search categories..."
              emptyMessage="No categories"
            />
          </div>
          <div className="min-w-[180px]">
            <MultiSelect
              options={departmentOptions}
              selected={selectedDepartmentIds}
              onChange={setSelectedDepartmentIds}
              triggerLabel="Departments"
              searchPlaceholder="Search departments..."
              emptyMessage="No departments"
            />
          </div>
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {/* Search + Sort row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-72 max-w-full">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search"
            className="h-9 w-full rounded-lg border border-input bg-white pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
        <InventorySortPopover
          sortBy={sortBy}
          sortDir={sortDir}
          isActive={hasActiveSort}
          onApply={(field, dir) => {
            setSortBy(field);
            setSortDir(dir);
            setPage(1);
          }}
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Product Name</th>
                <th className="px-4 py-3 font-medium">Department</th>
                <th className="px-4 py-3 font-medium">Categories</th>
                <th className="px-4 py-3 font-medium">In Stock Quantity</th>
                <th className="px-4 py-3 font-medium">Cost ($)</th>
                <th className="px-4 py-3 font-medium">Selling Price ($)</th>
                <th className="px-4 py-3 font-medium">Quantity Sold</th>
                <th className="px-4 py-3 font-medium">Total Cost ($)</th>
                <th className="px-4 py-3 font-medium">Total Revenue ($)</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <EmptyState icon={Package} title="No products" description="Try a different date range." />
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const margin =
                    row.totalRevenue > 0 ? ((row.totalRevenue - row.totalCost) / row.totalRevenue) * 100 : 0;
                  const marginTone =
                    margin >= 50
                      ? 'bg-green-100 text-green-800'
                      : margin >= 20
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-gray-100 text-gray-700';
                  return (
                    <tr key={row.id} className="border-b border-border/60 last:border-b-0 align-top">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{row.name}</div>
                        {row.primaryBarcode && (
                          <div className="text-xs text-muted-foreground">{row.primaryBarcode}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{row.departmentName || '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {row.categoryNames || <span className="text-muted-foreground">N/A</span>}
                      </td>
                      <td className="px-4 py-3 font-mono">{row.inStockQty.toFixed(2)}</td>
                      <td className="px-4 py-3 font-mono">{row.cost == null ? 'N/A' : row.cost.toFixed(2)}</td>
                      <td className="px-4 py-3 font-mono">
                        {row.cashPrice == null ? 'N/A' : row.cashPrice.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 font-mono">{row.quantitySold.toFixed(0)}</td>
                      <td className="px-4 py-3 font-mono">
                        <div>{row.totalCost.toFixed(2)}</div>
                        <div className="text-xs text-muted-foreground">Discount: {row.totalDiscount.toFixed(0)}</div>
                      </td>
                      <td className="px-4 py-3 font-mono">
                        <div>{row.totalRevenue.toFixed(2)}</div>
                        <span
                          className={`mt-1 inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold ${marginTone}`}
                        >
                          {margin.toFixed(2)}%
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function InventorySortPopover({
  sortBy,
  sortDir,
  isActive,
  onApply,
}: {
  sortBy: PosInventorySortBy;
  sortDir: 'asc' | 'desc';
  isActive: boolean;
  onApply: (field: PosInventorySortBy, dir: 'asc' | 'desc') => void;
}) {
  const [open, setOpen] = useState(false);
  const [draftField, setDraftField] = useState<PosInventorySortBy>(sortBy);
  const [draftDir, setDraftDir] = useState<'asc' | 'desc'>(sortDir);
  const ref = useRef<HTMLDivElement | null>(null);

  // React-recommended "adjust state on prop change" pattern: re-sync drafts
  // to the current sortBy/sortDir whenever the popover opens. Done during
  // render (not in an effect) to avoid cascading renders.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDraftField(sortBy);
      setDraftDir(sortDir);
    }
  }

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-2 rounded-lg border border-input bg-white px-3 text-sm hover:bg-muted/40"
      >
        <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
        Sort
        {isActive && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-amber-500" aria-label="Custom sort active" />}
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-lg border border-border bg-white p-2 shadow-lg">
          <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sort by</div>
          <div className="space-y-0.5">
            {INV_SORT_OPTIONS.map((opt) => {
              const checked = draftField === opt.value;
              return (
                <label
                  key={opt.value}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                >
                  <input
                    type="radio"
                    name="inv-sort-by"
                    checked={checked}
                    onChange={() => setDraftField(opt.value)}
                    className="h-4 w-4 text-primary focus:ring-primary"
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
          <div className="my-2 border-t border-border" />
          <button
            type="button"
            onClick={() => setDraftDir('asc')}
            className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted ${
              draftDir === 'asc' ? 'text-primary' : ''
            }`}
          >
            <span className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 rotate-180 text-muted-foreground" />A – Z
            </span>
            {draftDir === 'asc' && <span className="h-2 w-2 rounded-full bg-primary" />}
          </button>
          <button
            type="button"
            onClick={() => setDraftDir('desc')}
            className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted ${
              draftDir === 'desc' ? 'text-primary' : ''
            }`}
          >
            <span className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground" />Z – A
            </span>
            {draftDir === 'desc' && <span className="h-2 w-2 rounded-full bg-primary" />}
          </button>
          <div className="mt-2 flex justify-end gap-2 border-t border-border pt-2">
            <Button size="sm" variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                onApply(draftField, draftDir);
                setOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Inventory Valuation & Summary ───────────────────────────────────────────

const INV_VAL_SORT_OPTIONS: { value: PosInventoryValuationSortBy; label: string }[] = [
  { value: 'name', label: 'Product Name' },
  { value: 'primaryBarcode', label: 'Primary Barcode' },
  { value: 'inStockQty', label: 'Quantity In Stock' },
];

// ── Daily Item Movements ─────────────────────────────────────────────────────

/** Column order shared by the table and the CSV export. */
const DAILY_ITEM_MOVEMENTS_COLUMNS = [
  'Date',
  'Location',
  'Product Name',
  'SKU',
  'Barcode',
  'Department',
  'Category',
  'Qty Sold',
  'Weight Sold (lb)',
  'Gross Sales ($)',
  'Discounts ($)',
  'Returns ($)',
  'Net Sales ($)',
  'COGS ($)',
] as const;

/** 'YYYY-MM-DD' → MM/DD/YYYY, the format the date picker shows, read as a
 *  calendar day (no zone shift). */
function formatBusinessDate(input: string): string {
  const [y, m, d] = input.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/** One CSV row, in DAILY_ITEM_MOVEMENTS_COLUMNS order. */
function dailyItemMovementCsvRow(r: PosDailyItemMovementRow, dateLabel: string): (string | number)[] {
  return [
    dateLabel,
    r.locationName,
    r.productName,
    r.sku ?? '',
    r.barcode ?? '',
    r.departmentName,
    r.categoryNames,
    r.qtySold.toFixed(2),
    r.weightSold == null ? '' : r.weightSold.toFixed(2),
    r.grossSales.toFixed(2),
    r.discounts.toFixed(2),
    r.returns.toFixed(2),
    r.netSales.toFixed(2),
    r.cogs.toFixed(2),
  ];
}

function PosDailyItemMovementsReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  // One business day in the reporting zone. Nothing loads until Submit, so
  // `applied` (the submitted day) starts empty while the picker shows today.
  const [dateInput, setDateInput] = useState(() => reportingTodayInput());
  const [applied, setApplied] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  // Export walks every page of the day's rows; disable the button while it runs.
  const [exporting, setExporting] = useState(false);

  const { data, isLoading } = usePosDailyItemMovements(
    {
      dateFrom: applied ?? undefined,
      dateTo: applied ?? undefined,
      locationId: selectedLocationId || undefined,
      page,
      perPage,
    },
    { enabled: !!applied },
  );

  const rows: PosDailyItemMovementRow[] = data?.data ?? [];
  const pagination = data?.pagination;
  const dateLabel = applied ? formatBusinessDate(applied) : '';

  const handleSubmit = () => {
    if (!dateInput) return;
    setApplied(dateInput);
    setPage(1);
  };

  const handleReset = () => {
    setDateInput(reportingTodayInput());
    setApplied(null);
    setPage(1);
  };

  const handleExport = async () => {
    if (!applied) return;
    // Export the WHOLE day, not just the page on screen: re-query page by
    // page with the same filters and concatenate.
    setExporting(true);
    try {
      const all = await fetchAllReportPages<PosDailyItemMovementRow>(
        '/pos/report/daily-item-movements',
        { dateFrom: applied, dateTo: applied, locationId: selectedLocationId || undefined },
        { tz: reportTz },
      );
      downloadCsv(reportFilename('daily_item_movements', selectedLocationName, applied), [
        [...DAILY_ITEM_MOVEMENTS_COLUMNS],
        ...all.map((r) => dailyItemMovementCsvRow(r, dateLabel)),
      ]);
    } catch {
      toast.error('Could not export the report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Daily Item Movements"
        description="What sold and what came back on one day, item by item, with discounts, net sales and cost."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={rows.length === 0 || exporting} onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — a single business day, applied on Submit */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Business date</label>
            <DateInput
              value={dateInput}
              max={reportingTodayInput()}
              onChange={(e) => setDateInput(e.target.value)}
              className="h-9 w-44 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <Button size="sm" variant="primary" onClick={handleSubmit} disabled={!dateInput}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1400px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                {DAILY_ITEM_MOVEMENTS_COLUMNS.map((col, i) => (
                  <th key={col} className={cn('px-4 py-3 font-medium', i >= 7 && 'text-right')}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!applied ? (
                <tr>
                  <td colSpan={DAILY_ITEM_MOVEMENTS_COLUMNS.length}>
                    <EmptyState
                      icon={Package}
                      title="Pick a date to begin"
                      description="Choose a business date above and click Submit to load that day's item movements."
                    />
                  </td>
                </tr>
              ) : isLoading ? (
                <tr>
                  <td
                    colSpan={DAILY_ITEM_MOVEMENTS_COLUMNS.length}
                    className="px-4 py-10 text-center text-muted-foreground"
                  >
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={DAILY_ITEM_MOVEMENTS_COLUMNS.length}>
                    <EmptyState
                      icon={Package}
                      title="No item movements"
                      description={`Nothing sold or came back on ${dateLabel}. Try another date.`}
                    />
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={`${row.locationId ?? ''}:${row.productId}`}
                    className="border-b border-border/60 last:border-b-0 align-top"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{dateLabel}</td>
                    <td className="px-4 py-3 text-muted-foreground">{row.locationName || '—'}</td>
                    <td className="px-4 py-3 font-medium text-foreground">{row.productName}</td>
                    <td className="px-4 py-3 font-mono text-muted-foreground">{row.sku || '—'}</td>
                    <td className="px-4 py-3 font-mono text-muted-foreground">{row.barcode || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{row.departmentName || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{row.categoryNames || 'N.A.'}</td>
                    <td className="px-4 py-3 text-right font-mono">{row.qtySold.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      {row.weightSold == null ? '—' : row.weightSold.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{row.grossSales.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono">{row.discounts.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono">{row.returns.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono font-medium text-foreground">
                      {row.netSales.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{row.cogs.toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function PosInventoryValuationReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const locationLabel = selectedLocationName || 'All locations';

  const { data: deptsData } = usePosReportDepartments();
  const { data: categoriesData } = usePosReportCategories();
  const { data: vendorsData } = usePosReportVendors();
  const departmentOptions = (deptsData?.data ?? []).map((d) => ({ value: d.id, label: d.name }));
  const categoryOptions = (categoriesData?.data ?? []).map((c) => ({ value: c.id, label: c.name }));
  const vendorOptions = (vendorsData?.data ?? []).map((v) => ({ value: v.id, label: v.name }));

  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
  const [selectedVendorIds, setSelectedVendorIds] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState<PosInventoryValuationSortBy>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [applied, setApplied] = useState<{
    categoryIds: string[];
    departmentIds: string[];
    vendorIds: string[];
    search: string;
  }>({
    categoryIds: [],
    departmentIds: [],
    vendorIds: [],
    search: '',
  });

  useEffect(() => {
    const t = setTimeout(() => {
      setApplied((prev) => ({ ...prev, search: searchInput.trim() }));
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading } = usePosInventoryValuation({
    locationId: selectedLocationId || undefined,
    categoryIds: idFilterParam(applied.categoryIds, categoryOptions),
    departmentIds: idFilterParam(applied.departmentIds, departmentOptions),
    vendorIds: applied.vendorIds.length > 0 ? applied.vendorIds.join(',') : undefined,
    search: applied.search || undefined,
    sortBy,
    sortDir,
    page,
    perPage,
  });

  const rows: PosInventoryValuationRow[] = data?.data ?? [];
  const totals = data?.totals ?? { retail: 0, cost: 0 };
  const pagination = data?.pagination;
  // Export walks every page of the filtered result, so it can take a moment —
  // disable the button while it runs rather than let a second click start a
  // duplicate download.
  const [exporting, setExporting] = useState(false);

  const handleApply = () => {
    setApplied({
      categoryIds: selectedCategoryIds,
      departmentIds: selectedDepartmentIds,
      vendorIds: selectedVendorIds,
      search: searchInput.trim(),
    });
    setPage(1);
  };

  const handleReset = () => {
    setSelectedCategoryIds([]);
    setSelectedDepartmentIds([]);
    setSelectedVendorIds([]);
    setSearchInput('');
    setSortBy('name');
    setSortDir('asc');
    setApplied({ categoryIds: [], departmentIds: [], vendorIds: [], search: '' });
    setPage(1);
  };

  const handleExport = async () => {
    // Export the WHOLE filtered result, not just the page on screen.
    setExporting(true);
    try {
      const all = await fetchAllReportPages<PosInventoryValuationRow>(
        '/pos/report/inventory-valuation',
        {
          locationId: selectedLocationId || undefined,
          categoryIds: idFilterParam(applied.categoryIds, categoryOptions),
          departmentIds: idFilterParam(applied.departmentIds, departmentOptions),
          vendorIds: applied.vendorIds.length > 0 ? applied.vendorIds.join(',') : undefined,
          search: applied.search || undefined,
          sortBy,
          sortDir,
        },
        { zonedDates: false },
      );
      const header = [
        'Location',
        'Product Name',
        'Primary Barcode',
        'Departments',
        'Categories',
        'Quantity In Stock',
        'Cost ($)',
        'Selling Price ($)',
        'Valuation Based on Base Retail Unit ($)',
        'Valuation Based on Base Unit Cost ($)',
      ];
      const body = all.map((r) => [
        r.locationName ?? locationLabel,
        r.name,
        r.primaryBarcode ?? '',
        r.departmentName,
        r.categoryNames,
        r.inStockQty.toFixed(2),
        r.cost == null ? '' : r.cost.toFixed(2),
        r.sellingPrice == null ? '' : r.sellingPrice.toFixed(2),
        r.retailValuation.toFixed(2),
        r.costValuation.toFixed(2),
      ]);
      downloadCsv(reportFilename('inventory_valuation_summary_report', selectedLocationName), [header, ...body]);
    } catch {
      toast.error('Could not export the report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const hasActiveSort = sortBy !== 'name' || sortDir !== 'asc';

  return (
    <div className="space-y-4">
      <PageHeader
        title="Inventory Valuation & Summary"
        description="Get a snapshot of all items currently in stock."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={rows.length === 0 || exporting} onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[180px]">
          <MultiSelect
            options={categoryOptions}
            selected={selectedCategoryIds}
            onChange={setSelectedCategoryIds}
            triggerLabel="Categories"
            searchPlaceholder="Search categories..."
            emptyMessage="No categories"
          />
        </div>
        <div className="min-w-[180px]">
          <MultiSelect
            options={departmentOptions}
            selected={selectedDepartmentIds}
            onChange={setSelectedDepartmentIds}
            triggerLabel="Departments"
            searchPlaceholder="Search departments..."
            emptyMessage="No departments"
          />
        </div>
        <div className="min-w-[180px]">
          <MultiSelect
            options={vendorOptions}
            selected={selectedVendorIds}
            onChange={setSelectedVendorIds}
            triggerLabel="Vendors"
            searchPlaceholder="Search vendors..."
            emptyMessage="No vendors"
          />
        </div>
        <Button size="sm" variant="primary" onClick={handleApply}>
          Apply
        </Button>
        <Button size="sm" variant="destructive-outline" onClick={handleReset}>
          Reset
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="text-sm font-medium text-muted-foreground">Total Valuation Based on Base Unit Retail</div>
          <div className="mt-1 text-2xl font-semibold text-foreground">${formatCurrencyPlain(totals.retail)}</div>
        </div>
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="text-sm font-medium text-muted-foreground">Total Valuation Based on Base Unit Cost</div>
          <div className="mt-1 text-2xl font-semibold text-foreground">${formatCurrencyPlain(totals.cost)}</div>
        </div>
      </div>

      {/* Search + Sort */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-72 max-w-full">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search"
            className="h-9 w-full rounded-lg border border-input bg-white pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
        <InventoryValuationSortPopover
          sortBy={sortBy}
          sortDir={sortDir}
          isActive={hasActiveSort}
          onApply={(field, dir) => {
            setSortBy(field);
            setSortDir(dir);
            setPage(1);
          }}
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Product Name</th>
                <th className="px-4 py-3 font-medium">Departments</th>
                <th className="px-4 py-3 font-medium">Categories</th>
                <th className="px-4 py-3 font-medium">Valuation Based on Base Retail Unit ($)</th>
                <th className="px-4 py-3 font-medium">Valuation Based on Base Unit Cost ($)</th>
                <th className="px-4 py-3 font-medium">Location</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState
                      icon={SearchX}
                      title="No matching products"
                      description="Try changing the filters or search."
                    />
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="border-b border-border/60 last:border-b-0 align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{row.name}</div>
                      {row.primaryBarcode && <div className="text-xs text-muted-foreground">{row.primaryBarcode}</div>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{row.departmentName || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {row.categoryNames || <span className="text-muted-foreground">N.A.</span>}
                    </td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.retailValuation)}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(row.costValuation)}</td>
                    <td className="px-4 py-3">
                      <div className="text-foreground">{row.locationName ?? locationLabel}</div>
                      <div className="text-xs text-muted-foreground">{row.inStockQty.toFixed(2)} In stock</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function InventoryValuationSortPopover({
  sortBy,
  sortDir,
  isActive,
  onApply,
}: {
  sortBy: PosInventoryValuationSortBy;
  sortDir: 'asc' | 'desc';
  isActive: boolean;
  onApply: (field: PosInventoryValuationSortBy, dir: 'asc' | 'desc') => void;
}) {
  const [open, setOpen] = useState(false);
  const [draftField, setDraftField] = useState<PosInventoryValuationSortBy>(sortBy);
  const [draftDir, setDraftDir] = useState<'asc' | 'desc'>(sortDir);
  const ref = useRef<HTMLDivElement | null>(null);

  // React-recommended "adjust state on prop change" pattern: re-sync drafts
  // to the current sortBy/sortDir whenever the popover opens. Done during
  // render (not in an effect) to avoid cascading renders.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDraftField(sortBy);
      setDraftDir(sortDir);
    }
  }

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-2 rounded-lg border border-input bg-white px-3 text-sm hover:bg-muted/40"
      >
        <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
        Sort
        {isActive && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-amber-500" aria-label="Custom sort active" />}
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-lg border border-border bg-white p-2 shadow-lg">
          <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sort by</div>
          <div className="space-y-0.5">
            {INV_VAL_SORT_OPTIONS.map((opt) => {
              const checked = draftField === opt.value;
              return (
                <label
                  key={opt.value}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                >
                  <input
                    type="radio"
                    name="inv-val-sort-by"
                    checked={checked}
                    onChange={() => setDraftField(opt.value)}
                    className="h-4 w-4 text-primary focus:ring-primary"
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
          <div className="my-2 border-t border-border" />
          <button
            type="button"
            onClick={() => setDraftDir('asc')}
            className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted ${
              draftDir === 'asc' ? 'text-primary' : ''
            }`}
          >
            <span className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 rotate-180 text-muted-foreground" />A – Z
            </span>
            {draftDir === 'asc' && <span className="h-2 w-2 rounded-full bg-primary" />}
          </button>
          <button
            type="button"
            onClick={() => setDraftDir('desc')}
            className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted ${
              draftDir === 'desc' ? 'text-primary' : ''
            }`}
          >
            <span className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground" />Z – A
            </span>
            {draftDir === 'desc' && <span className="h-2 w-2 rounded-full bg-primary" />}
          </button>
          <div className="mt-2 flex justify-end gap-2 border-t border-border pt-2">
            <Button size="sm" variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                onApply(draftField, draftDir);
                setOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Price Updates ───────────────────────────────────────────────────────────

const PRICE_UPDATES_SORT_OPTIONS: { value: PosPriceUpdatesSortBy; label: string }[] = [
  { value: 'name', label: 'Product' },
  { value: 'primaryBarcode', label: 'Barcode' },
  { value: 'sellingPrice', label: 'Price ($)' },
  { value: 'rewardPoints', label: 'Reward Points' },
];

// Plain-language label + colour for a card attempt's state.
function cardAttemptStatus(r: PosCardAttemptRow): { label: string; classes: string } {
  if (r.refundedAt) return { label: 'Refunded', classes: 'bg-green-100 text-green-700' };
  switch (r.status) {
    case 'duplicate':
      return { label: 'Duplicate — refund needed', classes: 'bg-red-100 text-red-700' };
    case 'unverified':
      return { label: 'Being checked', classes: 'bg-amber-100 text-amber-800' };
    case 'unverified_expired':
      return { label: 'Not confirmed — check card account', classes: 'bg-orange-100 text-orange-800' };
    case 'resolved':
      return { label: 'Resolved', classes: 'bg-gray-100 text-gray-700' };
    default:
      return { label: r.status, classes: 'bg-gray-100 text-gray-700' };
  }
}

/**
 * Card attempts a register couldn't confirm (timeout / lost reply). A
 * "duplicate" was charged after the order had been paid another way — the
 * store refunds it (card account or register) and marks it refunded here.
 */
function PosCardAttemptsReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();
  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [statusFilter, setStatusFilter] = useState('refund_needed');
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string; status: string }>({
    ...initialRange,
    status: 'refund_needed',
  });

  const { data, isLoading } = usePosCardAttempts({
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    locationId: selectedLocationId || undefined,
    status: applied.status || undefined,
  });
  const markRefunded = useMarkCardAttemptRefunded();
  const canMarkRefunded = usePermissions().has('reports.pos.refunded');
  const rows: PosCardAttemptRow[] = data?.data ?? [];
  const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

  const handleApply = () => {
    setApplied({ dateFrom: dateFromInput, dateTo: dateToInput, status: statusFilter });
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setStatusFilter('refund_needed');
    setApplied({ ...r, status: 'refund_needed' });
  };
  const handleMarkRefunded = (r: PosCardAttemptRow) => {
    const note = window.prompt(
      `Mark the $${r.amount.toFixed(2)} charge as refunded? Optional note (e.g. how it was refunded):`,
      '',
    );
    if (note === null) return;
    markRefunded.mutate(
      { id: r.id, note: note.trim() || undefined },
      {
        onSuccess: () => toast.success('Marked as refunded'),
        onError: () => toast.error('Could not mark it refunded. Please try again.'),
      },
    );
  };
  const handleExport = () => {
    const header = [
      'Started',
      'Status',
      'Amount ($)',
      'Register',
      'Paid under order',
      'Terminal ref',
      'Auth',
      'Refunded',
      'Note',
    ];
    const body = rows.map((r) => [
      fmtDate(r.startedAt),
      cardAttemptStatus(r).label,
      r.amount.toFixed(2),
      r.registerName ?? '',
      r.paidOrderNumber ? `#${r.paidOrderNumber}` : '',
      r.terminalRef ?? '',
      r.authCode ?? '',
      r.refundedAt ? fmtDate(r.refundedAt) : '',
      r.refundNote ?? '',
    ]);
    downloadCsv(reportFilename('pos_card_attempts', selectedLocationName), [header, ...body]);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Card Attempts to Check"
        description={`Card payments the terminal could not confirm — ${selectedLocationName || 'All locations'}.`}
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={rows.length === 0} onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        </PosExportOnly>
      </PageHeader>

      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Show</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-md border border-input bg-white px-2.5 text-sm focus:outline-none"
            >
              <option value="refund_needed">Refund needed</option>
              <option value="unverified">Being checked</option>
              <option value="unverified_expired">Not confirmed — check card account</option>
              <option value="refunded">Refunded</option>
              <option value="resolved">Resolved</option>
              <option value="">All</option>
            </select>
          </div>
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Started</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Amount ($)</th>
                <th className="px-4 py-3 font-medium">Register</th>
                <th className="px-4 py-3 font-medium">Paid under order</th>
                <th className="px-4 py-3 font-medium">Terminal ref</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      icon={CreditCard}
                      title="Nothing to check"
                      description="No card attempts match these filters."
                    />
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const st = cardAttemptStatus(r);
                  return (
                    <tr key={r.id} className="border-b border-border/60 last:border-b-0">
                      <td className="px-4 py-3 align-top text-muted-foreground">{fmtDate(r.startedAt)}</td>
                      <td className="px-4 py-3 align-top">
                        <span className={`rounded px-2 py-0.5 text-xs font-medium ${st.classes}`}>{st.label}</span>
                        {r.refundNote ? <div className="mt-1 text-xs text-muted-foreground">{r.refundNote}</div> : null}
                      </td>
                      <td className="px-4 py-3 align-top font-mono">{r.amount.toFixed(2)}</td>
                      <td className="px-4 py-3 align-top">{r.registerName ?? '—'}</td>
                      <td className="px-4 py-3 align-top">{r.paidOrderNumber ? `#${r.paidOrderNumber}` : '—'}</td>
                      <td className="px-4 py-3 align-top font-mono text-xs">{r.terminalRef ?? '—'}</td>
                      <td className="px-4 py-3 align-top text-right">
                        {r.status === 'duplicate' && !r.refundedAt && canMarkRefunded ? (
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={markRefunded.isPending}
                            onClick={() => handleMarkRefunded(r)}
                          >
                            Mark refunded
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PosPriceChangesReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();
  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [sourceFilter, setSourceFilter] = useState('');
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string; source: string }>({
    ...initialRange,
    source: '',
  });

  const { data, isLoading } = usePosPriceChanges({
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    locationId: selectedLocationId || undefined,
    source: applied.source || undefined,
  });
  const rows: PosPriceChangeRow[] = data?.data ?? [];

  const typeLabel = (s: string) => (s === 'product_update' ? 'Product price' : 'Order override');
  const fmtDate = (iso: string) => new Date(iso).toLocaleString();

  const handleApply = () => {
    setApplied({ dateFrom: dateFromInput, dateTo: dateToInput, source: sourceFilter });
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setSourceFilter('');
    setApplied({ ...r, source: '' });
  };
  const handleExport = () => {
    const header = ['Date', 'Product', 'Old Price ($)', 'New Price ($)', 'Changed By', 'Register', 'Type', 'Order'];
    const body = rows.map((r) => [
      fmtDate(r.createdAt),
      r.productName,
      r.oldPrice.toFixed(2),
      r.newPrice.toFixed(2),
      r.changedBy,
      r.registerName ?? '',
      typeLabel(r.source),
      r.orderNumber ?? '',
    ]);
    downloadCsv(reportFilename('pos_price_changes', selectedLocationName), [header, ...body]);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Price Changes"
        description={`Price changes made on the POS — ${selectedLocationName || 'All locations'}.`}
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={rows.length === 0} onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware) */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Type</label>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="h-9 rounded-md border border-input bg-white px-2.5 text-sm focus:outline-none"
            >
              <option value="">All types</option>
              <option value="order_override">Order override</option>
              <option value="product_update">Product price</option>
            </select>
          </div>
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Product</th>
                <th className="px-4 py-3 font-medium">Price ($)</th>
                <th className="px-4 py-3 font-medium">Changed By</th>
                <th className="px-4 py-3 font-medium">Register</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Order</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState icon={History} title="No price changes" description="Try a different date range." />
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 last:border-b-0">
                    <td className="px-4 py-3 align-top text-muted-foreground">{fmtDate(r.createdAt)}</td>
                    <td className="px-4 py-3 align-top font-medium text-foreground">{r.productName}</td>
                    <td className="px-4 py-3 align-top font-mono">
                      {r.oldPrice.toFixed(2)} → {r.newPrice.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 align-top">{r.changedBy}</td>
                    <td className="px-4 py-3 align-top">{r.registerName ?? '—'}</td>
                    <td className="px-4 py-3 align-top">{typeLabel(r.source)}</td>
                    <td className="px-4 py-3 align-top">{r.orderNumber ? `#${r.orderNumber}` : '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PosPriceUpdatesReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const locationLabel = selectedLocationName || 'All locations';
  // Reporting timezone drives the datetime pickers, the query boundary math
  // (withLocalDateRange) and the Date column below, so nothing renders in raw UTC.
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const { data: deptsData } = usePosReportDepartments();
  const { data: categoriesData } = usePosReportCategories();
  const departmentOptions = (deptsData?.data ?? []).map((d) => ({ value: d.id, label: d.name }));
  const categoryOptions = (categoriesData?.data ?? []).map((c) => ({ value: c.id, label: c.name }));

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<PosPriceUpdatesSortBy>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [applied, setApplied] = useState<{
    dateFrom: string;
    dateTo: string;
    departmentIds: string[];
    categoryIds: string[];
  }>({
    ...initialRange,
    departmentIds: [],
    categoryIds: [],
  });

  const { data, isLoading } = usePosPriceUpdates({
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    locationId: selectedLocationId || undefined,
    departmentIds: idFilterParam(applied.departmentIds, departmentOptions),
    categoryIds: idFilterParam(applied.categoryIds, categoryOptions),
    sortBy,
    sortDir,
    page,
    perPage,
  });

  const rows: PosPriceUpdateRow[] = data?.data ?? [];
  const pagination = data?.pagination;
  // Export walks every page of the filtered result, so it can take a moment —
  // disable the button while it runs rather than let a second click start a
  // duplicate download.
  const [exporting, setExporting] = useState(false);

  const handleApply = () => {
    setApplied({
      dateFrom: dateFromInput,
      dateTo: dateToInput,
      departmentIds: selectedDepartmentIds,
      categoryIds: selectedCategoryIds,
    });
    setPage(1);
  };

  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setSelectedDepartmentIds([]);
    setSelectedCategoryIds([]);
    setSortBy('name');
    setSortDir('asc');
    setApplied({ ...r, departmentIds: [], categoryIds: [] });
    setPage(1);
  };

  const handleExport = async () => {
    // Export the WHOLE filtered result, not just the page on screen.
    setExporting(true);
    try {
      const all = await fetchAllReportPages<PosPriceUpdateRow>(
        '/pos/report/price-updates',
        {
          dateFrom: applied.dateFrom || undefined,
          dateTo: applied.dateTo || undefined,
          locationId: selectedLocationId || undefined,
          departmentIds: idFilterParam(applied.departmentIds, departmentOptions),
          categoryIds: idFilterParam(applied.categoryIds, categoryOptions),
          sortBy,
          sortDir,
        },
        { tz: reportTz },
      );
      const header = [
        'Location',
        'Product',
        'Primary Barcode',
        'Previous Price ($)',
        'Price ($)',
        'Reward Points',
        'Department',
        'Categories',
        'In Stock Quantity',
        'Date',
      ];
      const body = all.map((r) => [
        locationLabel,
        r.name,
        r.primaryBarcode ?? '',
        r.previousPrice == null ? '' : r.previousPrice.toFixed(2),
        r.sellingPrice == null ? '' : r.sellingPrice.toFixed(2),
        r.rewardPoints.toFixed(2),
        r.departmentName,
        r.categoryNames,
        r.inStockQty.toFixed(2),
        formatPosDateTime(r.lastChangedAt, reportTz),
      ]);
      downloadCsv(reportFilename('price_updates_report', selectedLocationName), [header, ...body]);
    } catch {
      toast.error('Could not export the report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const hasActiveSort = sortBy !== 'name' || sortDir !== 'asc';

  return (
    <div className="space-y-4">
      <PageHeader title="Price Updates" description="View changes made to product prices recently." onBack={onBack}>
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={rows.length === 0 || exporting} onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware) */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Departments</label>
            <div className="min-w-[180px]">
              <MultiSelect
                options={departmentOptions}
                selected={selectedDepartmentIds}
                onChange={setSelectedDepartmentIds}
                triggerLabel="Departments"
                searchPlaceholder="Search departments..."
                emptyMessage="No departments"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Categories</label>
            <div className="min-w-[180px]">
              <MultiSelect
                options={categoryOptions}
                selected={selectedCategoryIds}
                onChange={setSelectedCategoryIds}
                triggerLabel="Categories"
                searchPlaceholder="Search categories..."
                emptyMessage="No categories"
              />
            </div>
          </div>
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {/* Sort row */}
      <div className="flex flex-wrap items-center gap-3">
        <PriceUpdatesSortPopover
          sortBy={sortBy}
          sortDir={sortDir}
          isActive={hasActiveSort}
          onApply={(field, dir) => {
            setSortBy(field);
            setSortDir(dir);
            setPage(1);
          }}
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Product</th>
                <th className="px-4 py-3 font-medium">Previous Price ($)</th>
                <th className="px-4 py-3 font-medium">Price ($)</th>
                <th className="px-4 py-3 font-medium">Reward Points</th>
                <th className="px-4 py-3 font-medium">Department</th>
                <th className="px-4 py-3 font-medium">Categories</th>
                <th className="px-4 py-3 font-medium">In Stock Quantity</th>
                <th className="px-4 py-3 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <EmptyState icon={History} title="No price updates" description="Try a different date range." />
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="border-b border-border/60 last:border-b-0 align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-primary">{row.name}</div>
                      {row.primaryBarcode && <div className="text-xs text-muted-foreground">{row.primaryBarcode}</div>}
                    </td>
                    <td className="px-4 py-3 font-mono text-muted-foreground">
                      {row.previousPrice == null ? '—' : row.previousPrice.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 font-mono">
                      {row.sellingPrice == null ? '—' : row.sellingPrice.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 font-mono">{row.rewardPoints.toFixed(2)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{row.departmentName || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {row.categoryNames || <span className="text-muted-foreground">N.A.</span>}
                    </td>
                    <td className="px-4 py-3 font-mono">{row.inStockQty.toFixed(2)}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                      {row.lastChangedAt ? formatPosDateTime(row.lastChangedAt, reportTz) : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function PriceUpdatesSortPopover({
  sortBy,
  sortDir,
  isActive,
  onApply,
}: {
  sortBy: PosPriceUpdatesSortBy;
  sortDir: 'asc' | 'desc';
  isActive: boolean;
  onApply: (field: PosPriceUpdatesSortBy, dir: 'asc' | 'desc') => void;
}) {
  const [open, setOpen] = useState(false);
  const [draftField, setDraftField] = useState<PosPriceUpdatesSortBy>(sortBy);
  const [draftDir, setDraftDir] = useState<'asc' | 'desc'>(sortDir);
  const ref = useRef<HTMLDivElement | null>(null);

  // React-recommended "adjust state on prop change" pattern: re-sync drafts
  // to the current sortBy/sortDir whenever the popover opens. Done during
  // render (not in an effect) to avoid cascading renders.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDraftField(sortBy);
      setDraftDir(sortDir);
    }
  }

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-2 rounded-lg border border-input bg-white px-3 text-sm hover:bg-muted/40"
      >
        <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
        Sort
        {isActive && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-amber-500" aria-label="Custom sort active" />}
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-lg border border-border bg-white p-2 shadow-lg">
          <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sort by</div>
          <div className="space-y-0.5">
            {PRICE_UPDATES_SORT_OPTIONS.map((opt) => {
              const checked = draftField === opt.value;
              return (
                <label
                  key={opt.value}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                >
                  <input
                    type="radio"
                    name="price-updates-sort-by"
                    checked={checked}
                    onChange={() => setDraftField(opt.value)}
                    className="h-4 w-4 text-primary focus:ring-primary"
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
          <div className="my-2 border-t border-border" />
          <button
            type="button"
            onClick={() => setDraftDir('asc')}
            className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted ${
              draftDir === 'asc' ? 'text-primary' : ''
            }`}
          >
            <span className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 rotate-180 text-muted-foreground" />A – Z
            </span>
            {draftDir === 'asc' && <span className="h-2 w-2 rounded-full bg-primary" />}
          </button>
          <button
            type="button"
            onClick={() => setDraftDir('desc')}
            className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted ${
              draftDir === 'desc' ? 'text-primary' : ''
            }`}
          >
            <span className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground" />Z – A
            </span>
            {draftDir === 'desc' && <span className="h-2 w-2 rounded-full bg-primary" />}
          </button>
          <div className="mt-2 flex justify-end gap-2 border-t border-border pt-2">
            <Button size="sm" variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                onApply(draftField, draftDir);
                setOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Inventory Trail ──────────────────────────────────────────────────────────

// Operation kinds shown in the filter. Only Sales & Refund are derivable from
// the current POS data; Receiving / Shrink / Recount have no ledger source yet,
// so selecting only those returns no rows.
const INVENTORY_TRAIL_OPERATIONS: { value: string; label: string }[] = [
  { value: 'sales', label: 'Sales' },
  { value: 'receiving', label: 'Receiving' },
  { value: 'shrink', label: 'Shrink' },
  { value: 'recount', label: 'Recount' },
  { value: 'refund', label: 'Refund' },
];

const INVENTORY_TRAIL_SORT_OPTIONS: { value: PosInventoryTrailSortBy; label: string }[] = [
  { value: 'date', label: 'Date' },
  { value: 'name', label: 'Product Name' },
  { value: 'department', label: 'Department' },
  { value: 'type', label: 'Type' },
  { value: 'quantity', label: 'Quantity' },
  { value: 'user', label: 'User' },
];

function PosInventoryTrailReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const locationLabel = selectedLocationName || 'All locations';
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const { data: deptsData } = usePosReportDepartments();
  const { data: categoriesData } = usePosReportCategories();
  const departmentOptions = (deptsData?.data ?? []).map((d) => ({ value: d.id, label: d.name }));
  const categoryOptions = (categoriesData?.data ?? []).map((c) => ({ value: c.id, label: c.name }));

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [selectedOperations, setSelectedOperations] = useState<string[]>([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [sortBy, setSortBy] = useState<PosInventoryTrailSortBy>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [applied, setApplied] = useState<{
    dateFrom: string;
    dateTo: string;
    operations: string[];
    departmentIds: string[];
    categoryIds: string[];
  }>({
    ...initialRange,
    operations: [],
    departmentIds: [],
    categoryIds: [],
  });

  // Debounce the search box so each keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedSearch(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading } = usePosInventoryTrail({
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    locationId: selectedLocationId || undefined,
    operations: applied.operations.length > 0 ? applied.operations.join(',') : undefined,
    departmentIds: idFilterParam(applied.departmentIds, departmentOptions),
    categoryIds: idFilterParam(applied.categoryIds, categoryOptions),
    search: appliedSearch || undefined,
    sortBy,
    sortDir,
    page,
    perPage,
  });

  const rows: PosInventoryTrailRow[] = data?.data ?? [];
  const pagination = data?.pagination;
  // Export walks every page of the filtered result, so it can take a moment —
  // disable the button while it runs rather than let a second click start a
  // duplicate download.
  const [exporting, setExporting] = useState(false);

  const handleApply = () => {
    setApplied({
      dateFrom: dateFromInput,
      dateTo: dateToInput,
      operations: selectedOperations,
      departmentIds: selectedDepartmentIds,
      categoryIds: selectedCategoryIds,
    });
    setPage(1);
  };

  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setSelectedOperations([]);
    setSelectedDepartmentIds([]);
    setSelectedCategoryIds([]);
    setSearchInput('');
    setAppliedSearch('');
    setSortBy('date');
    setSortDir('desc');
    setApplied({ ...r, operations: [], departmentIds: [], categoryIds: [] });
    setPage(1);
  };

  const handleExport = async () => {
    // Export the WHOLE filtered result, not just the page on screen.
    setExporting(true);
    try {
      const all = await fetchAllReportPages<PosInventoryTrailRow>(
        '/pos/report/inventory-trail',
        {
          dateFrom: applied.dateFrom || undefined,
          dateTo: applied.dateTo || undefined,
          locationId: selectedLocationId || undefined,
          operations: applied.operations.length > 0 ? applied.operations.join(',') : undefined,
          departmentIds: idFilterParam(applied.departmentIds, departmentOptions),
          categoryIds: idFilterParam(applied.categoryIds, categoryOptions),
          search: appliedSearch || undefined,
          sortBy,
          sortDir,
        },
        { tz: reportTz },
      );
      const header = [
        'Location',
        'Product',
        'Primary Barcode',
        'Department',
        'Categories',
        'Type',
        'Quantity',
        'User',
        'Date',
        'Description',
      ];
      const body = all.map((r) => [
        r.locationName || locationLabel,
        r.name,
        r.primaryBarcode ?? '',
        r.departmentName,
        r.categoryNames,
        r.type,
        r.quantity.toFixed(2),
        r.userName,
        formatDateTime(r.createdAt),
        r.description,
      ]);
      downloadCsv(reportFilename('inventory_trail_report', selectedLocationName), [header, ...body]);
    } catch {
      toast.error('Could not export the report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const hasActiveSort = sortBy !== 'date' || sortDir !== 'desc';

  return (
    <div className="space-y-4">
      <PageHeader title="Inventory Trail" description="Track every stock movement across your store." onBack={onBack}>
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={rows.length === 0 || exporting} onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware) */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <div className="min-w-[180px]">
            <MultiSelect
              options={INVENTORY_TRAIL_OPERATIONS}
              selected={selectedOperations}
              onChange={setSelectedOperations}
              triggerLabel="Operations"
              searchPlaceholder="Search operations..."
              emptyMessage="No operations"
            />
          </div>
          <div className="min-w-[180px]">
            <MultiSelect
              options={departmentOptions}
              selected={selectedDepartmentIds}
              onChange={setSelectedDepartmentIds}
              triggerLabel="Departments"
              searchPlaceholder="Search departments..."
              emptyMessage="No departments"
            />
          </div>
          <div className="min-w-[180px]">
            <MultiSelect
              options={categoryOptions}
              selected={selectedCategoryIds}
              onChange={setSelectedCategoryIds}
              triggerLabel="Categories"
              searchPlaceholder="Search categories..."
              emptyMessage="No categories"
            />
          </div>
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {/* Search + Sort row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex max-w-sm flex-1 items-center gap-2 rounded-md border border-input bg-white px-2.5 py-1.5 text-sm">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full bg-transparent text-sm focus:outline-none"
          />
        </div>
        <InventoryTrailSortPopover
          sortBy={sortBy}
          sortDir={sortDir}
          isActive={hasActiveSort}
          onApply={(field, dir) => {
            setSortBy(field);
            setSortDir(dir);
            setPage(1);
          }}
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Product Name</th>
                <th className="px-4 py-3 font-medium">Department</th>
                <th className="px-4 py-3 font-medium">Categories</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Quantity</th>
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Location</th>
                <th className="px-4 py-3 font-medium">Description</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <EmptyState
                      icon={ArrowUpDown}
                      title="No inventory movements"
                      description="Try a different date range."
                    />
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="border-b border-border/60 last:border-b-0 align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{row.name}</div>
                      {row.primaryBarcode && <div className="text-xs text-muted-foreground">{row.primaryBarcode}</div>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{row.departmentName || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {row.categoryNames || <span className="text-muted-foreground">N.A.</span>}
                    </td>
                    <td className="px-4 py-3">{row.type}</td>
                    <td className="px-4 py-3 font-mono">{row.quantity.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <div className="text-foreground">{row.userName || '—'}</div>
                      <div className="text-xs text-muted-foreground">{formatDateTime(row.createdAt)}</div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{row.locationName || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{row.description || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function InventoryTrailSortPopover({
  sortBy,
  sortDir,
  isActive,
  onApply,
}: {
  sortBy: PosInventoryTrailSortBy;
  sortDir: 'asc' | 'desc';
  isActive: boolean;
  onApply: (field: PosInventoryTrailSortBy, dir: 'asc' | 'desc') => void;
}) {
  const [open, setOpen] = useState(false);
  const [draftField, setDraftField] = useState<PosInventoryTrailSortBy>(sortBy);
  const [draftDir, setDraftDir] = useState<'asc' | 'desc'>(sortDir);
  const ref = useRef<HTMLDivElement | null>(null);

  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDraftField(sortBy);
      setDraftDir(sortDir);
    }
  }

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-2 rounded-lg border border-input bg-white px-3 text-sm hover:bg-muted/40"
      >
        <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
        Sort
        {isActive && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-amber-500" aria-label="Custom sort active" />}
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-lg border border-border bg-white p-2 shadow-lg">
          <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sort by</div>
          <div className="space-y-0.5">
            {INVENTORY_TRAIL_SORT_OPTIONS.map((opt) => {
              const checked = draftField === opt.value;
              return (
                <label
                  key={opt.value}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                >
                  <input
                    type="radio"
                    name="inventory-trail-sort-by"
                    checked={checked}
                    onChange={() => setDraftField(opt.value)}
                    className="h-4 w-4 text-primary focus:ring-primary"
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
          <div className="my-2 border-t border-border" />
          <button
            type="button"
            onClick={() => setDraftDir('asc')}
            className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted ${
              draftDir === 'asc' ? 'text-primary' : ''
            }`}
          >
            <span className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 rotate-180 text-muted-foreground" />A – Z
            </span>
            {draftDir === 'asc' && <span className="h-2 w-2 rounded-full bg-primary" />}
          </button>
          <button
            type="button"
            onClick={() => setDraftDir('desc')}
            className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted ${
              draftDir === 'desc' ? 'text-primary' : ''
            }`}
          >
            <span className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground" />Z – A
            </span>
            {draftDir === 'desc' && <span className="h-2 w-2 rounded-full bg-primary" />}
          </button>
          <div className="mt-2 flex justify-end gap-2 border-t border-border pt-2">
            <Button size="sm" variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                onApply(draftField, draftDir);
                setOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function PosTopItemsReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const locationLabel = selectedLocationName || 'All locations';
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [searchInput, setSearchInput] = useState('');
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string; search: string }>({
    ...initialRange,
    search: '',
  });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  // Debounce search so each keystroke doesn't fire a new request.
  useEffect(() => {
    const t = setTimeout(() => {
      setApplied((prev) => ({ ...prev, search: searchInput }));
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading } = usePosTopItems({
    page,
    perPage,
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    locationId: selectedLocationId || undefined,
    search: applied.search || undefined,
  });

  const rows: PosTopItemRow[] = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;
  const totalPages = data?.pagination?.totalPages ?? 1;
  const [exporting, setExporting] = useState(false);

  const handleApply = () => {
    setApplied({ dateFrom: dateFromInput, dateTo: dateToInput, search: searchInput });
    setPage(1);
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setSearchInput('');
    setApplied({ ...r, search: '' });
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Top Items by Sales & Volume"
        description="Discover which products made you the most revenue."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button
            variant="primary"
            size="sm"
            disabled={total === 0 || exporting}
            onClick={async () => {
              // Pull every matching row, not just the visible page. We page
              // through 500 at a time so a huge result set doesn't hang on
              // one giant query.
              setExporting(true);
              try {
                const all: PosTopItemRow[] = [];
                const batch = 500;
                for (let p = 1; ; p++) {
                  const res = await api.get<PaginatedResponse<PosTopItemRow>>(
                    '/pos/report/top-items',
                    withLocalDateRange({
                      page: p,
                      perPage: batch,
                      dateFrom: applied.dateFrom || undefined,
                      dateTo: applied.dateTo || undefined,
                      locationId: selectedLocationId || undefined,
                      search: applied.search || undefined,
                    }) as Record<string, string | number | undefined>,
                  );
                  all.push(...res.data);
                  if (res.data.length < batch || p >= (res.pagination?.totalPages ?? 1)) break;
                }
                const header = ['Location', 'Product Name', 'Total Sales Amount ($)', 'Units Sold'];
                const body = all.map((r) => [
                  locationLabel,
                  r.productName,
                  r.totalSalesAmount.toFixed(2),
                  r.unitsSold.toFixed(2),
                ]);
                downloadCsv(reportFilename('top_items_by_sales_and_volume_report', selectedLocationName), [
                  header,
                  ...body,
                ]);
              } finally {
                setExporting(false);
              }
            }}
          >
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      <div className="inline-flex max-w-sm items-center gap-2 rounded-md border border-input bg-white px-2.5 py-1.5 text-sm">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search"
          className="w-full bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Product Name</th>
                <th className="px-4 py-3 font-medium">Total Sales Amount ($)</th>
                <th className="px-4 py-3 font-medium">Units Sold</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={3} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={3}>
                    <EmptyState icon={Package} title="No items" description="Try a different date range or search." />
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.productId} className="border-b border-border/60 last:border-b-0">
                    <td className="px-4 py-3">{r.productName}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(r.totalSalesAmount)}</td>
                    <td className="px-4 py-3 font-mono">{r.unitsSold.toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {total > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={page}
              totalPages={totalPages}
              total={total}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function PosSalesByVendorReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const locationLabel = selectedLocationName || 'All locations';
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const { data: vendorOptsData } = usePosReportVendors();
  const { data: deptsData } = usePosReportDepartments();
  const { data: tagsData } = usePosReportTags();
  const allVendors = vendorOptsData?.data ?? [];
  const allDepartments = deptsData?.data ?? [];
  const allTags = tagsData?.data ?? [];

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [selectedVendorIds, setSelectedVendorIds] = useState<string[]>([]);
  const [selectedDeptIds, setSelectedDeptIds] = useState<string[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [applied, setApplied] = useState<{
    dateFrom: string;
    dateTo: string;
    vendorIds: string[];
    departmentIds: string[];
    tagIds: string[];
  }>({
    ...initialRange,
    vendorIds: [],
    departmentIds: [],
    tagIds: [],
  });
  const [activeVendorId, setActiveVendorId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [exporting, setExporting] = useState(false);

  // Default-select all departments and tags on first load.
  const [hasInitDepts, setHasInitDepts] = useState(false);
  if (!hasInitDepts && allDepartments.length > 0) {
    const ids = allDepartments.map((d) => d.id);
    setSelectedDeptIds(ids);
    setApplied((prev) => ({ ...prev, departmentIds: ids }));
    setHasInitDepts(true);
  }
  const [hasInitTags, setHasInitTags] = useState(false);
  if (!hasInitTags && allTags.length > 0) {
    const ids = allTags.map((t) => t.id);
    setSelectedTagIds(ids);
    setApplied((prev) => ({ ...prev, tagIds: ids }));
    setHasInitTags(true);
  }

  // Debounce search.
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedSearch(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const vendorFilterParams = {
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    locationId: selectedLocationId || undefined,
    vendorIds: applied.vendorIds.length > 0 ? applied.vendorIds.join(',') : undefined,
    departmentIds: applied.departmentIds.length > 0 ? applied.departmentIds.join(',') : undefined,
    tagIds: applied.tagIds.length > 0 ? applied.tagIds.join(',') : undefined,
  };

  const { data: vendorsData, isLoading: vendorsLoading } = usePosSalesByVendorVendors(vendorFilterParams);
  const vendors: PosSalesByVendorVendor[] = vendorsData?.data ?? [];

  // Auto-select the first vendor whenever the list changes and the current
  // selection is gone.
  useEffect(() => {
    if (vendors.length === 0) {
      if (activeVendorId !== null) setActiveVendorId(null);
      return;
    }
    if (!activeVendorId || !vendors.find((v) => v.vendorId === activeVendorId)) {
      setActiveVendorId(vendors[0].vendorId);
      setPage(1);
    }
  }, [vendors, activeVendorId]);

  const { data: productsData, isLoading: productsLoading } = usePosSalesByVendorProducts({
    vendorId: activeVendorId || undefined,
    page,
    perPage,
    search: appliedSearch || undefined,
    ...vendorFilterParams,
  });
  const products: PosSalesByVendorProduct[] = productsData?.data ?? [];
  const total = productsData?.pagination?.total ?? 0;
  const totalPages = productsData?.pagination?.totalPages ?? 1;

  const activeVendor = vendors.find((v) => v.vendorId === activeVendorId);

  const handleApply = () => {
    setApplied({
      dateFrom: dateFromInput,
      dateTo: dateToInput,
      vendorIds: selectedVendorIds,
      departmentIds: selectedDeptIds,
      tagIds: selectedTagIds,
    });
    setPage(1);
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    const deptIds = allDepartments.map((d) => d.id);
    const tagIds = allTags.map((t) => t.id);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setSelectedVendorIds([]);
    setSelectedDeptIds(deptIds);
    setSelectedTagIds(tagIds);
    setApplied({ ...r, vendorIds: [], departmentIds: deptIds, tagIds });
    setSearchInput('');
    setAppliedSearch('');
    setPage(1);
  };

  const vendorOptions = allVendors.map((v) => ({ value: v.id, label: v.name }));
  const deptOptions = allDepartments.map((d) => ({ value: d.id, label: d.name }));
  const tagOptions = allTags.map((t) => ({ value: t.id, label: t.name }));

  return (
    <div className="space-y-4">
      <PageHeader
        title="Sales By Vendor"
        description="Track detailed product revenue and stock levels for each vendor."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button
            variant="primary"
            size="sm"
            disabled={!activeVendor || exporting}
            onClick={async () => {
              if (!activeVendor) return;
              setExporting(true);
              try {
                const all: PosSalesByVendorProduct[] = [];
                const batch = 500;
                for (let p = 1; ; p++) {
                  const res = await api.get<PaginatedResponse<PosSalesByVendorProduct>>(
                    '/pos/report/sales-by-vendor/products',
                    withLocalDateRange({
                      vendorId: activeVendor.vendorId,
                      page: p,
                      perPage: batch,
                      search: appliedSearch || undefined,
                      ...vendorFilterParams,
                    }) as Record<string, string | number | undefined>,
                  );
                  all.push(...res.data);
                  if (res.data.length < batch || p >= (res.pagination?.totalPages ?? 1)) break;
                }
                const header = [
                  'Location',
                  'Vendor',
                  'Product Name',
                  'Barcode',
                  'Department',
                  'Units Sold',
                  'In Stock',
                  'Total Sales ($)',
                ];
                const body = all.map((r) => [
                  locationLabel,
                  activeVendor.vendorName,
                  r.productName,
                  r.primaryBarcode ?? '',
                  r.departmentName,
                  r.unitsSold.toFixed(2),
                  String(r.inStockQty),
                  r.totalSales.toFixed(2),
                ]);
                const slug = `sales_by_vendor_${activeVendor.vendorName}`;
                downloadCsv(reportFilename(slug, selectedLocationName), [header, ...body]);
              } finally {
                setExporting(false);
              }
            }}
          >
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware) */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <div className="min-w-[240px]">
            <MultiSelect
              options={vendorOptions}
              selected={selectedVendorIds}
              onChange={setSelectedVendorIds}
              placeholder="Vendors"
              searchPlaceholder="Find something..."
              emptyMessage="No vendors"
            />
          </div>
          <div className="min-w-[200px]">
            <MultiSelect
              options={deptOptions}
              selected={selectedDeptIds}
              onChange={setSelectedDeptIds}
              triggerLabel="Select Departments"
              searchPlaceholder="Find something..."
              emptyMessage="No departments"
            />
          </div>
          <div className="min-w-[200px]">
            <MultiSelect
              options={tagOptions}
              selected={selectedTagIds}
              onChange={setSelectedTagIds}
              triggerLabel="Select Tags"
              searchPlaceholder="Find something..."
              emptyMessage="No tags"
            />
          </div>
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        {/* Vendor rail */}
        <div className="overflow-hidden rounded-xl border border-border bg-white">
          {vendorsLoading ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : vendors.length === 0 ? (
            <EmptyState
              icon={Truck}
              title="No vendors with sales"
              description="Try a different date range."
              className="py-8"
            />
          ) : (
            <ul role="tablist" aria-label="Vendors">
              {vendors.map((v) => {
                const isSelected = v.vendorId === activeVendorId;
                return (
                  <li key={v.vendorId} className="border-b border-border/60 last:border-b-0">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={isSelected}
                      onClick={() => {
                        setActiveVendorId(v.vendorId);
                        setPage(1);
                      }}
                      className={cn(
                        'w-full px-4 py-3 text-left transition-colors',
                        isSelected
                          ? 'border-l-4 border-primary bg-primary/5'
                          : 'border-l-4 border-transparent hover:bg-muted/40',
                      )}
                    >
                      <div className="text-sm font-medium text-foreground">{v.vendorName}</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        Total Units Sold: {v.totalUnitsSold.toLocaleString()}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Product breakdown */}
        <div className="overflow-hidden rounded-xl border border-border bg-white">
          <div className="border-b border-border px-5 py-3">
            <div className="text-sm font-semibold text-foreground">
              Vendor: <span className="font-normal">{activeVendor?.vendorName ?? '—'}</span>
            </div>
          </div>

          <div className="border-b border-border px-5 py-3">
            <div className="inline-flex max-w-md items-center gap-2 rounded-md border border-input bg-white px-2.5 py-1.5 text-sm">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search"
                className="w-full bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Product Name</th>
                  <th className="px-4 py-3 font-medium">Department</th>
                  <th className="px-4 py-3 font-medium">Units Sold</th>
                  <th className="px-4 py-3 font-medium">Total Sales ($)</th>
                </tr>
              </thead>
              <tbody>
                {!activeVendor ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">
                      Select a vendor to view product sales.
                    </td>
                  </tr>
                ) : productsLoading ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                ) : products.length === 0 ? (
                  <tr>
                    <td colSpan={4}>
                      <EmptyState
                        icon={Package}
                        title="No products for this vendor"
                        description="Try changing the filters."
                      />
                    </td>
                  </tr>
                ) : (
                  products.map((p) => (
                    <tr key={p.productId} className="border-b border-border/60 last:border-b-0">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{p.productName}</div>
                        {p.primaryBarcode && <div className="text-xs text-muted-foreground">{p.primaryBarcode}</div>}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{p.departmentName}</td>
                      <td className="px-4 py-3">
                        <div className="font-mono font-medium">{p.unitsSold.toFixed(0)}</div>
                        <div className="text-xs text-muted-foreground">(In Stock: {p.inStockQty})</div>
                      </td>
                      <td className="px-4 py-3 font-mono">{formatCurrencyPlain(p.totalSales)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div className="border-t border-border">
              <DataTablePagination
                page={page}
                totalPages={totalPages}
                total={total}
                perPage={perPage}
                onPageChange={setPage}
                perPageOptions={[10, 25, 50, 100]}
                onPerPageChange={(n) => {
                  setPerPage(n);
                  setPage(1);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const PAYMENT_METHOD_OPTIONS = [
  { value: '', label: 'All Payment Types' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'ebt', label: 'EBT' },
  // POS records the cheque tender as 'cheque' (not 'check').
  { value: 'cheque', label: 'Cheque' },
  // Customer Credit Account (Pay-by-Credit) tender.
  { value: 'credit', label: 'Credit Account' },
  { value: 'gift', label: 'Gift Card' },
  { value: 'store_credit', label: 'Store Credit' },
];

function paymentMethodBadgeLabel(method: string): string {
  switch (method.toLowerCase()) {
    case 'cash':
      return 'CASH';
    // Card terminal tenders (credit/debit *card*) — distinct from the
    // Credit Account tender below.
    case 'card':
    case 'debit':
      return 'CARD';
    // Customer Credit Account (Pay-by-Credit) — must not collapse into CARD,
    // otherwise credit-account orders look like card orders in All Orders.
    case 'credit':
      return 'CREDIT';
    case 'ebt':
      return 'EBT';
    case 'cheque':
    case 'check':
      return 'CHEQUE';
    case 'gift':
      return 'GIFT';
    case 'store_credit':
      return 'STORE CREDIT';
    default:
      return method.toUpperCase();
  }
}

// EBT tenders are card-form but carry their account type in `card_type`
// ('ebt_food' / 'ebt_cash'), NOT in `method` (which stays a generic
// card/credit/debit). Detect that so EBT stops masquerading as "CARD".
function isEbtCardType(cardType: string | null | undefined): boolean {
  return cardType === 'ebt_food' || cardType === 'ebt_cash';
}

// Payment Types badge, EBT-aware: a card-form leg whose card_type is EBT
// renders as "EBT" instead of "CARD". Falls back to the method label otherwise.
function paymentTypeBadgeLabel(method: string, cardType: string | null | undefined): string {
  if (isEbtCardType(cardType)) return 'EBT';
  return paymentMethodBadgeLabel(method);
}

// Human label for the card_type (account type) column. Mirrors the receipt
// printer's mapping (order-receipt-print.tsx) so both stay consistent.
function formatCardTypeLabel(cardType: string | null | undefined): string | null {
  if (!cardType) return null;
  switch (cardType) {
    case 'credit':
      return 'CREDIT';
    case 'debit':
      return 'DEBIT';
    case 'ebt_food':
      return 'EBT FOOD';
    case 'ebt_cash':
      return 'EBT CASH';
    case 'gift':
      return 'GIFT';
    case 'check':
      return 'CHECK';
    default:
      return cardType.toUpperCase();
  }
}

const PAYMENT_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'paid', label: 'Paid' },
  { value: 'ar_payment', label: 'A/R Payment' },
  { value: 'held', label: 'Held' },
  { value: 'held_payment_check', label: 'Held — card not confirmed' },
  { value: 'card_check', label: 'Card check / refund needed' },
  { value: 'partial_refund', label: 'Partial Refund' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'recalled', label: 'Recalled' },
  { value: 'voided', label: 'Voided' },
];

// holdReason 'payment_check': the POS held the order itself because the card
// terminal couldn't confirm a payment attempt (the cashier just sees it held).
function paymentStatusLabel(status: string, holdReason?: string | null): { label: string; classes: string } {
  switch (status.toLowerCase()) {
    case 'held':
      return holdReason === 'payment_check'
        ? { label: 'Held — card not confirmed', classes: 'bg-amber-100 text-amber-800' }
        : { label: 'Held', classes: 'bg-gray-100 text-gray-700' };
    case 'paid':
    case 'completed':
      return { label: 'Paid', classes: 'bg-green-100 text-green-700' };
    case 'ar_payment':
      return { label: 'A/R Payment', classes: 'bg-indigo-100 text-indigo-700' };
    case 'refunded':
      return { label: 'Refunded', classes: 'bg-blue-100 text-blue-700' };
    case 'voided':
      return { label: 'Voided', classes: 'bg-red-100 text-red-700' };
    default:
      return { label: status, classes: 'bg-gray-100 text-gray-700' };
  }
}

// cardCheck: the order was paid another way while an earlier card attempt on it
// was still open — 'card_check' (never confirmed) or 'refund_needed' (it turned
// out charged). Details and the Mark Refunded action live in Card Attempts to Check.
function cardCheckLabel(cardCheck?: string | null): { label: string; classes: string } | null {
  if (cardCheck === 'refund_needed') return { label: 'Refund needed', classes: 'bg-red-100 text-red-700' };
  if (cardCheck === 'card_check') return { label: 'Card check', classes: 'bg-amber-100 text-amber-800' };
  return null;
}

function CardCheckBadge({ cardCheck }: { cardCheck?: string | null }) {
  const cc = cardCheckLabel(cardCheck);
  if (!cc) return null;
  return (
    <span
      title="An earlier card attempt on this order is open — see Reports › Orders › Card Attempts to Check"
      className={cn('ml-1 inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium', cc.classes)}
    >
      {cc.label}
    </span>
  );
}

function OrderField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-semibold text-foreground">{value}</div>
    </div>
  );
}

type OrderModalTab = 'basic' | 'lines' | 'payment' | 'refund' | 'reward';

function OrderDetailsModal({ open, orderId, onClose }: { open: boolean; orderId: string | null; onClose: () => void }) {
  const [tab, setTab] = useState<OrderModalTab>('basic');
  const { data, isLoading } = usePosOrderDetail(orderId);
  const order = data?.data;

  const ps = order ? paymentStatusLabel(order.status, order.holdReason) : { label: '—', classes: '' };
  const total = order ? parseFloat(order.total) : 0;
  const subtotalNum = order ? parseFloat(order.subtotal) : 0;
  const taxNum = order ? parseFloat(order.tax) : 0;
  const redemptionNum = order ? parseFloat(order.redemptionDiscount ?? '0') : 0;
  // "Order Discount" is the non-redemption portion of the combined discount;
  // the loyalty redemption is broken out into its own field below.
  const discountNum = order ? parseFloat(order.discount) - redemptionNum : 0;

  const tabs: { id: OrderModalTab; label: string }[] = [
    { id: 'basic', label: 'Basic Details' },
    { id: 'lines', label: 'Lines Details' },
    { id: 'payment', label: 'Payment Details' },
    { id: 'refund', label: 'Refund Details' },
    { id: 'reward', label: 'Reward Details' },
  ];

  return (
    <Modal open={open} onClose={onClose} title="Order Details" widthClass="max-w-[1100px]">
      {isLoading || !order ? (
        <div className="flex h-40 items-center justify-center text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-5">
          {/* Header strip */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-3xl font-semibold text-foreground">${formatCurrencyPlain(total)}</span>
              <span
                className={cn('inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium', ps.classes)}
              >
                ● {ps.label}
              </span>
              <CardCheckBadge cardCheck={order.cardCheck} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <OrderField label="Order ID" value={`#${order.dailyOrderNumber ?? order.id.slice(0, 8)}`} />
              <OrderField label="Order Date" value={formatPosDateTime(order.createdAt)} />
              <OrderField label="Created by" value={order.employeeName ?? '—'} />
              <OrderField label="Location" value={order.locationName ?? '—'} />
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-border">
            <div className="flex flex-wrap" role="tablist" aria-label="Order detail tabs">
              {tabs.map((t) => {
                const isSelected = tab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    onClick={() => setTab(t.id)}
                    className={cn(
                      'relative px-4 py-2.5 text-sm font-medium',
                      isSelected ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {t.label}
                    {isSelected && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab content */}
          {tab === 'basic' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                  <ClipboardList className="h-4 w-4 text-primary" />
                  Basic Details
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <OrderField label="Local ID/Order No" value={order.id} />
                  <OrderField label="Order Note" value={order.note ?? 'N.A'} />
                  <OrderField label="POS Station ID" value={order.stationName ?? order.registerId ?? 'N.A'} />
                  <OrderField label="Reward Customer Id" value="N.A" />
                </div>
              </div>
              <div className="rounded-lg border border-border p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                  <CreditCard className="h-4 w-4 text-primary" />
                  Payment Metadata
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <OrderField
                    label="Payment Gateway"
                    value={order.payments[0]?.terminalRef || order.payments[0]?.authCode || 'N.A'}
                  />
                  <OrderField label="Merchant Fee" value={`$${formatCurrencyPlain(0)}`} />
                  <OrderField
                    label={order.customFee1Label || 'Custom Fee 1'}
                    value={`$${formatCurrencyPlain(parseFloat(order.customFee1Total ?? '0'))}`}
                  />
                  <OrderField
                    label={order.customFee2Label || 'Custom Fee 2'}
                    value={`$${formatCurrencyPlain(parseFloat(order.customFee2Total ?? '0'))}`}
                  />
                  <OrderField label="Order Total" value={`$${formatCurrencyPlain(total)}`} />
                  <OrderField label="Order Discount" value={`$${formatCurrencyPlain(discountNum)}`} />
                  <OrderField label="Rewards Redeemed" value={`$${formatCurrencyPlain(redemptionNum)}`} />
                  <OrderField label="Points Redeemed" value={`${order.pointsRedeemed ?? 0} pts`} />
                  <OrderField label="Points Balance" value={`${order.pointsBalance ?? 0} pts`} />
                  <OrderField
                    label="Total Tips Amount"
                    value={`$${formatCurrencyPlain(parseFloat(order.tipAmount ?? '0'))}`}
                  />
                  <OrderField label="Cash Discount" value={`$${formatCurrencyPlain(0)}`} />
                  <OrderField label="Promotion Applied" value={order.promotionApplied ? 'Yes' : 'No'} />
                  <OrderField label="Discount Reason" value="N.A" />
                  <OrderField label="Tax Exempt" value="No" />
                  <OrderField label="EBT Tax Exemption" value="No" />
                  <OrderField label="EBT Tax Saved" value={`$${formatCurrencyPlain(0)}`} />
                </div>
              </div>
            </div>
          )}

          {tab === 'lines' && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
              <div className="overflow-hidden rounded-lg border border-border">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                        <th className="px-4 py-3 font-medium">Sr No</th>
                        <th className="px-4 py-3 font-medium">Product Name</th>
                        <th className="px-4 py-3 font-medium">Quantity</th>
                        <th className="px-4 py-3 font-medium">Unit</th>
                        <th className="px-4 py-3 font-medium">Sale Price ($)</th>
                        <th className="px-4 py-3 font-medium">Sub Total ($)</th>
                        <th className="px-4 py-3 font-medium">Tax ($)</th>
                        <th className="px-4 py-3 font-medium">Final Total ($)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.length === 0 ? (
                        <tr>
                          <td colSpan={8}>
                            <EmptyState
                              icon={ShoppingCart}
                              title="No items"
                              description="This order has no line items."
                            />
                          </td>
                        </tr>
                      ) : (
                        order.items.map((it: PosOrderItem, idx: number) => (
                          <tr key={it.id} className="border-b border-border/60 last:border-b-0">
                            <td className="px-4 py-3 align-top text-muted-foreground">{idx + 1}</td>
                            <td className="px-4 py-3 align-top">
                              <div className="font-medium text-foreground">{it.productName}</div>
                              {it.primaryBarcode && (
                                <div className="text-xs text-muted-foreground">{it.primaryBarcode}</div>
                              )}
                              {it.note && <div className="text-xs italic text-muted-foreground">Note: {it.note}</div>}
                            </td>
                            <td className="px-4 py-3 align-top font-mono">{it.qty.toFixed(2)}</td>
                            <td className="px-4 py-3 align-top capitalize">{it.unitName}</td>
                            <td className="px-4 py-3 align-top font-mono">{it.unitPrice.toFixed(2)}</td>
                            <td className="px-4 py-3 align-top font-mono">{it.lineTotal.toFixed(2)}</td>
                            <td className="px-4 py-3 align-top font-mono">{it.tax.toFixed(2)}</td>
                            <td className="px-4 py-3 align-top font-mono">{(it.lineTotal + it.tax).toFixed(2)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Summary panel */}
              <div className="self-start rounded-lg border border-border p-4">
                <div className="mb-3 text-sm font-semibold text-foreground">Summary</div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Total Quantity</span>
                    <span className="font-mono text-foreground">
                      {order.items.reduce((s, i) => s + i.qty, 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Total Unit Quantity</span>
                    <span className="font-mono text-foreground">0.00</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Total Sale Price</span>
                    <span className="font-mono text-foreground">
                      ${formatCurrencyPlain(order.items.reduce((s, i) => s + i.unitPrice, 0))}
                    </span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Total Tax</span>
                    <span className="font-mono text-foreground">${formatCurrencyPlain(taxNum)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Total Fee</span>
                    <span className="font-mono text-foreground">
                      $
                      {formatCurrencyPlain(
                        parseFloat(order.customFee1Total ?? '0') + parseFloat(order.customFee2Total ?? '0'),
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-border pt-1.5 font-medium text-foreground">
                    <span>Sub Total</span>
                    <span className="font-mono">${formatCurrencyPlain(subtotalNum)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Total Discounts</span>
                    <span className="font-mono text-foreground">${formatCurrencyPlain(discountNum)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Total EBT Tax Exemptions</span>
                    <span className="font-mono text-foreground">$0.00</span>
                  </div>
                  <div className="flex justify-between border-t border-border pt-1.5 font-medium text-foreground">
                    <span>Order Total</span>
                    <span className="font-mono">${formatCurrencyPlain(total)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === 'payment' && (
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="border-b border-border px-4 py-3 text-sm font-semibold text-foreground">
                <span className="mr-2 inline-block">💲</span>Payment Details
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                      <th className="px-4 py-3 font-medium">Sr No</th>
                      <th className="px-4 py-3 font-medium">Amount ($)</th>
                      <th className="px-4 py-3 font-medium">External Reference</th>
                      {/* This column has always rendered p.authCode; it was
                          mislabelled "Payment Gateway" (there is no gateway
                          field on a POS payment). */}
                      <th className="px-4 py-3 font-medium">Auth Code</th>
                      <th className="px-4 py-3 font-medium">Type</th>
                      <th className="px-4 py-3 font-medium">Card Last 4</th>
                      <th className="px-4 py-3 font-medium">Aid</th>
                      <th className="px-4 py-3 font-medium">Tip Amount ($)</th>
                      <th className="px-4 py-3 font-medium">Merchant Fee ($)</th>
                      <th className="px-4 py-3 font-medium">Pax Response</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.payments.length === 0 ? (
                      <tr>
                        <td colSpan={10}>
                          <EmptyState
                            icon={CreditCard}
                            title="No payments captured"
                            description="Payments taken on this order will appear here."
                          />
                        </td>
                      </tr>
                    ) : (
                      order.payments.map((p, idx) => (
                        <tr key={p.id} className="border-b border-border/60 last:border-b-0">
                          <td className="px-4 py-3">{idx + 1}</td>
                          <td className="px-4 py-3">
                            <div className="font-mono font-medium text-foreground">
                              {parseFloat(p.amount).toFixed(2)}
                            </div>
                            <div className="text-xs text-muted-foreground">On {formatPosDateTime(order.createdAt)}</div>
                          </td>
                          <td className="px-4 py-3 font-mono">{p.terminalRef ?? '—'}</td>
                          <td className="px-4 py-3 font-mono">{p.authCode ?? '—'}</td>
                          <td className="px-4 py-3">{paymentMethodBadgeLabel(p.method)}</td>
                          {/* Cash and other non-card tenders carry no PAN, so
                              they render the same em dash as a card that came
                              back without one. */}
                          <td className="px-4 py-3 font-mono">{p.cardLastFour ?? '—'}</td>
                          <td className="px-4 py-3 text-muted-foreground">
                            <div>N.A.</div>
                            <div className="text-xs">N.A.</div>
                          </td>
                          <td className="px-4 py-3 font-mono">0.00</td>
                          <td className="px-4 py-3 font-mono">0.00</td>
                          <td className="px-4 py-3 text-muted-foreground">N.A.</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'refund' &&
            (() => {
              const refunds = order.refunds ?? [];
              const isRefund = !!order.originalTransactionId;
              if (refunds.length === 0 && !isRefund) {
                return (
                  <div className="rounded-lg border border-dashed border-border">
                    <EmptyState icon={RotateCcw} title="No refunds" description="This order has not been refunded." />
                  </div>
                );
              }
              return (
                <div className="space-y-4">
                  {/* When this order IS a refund, show what it reversed. */}
                  {isRefund && (
                    <div className="rounded-lg border border-border p-4">
                      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                        <span className="inline-block">↩️</span> Refund Summary
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <OrderField
                          label="Refund of Order"
                          value={<span className="font-mono text-xs">{order.originalTransactionId?.slice(0, 18)}</span>}
                        />
                        <OrderField label="Refunded Amount" value={`$${formatCurrencyPlain(Math.abs(total))}`} />
                        <OrderField label="Refund Status" value={ps.label} />
                        <OrderField label="Refund Date" value={formatPosDateTime(order.createdAt)} />
                        {/* Same `note` column as the Reason cell below — this
                            side of the pair is the refund's own row. */}
                        <OrderField label="Refund Reason" value={order.note?.trim() || '—'} />
                      </div>
                    </div>
                  )}
                  {/* When this order is a sale that was refunded, list those refunds. */}
                  {refunds.length > 0 && (
                    <div className="overflow-hidden rounded-lg border border-border">
                      <div className="border-b border-border px-4 py-3 text-sm font-semibold text-foreground">
                        <span className="mr-2 inline-block">↩️</span>Refunds against this order
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                              <th className="px-4 py-3 font-medium">Refund Order</th>
                              <th className="px-4 py-3 font-medium">Date</th>
                              <th className="px-4 py-3 font-medium">Status</th>
                              <th className="px-4 py-3 font-medium">Reason</th>
                              <th className="px-4 py-3 font-medium">Amount ($)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {refunds.map((r) => (
                              <tr key={r.id} className="border-b border-border/60 last:border-b-0">
                                <td className="px-4 py-3 font-mono text-xs">
                                  #{r.dailyOrderNumber ?? r.id.slice(0, 8)}
                                </td>
                                <td className="px-4 py-3">{formatPosDateTime(r.createdAt)}</td>
                                <td className="px-4 py-3">{paymentStatusLabel(r.status).label}</td>
                                <td className="px-4 py-3">{r.note?.trim() || '—'}</td>
                                <td className="px-4 py-3 font-mono">{formatCurrencyPlain(r.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          {tab === 'reward' &&
            (() => {
              const pointsRedeemed = order.pointsRedeemed ?? 0;
              const pointsBalance = order.pointsBalance ?? 0;
              const rewardValue = parseFloat(order.redemptionDiscount ?? '0');
              const hasReward = !!order.customerId || pointsRedeemed > 0 || rewardValue > 0;
              if (!hasReward) {
                return (
                  <div className="rounded-lg border border-dashed border-border">
                    <EmptyState
                      icon={Gift}
                      title="No reward activity"
                      description="No customer or reward points were attached to this order."
                    />
                  </div>
                );
              }
              return (
                <div className="rounded-lg border border-border p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                    <span className="inline-block">🎁</span> Reward Details
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <OrderField label="Reward Customer" value={order.customerName ?? 'N.A'} />
                    <OrderField label="Customer Phone" value={order.customerPhone ?? 'N.A'} />
                    <OrderField label="Points Redeemed" value={`${pointsRedeemed} pts`} />
                    <OrderField label="Reward Value Redeemed" value={`$${formatCurrencyPlain(rewardValue)}`} />
                    <OrderField label="Points Balance (after order)" value={`${pointsBalance} pts`} />
                    <OrderField
                      label="Loyalty Points (current)"
                      value={order.customerLoyaltyPoints != null ? `${order.customerLoyaltyPoints} pts` : 'N.A'}
                    />
                  </div>
                </div>
              );
            })()}
        </div>
      )}
    </Modal>
  );
}

// Height the pinned pagination bar under the All Orders table occupies, plus the
// gap above it — the table's max-height is computed to leave exactly this free
// (same figure as All Products, whose footer this page mirrors).
const ALL_ORDERS_PAGINATION_BAR_HEIGHT = 86;

// Header cells freeze individually (not the <thead>) with an opaque background,
// so rows scrolling underneath don't show through — same approach as DataTable.
const ALL_ORDERS_TH = 'sticky top-0 z-20 bg-gray-50 px-4 py-3 font-medium';

function PosAllOrdersReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();

  // The table scrolls inside itself rather than stretching the page, so the
  // header stays frozen and the horizontal scrollbar stays on screen.
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const tableMaxHeight = useFillViewportHeight(tableScrollRef, ALL_ORDERS_PAGINATION_BAR_HEIGHT);

  // Follows the global reporting timezone (set via the ReportingTimeZoneBar at
  // the top of Reports; defaults to the business's Pacific zone). Threaded
  // through the pickers, query, and export so all stay in sync — mirrors the
  // Refunds report.
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [paymentMethodInput, setPaymentMethodInput] = useState<string[]>([]);
  const [statusInput, setStatusInput] = useState<string[]>([]);
  const [registerInput, setRegisterInput] = useState<string[]>([]);
  const [applied, setApplied] = useState<{
    dateFrom: string;
    dateTo: string;
    paymentMethods: string[];
    statuses: string[];
    registerIds: string[];
  }>({
    ...initialRange,
    paymentMethods: [],
    statuses: [],
    registerIds: [],
  });
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Register/station options + id→name lookup for the filter dropdown and the
  // Register column (the transactions endpoint returns registerId only).
  const { data: stationsData } = usePosStations({ locationId: selectedLocationId || undefined });
  const stationOptions = (stationsData?.data ?? []).map((s) => ({ value: s.registerId, label: s.stationName }));
  const stationNameById = new Map(stationOptions.map((s) => [s.value, s.label]));
  const registerLabel = (registerId: string | null) =>
    registerId ? (stationNameById.get(registerId) ?? `Reg ${registerId.slice(0, 6)}`) : '—';

  // Which payment rows to render for an order. With a payment-type filter
  // active, a split-tender order shows only the matching tender's row(s) — so a
  // Cash filter no longer also lists the card half of a split (and vice versa).
  // Mirrors the API filter: raw method equality, except 'ebt' — which isn't a
  // distinct method but a card_type ('ebt_food' / 'ebt_cash') — so it matches on
  // that instead. Falls back to all payments if none match (shouldn't happen —
  // the API only returns orders that have the matching tender).
  const rowPaymentsFor = (t: PosTransaction) => {
    const all = t.payments ?? [];
    if (!applied.paymentMethods.length) return all;
    const matched = all.filter((p) =>
      applied.paymentMethods.some((m) => (m === 'ebt' ? isEbtCardType(p.cardType) : p.method === m)),
    );
    return matched.length ? matched : all;
  };

  // Debounce search.
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const { data: txData, isLoading: txLoading } = usePosTransactions(
    {
      page,
      perPage,
      locationId: selectedLocationId || undefined,
      dateFrom: applied.dateFrom || undefined,
      dateTo: applied.dateTo || undefined,
      search: appliedSearch || undefined,
      paymentMethod: applied.paymentMethods.join(',') || undefined,
      status: applied.statuses.join(',') || undefined,
      registerId: applied.registerIds.join(',') || undefined,
    },
    reportTz,
  );
  const transactions: PosTransaction[] = txData?.data ?? [];
  const pagination = txData?.pagination;

  const handleApply = () => {
    setApplied({
      dateFrom: dateFromInput,
      dateTo: dateToInput,
      paymentMethods: paymentMethodInput,
      statuses: statusInput,
      registerIds: registerInput,
    });
    setPage(1);
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setPaymentMethodInput([]);
    setStatusInput([]);
    setRegisterInput([]);
    setApplied({ ...r, paymentMethods: [], statuses: [], registerIds: [] });
    setSearch('');
    setAppliedSearch('');
    setPage(1);
  };

  const downloadOrderDetails = (txn: PosTransaction) => {
    // Open the receipt in a named tab — second click on the same order reuses
    // the tab. `?download=1` makes the page auto-generate the receipt PDF and
    // download it directly (no print dialog); a "Download PDF" button on the
    // page re-triggers it.
    window.open(`#/reports/orders/${txn.id}/print?download=1`, `order-print-${txn.id}`);
  };

  return (
    <div className="space-y-4">
      <PageHeader title="All Orders" description="See a full list of every order placed in your store." onBack={onBack}>
        <PosExportOnly>
          <Button
            variant="primary"
            size="sm"
            disabled={!pagination?.total || exporting}
            onClick={async () => {
              setExporting(true);
              try {
                const all: PosTransaction[] = [];
                const batch = 500;
                for (let p = 1; ; p++) {
                  const res = await api.get<PaginatedResponse<PosTransaction>>(
                    '/pos/transactions',
                    withLocalDateRange(
                      {
                        page: p,
                        perPage: batch,
                        locationId: selectedLocationId || undefined,
                        dateFrom: applied.dateFrom || undefined,
                        dateTo: applied.dateTo || undefined,
                        search: appliedSearch || undefined,
                        paymentMethod: applied.paymentMethods.join(',') || undefined,
                        status: applied.statuses.join(',') || undefined,
                        registerId: applied.registerIds.join(',') || undefined,
                      },
                      reportTz,
                    ) as Record<string, string | number | undefined>,
                  );
                  all.push(...res.data);
                  if (res.data.length < batch || p >= (res.pagination?.totalPages ?? 1)) break;
                }
                const header = [
                  'Order ID',
                  'Local ID/Order No.',
                  'Register',
                  'Customer Phone',
                  'Custom Fee 1 ($)',
                  'Custom Fee 2 ($)',
                  'Tip ($)',
                  'Tax ($)',
                  'Discount ($)',
                  'Total ($)',
                  'Payment Status',
                  'Payment Types',
                  'Auth Code',
                  'Card Last 4',
                  'Card Brand',
                  'Card Type',
                  'Order Date',
                ];
                // Split-tender orders export one row per payment (matching the
                // table); each row repeats the order identity so it stands alone,
                // and order-level fees/tip appear once per order to avoid
                // double-counting. Under a payment-type filter, only the matching
                // tender's rows are exported.
                const body = all.flatMap((t) => {
                  const allPayments = t.payments ?? [];
                  const isSplit = allPayments.length >= 2;
                  const row = (
                    amount: string,
                    method: string,
                    cardBrand: string,
                    cardType: string,
                    authCode: string,
                    cardLastFour: string,
                    first: boolean,
                  ) => [
                    t.dailyOrderNumber ?? '',
                    t.id,
                    registerLabel(t.registerId),
                    t.customerPhone ?? '',
                    first ? parseFloat(t.customFee1Total ?? '0').toFixed(2) : '',
                    first ? parseFloat(t.customFee2Total ?? '0').toFixed(2) : '',
                    first ? parseFloat(t.tipAmount ?? '0').toFixed(2) : '',
                    first ? parseFloat(t.tax ?? '0').toFixed(2) : '',
                    first ? parseFloat(t.discount ?? '0').toFixed(2) : '',
                    amount,
                    [paymentStatusLabel(t.status, t.holdReason).label, cardCheckLabel(t.cardCheck)?.label]
                      .filter(Boolean)
                      .join(' — '),
                    method,
                    authCode,
                    cardLastFour,
                    cardBrand,
                    cardType,
                    formatPosDateTime(t.createdAt),
                  ];
                  if (isSplit) {
                    return rowPaymentsFor(t).map((p, i) =>
                      row(
                        parseFloat(p.amount).toFixed(2),
                        paymentTypeBadgeLabel(p.method, p.cardType),
                        (p.cardBrand ?? '').toUpperCase(),
                        formatCardTypeLabel(p.cardType) ?? '',
                        p.authCode ?? '',
                        p.cardLastFour ?? '',
                        i === 0,
                      ),
                    );
                  }
                  // Refunds have no payments of their own — fall back to the
                  // original sale's tender for the metadata columns only (the
                  // amount stays the refund's own total).
                  const p = allPayments[0] ?? t.originalPayments?.[0];
                  return [
                    row(
                      parseFloat(t.total).toFixed(2),
                      p ? paymentTypeBadgeLabel(p.method, p.cardType) : '—',
                      (p?.cardBrand ?? '').toUpperCase(),
                      formatCardTypeLabel(p?.cardType) ?? '',
                      p?.authCode ?? '',
                      p?.cardLastFour ?? '',
                      true,
                    ),
                  ];
                });
                downloadCsv(reportFilename('all_orders_report', selectedLocationName), [header, ...body]);
              } finally {
                setExporting(false);
              }
            }}
          >
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware),
          matching the Refunds report. */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Filters</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Payment Types</label>
            <div className="min-w-[200px]">
              <MultiSelect
                options={PAYMENT_METHOD_OPTIONS.filter((o) => o.value)}
                selected={paymentMethodInput}
                onChange={setPaymentMethodInput}
                triggerLabel="Payment Types"
                searchPlaceholder="Find a payment type..."
                emptyMessage="No payment types"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Statuses</label>
            <div className="min-w-[200px]">
              <MultiSelect
                options={PAYMENT_STATUS_OPTIONS.filter((o) => o.value)}
                selected={statusInput}
                onChange={setStatusInput}
                triggerLabel="Statuses"
                searchPlaceholder="Find a status..."
                emptyMessage="No statuses"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Registers</label>
            <div className="min-w-[200px]">
              <MultiSelect
                options={stationOptions}
                selected={registerInput}
                onChange={setRegisterInput}
                triggerLabel="Registers"
                searchPlaceholder="Find a register..."
                emptyMessage="No registers"
              />
            </div>
          </div>
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          {/* "Submit", not "Apply" — the date picker has its own Apply, which
              only commits the range into the draft. This button is the one that
              actually runs the query, so it needs a distinct label. */}
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="space-y-1">
        <div className="inline-flex max-w-sm items-center gap-2 rounded-md border border-input bg-white px-2.5 py-1.5 text-sm">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search orders..."
            className="w-full bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Search by Order ID, Order No., Customer Phone, Card Last 4, Auth Code, or Total Amount.
        </p>
      </div>

      {/* Orders table */}
      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div
          ref={tableScrollRef}
          style={tableMaxHeight ? { maxHeight: tableMaxHeight } : undefined}
          className="table-scroll overflow-auto"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                <th className={ALL_ORDERS_TH}>Order ID</th>
                <th className={ALL_ORDERS_TH}>Local ID/Order No.</th>
                <th className={ALL_ORDERS_TH}>Register</th>
                <th className={ALL_ORDERS_TH}>Customer Phone</th>
                <th className={ALL_ORDERS_TH}>Custom Fee 1 ($)</th>
                <th className={ALL_ORDERS_TH}>Custom Fee 2 ($)</th>
                <th className={ALL_ORDERS_TH}>Tip ($)</th>
                <th className={ALL_ORDERS_TH}>Tax ($)</th>
                <th className={ALL_ORDERS_TH}>Discount ($)</th>
                <th className={ALL_ORDERS_TH}>Total ($)</th>
                <th className={ALL_ORDERS_TH}>Payment Status</th>
                <th className={ALL_ORDERS_TH}>Payment Types</th>
                <th className={ALL_ORDERS_TH}>Auth Code</th>
                <th className={ALL_ORDERS_TH}>Card Last 4</th>
                <th className={ALL_ORDERS_TH}>Card Brand</th>
                <th className={ALL_ORDERS_TH}>Card Type</th>
                <th className={ALL_ORDERS_TH}>Order Date</th>
                {/* Actions column is pinned to the right so it stays reachable
                    while the data columns scroll horizontally. */}
                <th className="sticky top-0 right-0 z-30 border-l border-border bg-muted px-4 py-3 text-right font-medium shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {txLoading ? (
                <tr>
                  <td colSpan={18} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={18}>
                    <EmptyState icon={ShoppingCart} title="No orders" description="Try a different date range." />
                  </td>
                </tr>
              ) : (
                transactions.flatMap((txn) => {
                  const ps = paymentStatusLabel(txn.status, txn.holdReason);
                  const displayId = txn.dailyOrderNumber ?? txn.id.slice(0, 8);
                  const allPayments = txn.payments ?? [];
                  // Split-tender orders (e.g. part cash, part card) get one row per
                  // payment, each showing that method's amount. Single/zero-payment
                  // orders keep one row showing the order total. Under a payment-type
                  // filter, only the matching tender's rows are shown.
                  const isSplit = allPayments.length >= 2;
                  const splitRows = isSplit
                    ? rowPaymentsFor(txn).map((p) => ({
                        rowKey: `${txn.id}-${p.id}`,
                        method: paymentTypeBadgeLabel(p.method, p.cardType),
                        amount: parseFloat(p.amount),
                        cardBrand: p.cardBrand,
                        cardType: p.cardType,
                        authCode: p.authCode,
                        cardLastFour: p.cardLastFour,
                      }))
                    : [
                        (() => {
                          // Refunds carry no payments of their own — borrow the
                          // original sale's tender for the metadata columns only.
                          const meta = allPayments[0] ?? txn.originalPayments?.[0];
                          return {
                            rowKey: txn.id,
                            method: meta ? paymentTypeBadgeLabel(meta.method, meta.cardType) : null,
                            // Grand total: total (subtotal+tax+fees) + tip.
                            amount: parseFloat(txn.total) + parseFloat(txn.tipAmount ?? '0'),
                            cardBrand: meta?.cardBrand ?? null,
                            cardType: meta?.cardType ?? null,
                            authCode: meta?.authCode ?? null,
                            cardLastFour: meta?.cardLastFour ?? null,
                          };
                        })(),
                      ];
                  return splitRows.map((row, i) => {
                    const first = i === 0;
                    const last = i === splitRows.length - 1;
                    return (
                      <tr key={row.rowKey} className={cn('border-border/60', last && 'border-b last:border-b-0')}>
                        <td className="px-4 py-3 font-medium text-primary align-top">{displayId}</td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground align-top">
                          {/* Numeric order numbers (new POS builds) show in full;
                              legacy UUID ids keep the truncated preview. */}
                          {txn.id.includes('-') ? txn.id.slice(0, 18) : txn.id}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap align-top">{registerLabel(txn.registerId)}</td>
                        <td className="px-4 py-3 whitespace-nowrap align-top">{txn.customerPhone || '—'}</td>
                        {/* Order-level amounts (fees/tip) show once per order to
                            avoid double-counting across split-tender rows. */}
                        <td className="px-4 py-3 font-mono align-top">
                          {first ? formatCurrencyPlain(parseFloat(txn.customFee1Total ?? '0')) : ''}
                        </td>
                        <td className="px-4 py-3 font-mono align-top">
                          {first ? formatCurrencyPlain(parseFloat(txn.customFee2Total ?? '0')) : ''}
                        </td>
                        <td className="px-4 py-3 font-mono align-top">
                          {first ? formatCurrencyPlain(parseFloat(txn.tipAmount ?? '0')) : ''}
                        </td>
                        <td className="px-4 py-3 font-mono align-top">
                          {first ? formatCurrencyPlain(parseFloat(txn.tax ?? '0')) : ''}
                        </td>
                        <td className="px-4 py-3 font-mono align-top">
                          {first ? formatCurrencyPlain(parseFloat(txn.discount ?? '0')) : ''}
                        </td>
                        <td className="px-4 py-3 font-mono align-top">{formatCurrencyPlain(row.amount)}</td>
                        <td className="px-4 py-3 align-top">
                          <span
                            className={cn(
                              'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                              ps.classes,
                            )}
                          >
                            {ps.label}
                          </span>
                          {first && <CardCheckBadge cardCheck={txn.cardCheck} />}
                        </td>
                        <td className="px-4 py-3 align-top">
                          {row.method ? (
                            <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                              {row.method}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs whitespace-nowrap align-top">
                          {row.authCode || '—'}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs whitespace-nowrap align-top">
                          {row.cardLastFour ? `•••• ${row.cardLastFour}` : '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap align-top">
                          {row.cardBrand ? (
                            <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase text-slate-700">
                              {row.cardBrand}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap align-top">
                          {formatCardTypeLabel(row.cardType) ? (
                            <span
                              className={cn(
                                'inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold',
                                isEbtCardType(row.cardType)
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : 'bg-slate-100 text-slate-700',
                              )}
                            >
                              {formatCardTypeLabel(row.cardType)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground align-top">
                          {formatPosDateTime(txn.createdAt)}
                        </td>
                        {/* Pinned to the right (mirrors the header) so the
                            actions stay visible without horizontal scrolling. */}
                        <td className="sticky right-0 z-10 border-l border-border bg-white px-4 py-3 align-top shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)]">
                          {first ? (
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => setDetailOrderId(txn.id)}
                                aria-label="View order details"
                                className="rounded-md border border-border p-1.5 text-primary hover:bg-primary/5"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <PosExportOnly>
                                <button
                                  type="button"
                                  onClick={() => downloadOrderDetails(txn)}
                                  aria-label="Download order details"
                                  className="rounded-md border border-border p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              </PosExportOnly>
                            </div>
                          ) : (
                            ''
                          )}
                        </td>
                      </tr>
                    );
                  });
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination pinned to the bottom of the viewport, matching All Products,
          so paging controls stay reachable however long the page gets. */}
      {pagination && pagination.total > 0 && (
        <div className="sticky -bottom-4 z-30 -mx-4 border-t border-border bg-white px-4 shadow-[0_-2px_8px_rgba(15,23,42,0.06)] lg:-bottom-6 lg:-mx-6 lg:px-6">
          <DataTablePagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            perPage={pagination.perPage}
            onPageChange={setPage}
            perPageOptions={[10, 25, 50, 100]}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
        </div>
      )}

      <OrderDetailsModal
        key={detailOrderId ?? 'closed'}
        open={detailOrderId !== null}
        orderId={detailOrderId}
        onClose={() => setDetailOrderId(null)}
      />
    </div>
  );
}

/** A report's wall-clock "YYYY-MM-DDTHH:mm" input as "Sep 21, 2026, 11:59 PM",
 *  formatted as-is — the value is already in the reporting zone. */
function formatWallClock(value: string): string {
  const d = new Date(`${value.length === 10 ? `${value}T00:00` : value}:00Z`);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

/** One column of the Refunds summary: its range totals and refund count. */
interface RefundSummaryColumn {
  label: string;
  count: number | undefined;
  totals: PosRefundsTotals | undefined;
  byMethod?: PosRefundsMethodTotal[];
}

/** Tender label for the refunds breakdown; 'none' is a refund with no tender. */
function refundMethodLabel(method: string): string {
  return method === 'none' ? 'No Tender Recorded' : paymentMethodLabel(method);
}

/**
 * Totals for the applied range, With Order and Without Order side by side
 * plus both together. Figures come from the API's range totals (every row in
 * the filter, not just the page on screen) and keep the table's sign, so a
 * refund reads "- $5.59" here just as it does in the rows below.
 */
function PosRefundsSummary({ rangeLabel, columns }: { rangeLabel: string; columns: RefundSummaryColumn[] }) {
  const loaded = columns.every((c) => c.totals && c.count !== undefined);
  const all: RefundSummaryColumn = {
    label: 'All Refunds',
    count: loaded ? columns.reduce((n, c) => n + (c.count ?? 0), 0) : undefined,
    totals: loaded
      ? (['refundAmount', 'refundTaxFees', 'refundTotal', 'refundTips'] as const).reduce(
          (acc, key) => ({
            ...acc,
            [key]: columns.reduce((cents, c) => cents + Math.round((c.totals?.[key] ?? 0) * 100), 0) / 100,
          }),
          {} as PosRefundsTotals,
        )
      : undefined,
  };
  // Tender rows across both tabs, largest refunded first. Hidden against an
  // API that doesn't send byMethod yet.
  const methodCols = columns.map((c) => new Map((c.byMethod ?? []).map((m) => [m.method, m])));
  const showMethods = loaded && columns.every((c) => c.byMethod);
  const methodRows = showMethods
    ? Array.from(new Set(columns.flatMap((c) => (c.byMethod ?? []).map((m) => m.method))))
        .map((method) => {
          const cells = methodCols.map((m) => m.get(method));
          const allCents = cells.reduce((sum, m) => sum + Math.round((m?.total ?? 0) * 100), 0);
          const allCount = cells.reduce((n, m) => n + (m?.count ?? 0), 0);
          return { method, cells, all: { method, total: allCents / 100, count: allCount } };
        })
        .sort((a, b) => Math.abs(b.all.total) - Math.abs(a.all.total))
    : [];
  const methodCell = (m: PosRefundsMethodTotal | undefined) =>
    m ? (
      <>
        <span className="font-medium">{formatCurrency(m.total)}</span>
        <span className="ml-1.5 text-xs text-muted-foreground">({m.count})</span>
      </>
    ) : (
      <span className="text-muted-foreground">—</span>
    );

  const cols = [...columns, all];
  const money = (c: RefundSummaryColumn, key: keyof PosRefundsTotals) =>
    c.totals ? formatCurrency(c.totals[key]) : '...';
  const figures: { label: string; key: keyof PosRefundsTotals }[] = [
    { label: 'Refund Amount', key: 'refundAmount' },
    { label: 'Refund Tax + Fees', key: 'refundTaxFees' },
  ];

  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">Summary</h3>
        <span className="text-xs text-muted-foreground">{rangeLabel}</span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {cols.map((c, i) => (
          <div
            key={c.label}
            className={cn(
              'rounded-lg border p-4',
              i === cols.length - 1 ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/30',
            )}
          >
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{c.label}</p>
              <p className="text-xs text-muted-foreground">
                {c.count === undefined ? '...' : `${c.count} refund${c.count === 1 ? '' : 's'}`}
              </p>
            </div>
            <p className="mt-2 text-2xl font-semibold text-foreground">{money(c, 'refundTotal')}</p>
            <p className="text-xs text-muted-foreground">Total Refund</p>
            <dl className="mt-3 space-y-1 border-t border-border/60 pt-3 text-sm">
              {figures.map((f) => (
                <div key={f.key} className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">{f.label}</dt>
                  <dd className="font-medium">{money(c, f.key)}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
      {methodRows.length > 0 && (
        <div className="mt-4">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            By Payment Method
          </h4>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[480px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Payment Method</th>
                  {cols.map((c) => (
                    <th key={c.label} className="px-4 py-2 text-right font-medium">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {methodRows.map((row) => {
                  const Icon = row.method === 'none' ? Wallet : paymentMethodIcon(row.method);
                  return (
                    <tr key={row.method} className="border-b border-border/60 last:border-b-0">
                      <td className="px-4 py-2">
                        <span className="inline-flex items-center gap-2">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                          {refundMethodLabel(row.method)}
                        </span>
                      </td>
                      {row.cells.map((m, k) => (
                        <td key={k} className="px-4 py-2 text-right whitespace-nowrap">
                          {methodCell(m)}
                        </td>
                      ))}
                      <td className="px-4 py-2 text-right whitespace-nowrap bg-primary/5">{methodCell(row.all)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            (n) = refunds using that method. A refund without its own tender uses its original sale's.
          </p>
        </div>
      )}
    </div>
  );
}

export function PosRefundsReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();

  // Follows the global reporting timezone (set via the ReportingTimeZoneBar at
  // the top of Reports; defaults to the business's Pacific zone). Threaded
  // through the picker, query, table, and export so all four stay in sync.
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [stationInput, setStationInput] = useState<string[]>([]);
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string; registerIds: string[] }>({
    ...initialRange,
    registerIds: [],
  });
  const [tab, setTab] = useState<PosRefundsTab>('with-order');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const { data: stationsData } = usePosStations({ locationId: selectedLocationId || undefined });
  const stationOptions = (stationsData?.data ?? []).map((s) => ({ value: s.registerId, label: s.stationName }));

  const params = {
    tab,
    page,
    perPage,
    locationId: selectedLocationId || undefined,
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    registerIds: applied.registerIds.length > 0 ? applied.registerIds.join(',') : undefined,
    sortDir,
  };
  const { data: refundsData, isLoading } = usePosRefunds(params, reportTz);

  // The summary shows both tabs at once, so ask each tab for its range totals
  // with the same filters. perPage 1: only `totals` and the row count are used.
  const summaryParams = (t: PosRefundsTab): PosRefundsParams => ({
    tab: t,
    page: 1,
    perPage: 1,
    locationId: params.locationId,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    registerIds: params.registerIds,
  });
  const { data: withOrderSummary } = usePosRefunds(summaryParams('with-order'), reportTz);
  const { data: withoutOrderSummary } = usePosRefunds(summaryParams('without-order'), reportTz);
  const summaryRangeLabel =
    applied.dateFrom && applied.dateTo
      ? `${formatWallClock(applied.dateFrom)} – ${formatWallClock(applied.dateTo)}`
      : applied.dateFrom
        ? `From ${formatWallClock(applied.dateFrom)}`
        : applied.dateTo
          ? `Through ${formatWallClock(applied.dateTo)}`
          : 'All dates';

  const toggleSortDir = () => {
    setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  };
  const rows: PosRefundRow[] = refundsData?.data ?? [];
  const pagination = refundsData?.pagination;

  const handleApply = () => {
    setApplied({
      dateFrom: dateFromInput,
      dateTo: dateToInput,
      registerIds: stationInput,
    });
    setPage(1);
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setStationInput([]);
    setApplied({ ...r, registerIds: [] });
    setPage(1);
  };
  const handleTabChange = (next: PosRefundsTab) => {
    if (next === tab) return;
    setTab(next);
    setPage(1);
  };

  const exportRows = async () => {
    setExporting(true);
    try {
      const all: PosRefundRow[] = [];
      const batch = 500;
      for (let p = 1; ; p++) {
        const res = await api.get<{
          data: PosRefundRow[];
          pagination: { page: number; perPage: number; total: number; totalPages: number };
        }>(
          '/pos/report/refunds',
          withLocalDateRange(
            {
              ...params,
              page: p,
              perPage: batch,
            },
            reportTz,
          ) as Record<string, string | number | undefined>,
        );
        all.push(...res.data);
        if (res.data.length < batch || p >= (res.pagination?.totalPages ?? 1)) break;
      }
      if (tab === 'with-order') {
        const header = [
          'Order ID',
          'Local ID',
          'POS Station',
          'Emp Name',
          'Total ($)',
          'Refund Amount ($)',
          'Refund Tax + Fees ($)',
          'Total Refund ($)',
          'Refund Tips ($)',
          'Payment Type',
          'Date',
        ];
        const body = all.map((r) => [
          r.originalDailyOrderNumber ?? '',
          r.originalTransactionId ?? '',
          r.stationName,
          r.employeeName ?? '',
          (r.originalTotal ?? 0).toFixed(2),
          r.refundAmount.toFixed(2),
          r.refundTaxFees.toFixed(2),
          r.refundTotal.toFixed(2),
          r.refundTips.toFixed(2),
          r.payments[0] ? paymentMethodBadgeLabel(r.payments[0].method) : '',
          formatPosDateTime(r.createdAt, reportTz),
        ]);
        downloadCsv(reportFilename('refunds_with_order', selectedLocationName), [header, ...body]);
      } else {
        const header = [
          'ID',
          'POS Station',
          'Location',
          'Emp Name',
          'Refund Date',
          'Refund Amount ($)',
          'Refund Tax + Fees ($)',
          'Total Refund ($)',
          'Payment Type',
          'External Reference',
        ];
        const body = all.map((r) => {
          const ref = r.payments
            .map((p) => `${paymentMethodBadgeLabel(p.method)} ${p.authCode ?? p.terminalRef ?? ''}`.trim())
            .join(' | ');
          return [
            r.dailyOrderNumber ?? r.id,
            r.stationName,
            r.locationName ?? '',
            r.employeeName ?? '',
            formatPosDateTime(r.createdAt, reportTz),
            r.refundAmount.toFixed(2),
            r.refundTaxFees.toFixed(2),
            r.refundTotal.toFixed(2),
            r.payments[0] ? paymentMethodBadgeLabel(r.payments[0].method) : '',
            ref,
          ];
        });
        downloadCsv(reportFilename('refunds_without_order', selectedLocationName), [header, ...body]);
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Refunds"
        description="Track all refunds across your store, with or without associated customer orders."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={!pagination?.total || exporting} onClick={exportRows}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware) */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">POS Station</label>
            <div className="min-w-[200px]">
              <MultiSelect
                options={stationOptions}
                selected={stationInput}
                onChange={setStationInput}
                triggerLabel="POS Station"
                searchPlaceholder="Search stations..."
                emptyMessage="No stations"
              />
            </div>
          </div>
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      <PosRefundsSummary
        rangeLabel={summaryRangeLabel}
        columns={[
          {
            label: 'With Order',
            count: withOrderSummary?.pagination.total,
            totals: withOrderSummary?.totals,
            byMethod: withOrderSummary?.byMethod,
          },
          {
            label: 'Without Order',
            count: withoutOrderSummary?.pagination.total,
            totals: withoutOrderSummary?.totals,
            byMethod: withoutOrderSummary?.byMethod,
          },
        ]}
      />

      {/* With/Without Order tabs */}
      <div className="border-b border-border">
        <div className="flex w-full min-w-0" role="tablist" aria-label="Refund types">
          {(
            [
              { id: 'with-order', label: 'With Order' },
              { id: 'without-order', label: 'Without Order' },
            ] as const
          ).map((t) => {
            const isSelected = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => handleTabChange(t.id)}
                className={cn(
                  'relative shrink-0 px-4 py-2.5 text-sm font-medium transition-colors',
                  isSelected ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                )}
                aria-selected={isSelected}
                role="tab"
              >
                {t.label}
                {isSelected && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Refunds table */}
      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          {tab === 'with-order' ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Order ID</th>
                  <th className="px-4 py-3 font-medium">Local ID</th>
                  <th className="px-4 py-3 font-medium">POS Station</th>
                  <th className="px-4 py-3 font-medium">Emp Name</th>
                  <th className="px-4 py-3 font-medium">Total ($)</th>
                  <th className="px-4 py-3 font-medium">Refund Amount ($)</th>
                  <th className="px-4 py-3 font-medium">Refund Tax + Fees ($)</th>
                  <th className="px-4 py-3 font-medium">Total Refund ($)</th>
                  <th className="px-4 py-3 font-medium">Refund Tips ($)</th>
                  <th className="px-4 py-3 font-medium">Payment Type</th>
                  <th className="px-4 py-3 font-medium">
                    <button
                      type="button"
                      onClick={toggleSortDir}
                      className="inline-flex items-center gap-1 font-medium uppercase hover:text-foreground"
                    >
                      Date
                      {sortDir === 'asc' ? (
                        <ChevronUp className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">View</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-10 text-center text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={12}>
                      <EmptyState icon={RotateCcw} title="No refunds" description="Try a different date range." />
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const primaryPayment = r.payments[0];
                    return (
                      <tr key={r.id} className="border-b border-border/60 last:border-b-0">
                        <td className="px-4 py-3 font-medium text-primary">{r.originalDailyOrderNumber ?? '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                          {r.originalTransactionId ?? '—'}
                        </td>
                        <td className="px-4 py-3">{r.stationName}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{r.employeeName ?? '—'}</td>
                        <td className="px-4 py-3 font-mono">
                          {r.originalTotal !== null ? formatCurrencyPlain(r.originalTotal) : '—'}
                        </td>
                        <td className="px-4 py-3 font-mono">{formatCurrencyPlain(r.refundAmount)}</td>
                        <td className="px-4 py-3 font-mono">{formatCurrencyPlain(r.refundTaxFees)}</td>
                        <td className="px-4 py-3 font-mono">{formatCurrencyPlain(r.refundTotal)}</td>
                        <td className="px-4 py-3 font-mono">{formatCurrencyPlain(r.refundTips)}</td>
                        <td className="px-4 py-3">
                          {primaryPayment ? (
                            <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                              {paymentMethodBadgeLabel(primaryPayment.method)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                          {formatPosDateTime(r.createdAt, reportTz)}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => setDetailOrderId(r.originalTransactionId ?? r.id)}
                            aria-label="View refund details"
                            className="rounded-md border border-border p-1.5 text-primary hover:bg-primary/5"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                  <th className="px-4 py-3 font-medium">ID</th>
                  <th className="px-4 py-3 font-medium">POS Station</th>
                  <th className="px-4 py-3 font-medium">Emp Name</th>
                  <th className="px-4 py-3 font-medium">Refund Amount ($)</th>
                  <th className="px-4 py-3 font-medium">Refund Tax + Fees ($)</th>
                  <th className="px-4 py-3 font-medium">Total Refund ($)</th>
                  <th className="px-4 py-3 font-medium">Payment Type</th>
                  <th className="px-4 py-3 font-medium">External Reference</th>
                  <th className="px-4 py-3 font-medium">Refund Date</th>
                  <th className="px-4 py-3 font-medium">View</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={10}>
                      <EmptyState icon={RotateCcw} title="No refunds" description="Try a different date range." />
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const primaryPayment = r.payments[0];
                    return (
                      <tr key={r.id} className="border-b border-border/60 last:border-b-0">
                        <td className="px-4 py-3 font-medium">{r.dailyOrderNumber ?? r.id.slice(0, 8)}</td>
                        <td className="px-4 py-3">
                          <div>{r.stationName}</div>
                          <div className="text-xs text-muted-foreground">{r.locationName ?? '—'}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div>{r.employeeName ?? '—'}</div>
                          <div className="text-xs text-muted-foreground">On {formatPosDate(r.createdAt, reportTz)}</div>
                        </td>
                        <td className="px-4 py-3 font-mono">{formatCurrencyPlain(r.refundAmount)}</td>
                        <td className="px-4 py-3 font-mono">{formatCurrencyPlain(r.refundTaxFees)}</td>
                        <td className="px-4 py-3 font-mono">{formatCurrencyPlain(r.refundTotal)}</td>
                        <td className="px-4 py-3">
                          {primaryPayment ? (
                            <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                              {paymentMethodBadgeLabel(primaryPayment.method)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div>{primaryPayment?.authCode ?? primaryPayment?.terminalRef ?? '—'}</div>
                          <div className="text-xs text-muted-foreground">Not Needed</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                          {formatPosDateTime(r.createdAt, reportTz)}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => setDetailOrderId(r.id)}
                            aria-label="View refund details"
                            className="rounded-md border border-border p-1.5 text-primary hover:bg-primary/5"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

        {pagination && pagination.total > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={pagination.perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>

      <OrderDetailsModal
        key={detailOrderId ?? 'closed'}
        open={detailOrderId !== null}
        orderId={detailOrderId}
        onClose={() => setDetailOrderId(null)}
      />
    </div>
  );
}

const DIGITAL_TXN_SORT_OPTIONS: { value: PosDigitalTxnSortBy; label: string }[] = [
  { value: 'orderId', label: 'Order ID' },
  { value: 'amount', label: 'Amount ($)' },
  { value: 'orderDate', label: 'Order Date' },
  { value: 'location', label: 'Location' },
  { value: 'cardEnding', label: 'Card Ending' },
  { value: 'authCode', label: 'Auth Code' },
];

// ── Tills → Reconciled Cash Records ───────────────────────────────────────

type CashRecordStatus = 'tallied' | 'short' | 'excess' | 'not-counted';

const CASH_RECORD_STATUS_OPTIONS: { value: CashRecordStatus; label: string }[] = [
  { value: 'short', label: 'Short' },
  { value: 'excess', label: 'Excess' },
  { value: 'tallied', label: 'Tallied' },
  { value: 'not-counted', label: 'Not Counted' },
];

type CashRecord = PosCashReconciliationRow & { net: number | null; status: CashRecordStatus };

/** Adds net + status to a Reconciliation → Cash row, by the same rule that page
 *  uses (cashNet / systemNote), so a row reads identically in both places. */
function toCashRecord(r: PosCashReconciliationRow): CashRecord {
  if (r.actualReported == null) return { ...r, net: null, status: 'not-counted' };
  const net = cashNet(r.actualReported, r.systemReported);
  return { ...r, net, status: systemNote(net).toLowerCase() as CashRecordStatus };
}

const sumCents = (values: number[]) => values.reduce((c, v) => c + Math.round(v * 100), 0) / 100;

/**
 * Reports → POS → Tills → Reconciled Cash Records. A read-only view of the
 * Reconciliation → Cash data (GET /pos/report/cash-reconciliations): one row per
 * business day and POS station with the system-reported drawer cash, the amount
 * actually counted, the difference, and the bookkeeping notes. Counts are
 * entered on the Reconciliation page; this report is for reviewing and
 * exporting them.
 *
 * Cash is reconciled per business day, so only the days of the picked range
 * are used — a specific time of day would otherwise cut a day's cash in half.
 */
export function PosCashRecordsReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [stationInput, setStationInput] = useState<string[]>([]);
  const [statusInput, setStatusInput] = useState<string[]>([]);
  const [applied, setApplied] = useState<{
    dateFrom: string;
    dateTo: string;
    registerIds: string[];
    statuses: string[];
  }>({ ...initialRange, registerIds: [], statuses: [] });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data: stationsData } = usePosStations({ locationId: selectedLocationId || undefined });
  const stationOptions = (stationsData?.data ?? []).map((s) => ({ value: s.registerId, label: s.stationName }));

  const { data, isLoading } = usePosCashReconciliations({
    dateFrom: applied.dateFrom ? applied.dateFrom.slice(0, 10) : undefined,
    dateTo: applied.dateTo ? applied.dateTo.slice(0, 10) : undefined,
    locationId: selectedLocationId || undefined,
  });

  const records = useMemo(() => {
    const stations = new Set(applied.registerIds);
    const statuses = new Set(applied.statuses);
    return (
      (data?.data ?? [])
        .map(toCashRecord)
        .filter((r) => stations.size === 0 || (r.registerId !== null && stations.has(r.registerId)))
        .filter((r) => statuses.size === 0 || statuses.has(r.status))
        // Date follows the header toggle; stations stay A–Z within a day.
        .sort((a, b) => {
          const byDate = a.date.localeCompare(b.date);
          return (sortDir === 'asc' ? byDate : -byDate) || a.registerName.localeCompare(b.registerName);
        })
    );
  }, [data, applied.registerIds, applied.statuses, sortDir]);

  const summary = useMemo(() => {
    const counted = records.filter((r) => r.net !== null);
    const nets = counted.map((r) => r.net as number);
    const countOf = (st: CashRecordStatus) => records.filter((r) => r.status === st).length;
    return {
      records: records.length,
      counted: counted.length,
      system: sumCents(records.map((r) => r.systemReported)),
      actual: sumCents(counted.map((r) => r.actualReported as number)),
      net: sumCents(nets),
      short: sumCents(nets.filter((n) => n < 0)),
      excess: sumCents(nets.filter((n) => n > 0)),
      tallied: countOf('tallied'),
      shortCount: countOf('short'),
      excessCount: countOf('excess'),
      notCounted: countOf('not-counted'),
    };
  }, [records]);

  const totalPages = Math.max(1, Math.ceil(records.length / perPage));
  const visible = records.slice((page - 1) * perPage, page * perPage);

  const rangeLabel =
    applied.dateFrom && applied.dateTo
      ? applied.dateFrom.slice(0, 10) === applied.dateTo.slice(0, 10)
        ? formatCalendarDate(applied.dateFrom.slice(0, 10))
        : `${formatCalendarDate(applied.dateFrom.slice(0, 10))} – ${formatCalendarDate(applied.dateTo.slice(0, 10))}`
      : 'All dates';

  const handleApply = () => {
    setApplied({ dateFrom: dateFromInput, dateTo: dateToInput, registerIds: stationInput, statuses: statusInput });
    setPage(1);
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setStationInput([]);
    setStatusInput([]);
    setApplied({ ...r, registerIds: [], statuses: [] });
    setPage(1);
  };

  const statusLabel = (st: CashRecordStatus) => CASH_RECORD_STATUS_OPTIONS.find((o) => o.value === st)?.label ?? st;

  const exportRows = () => {
    const header = [
      'Date',
      'POS Station',
      'Employee',
      'Cash - System Reported ($)',
      'Cash - Actually Reported ($)',
      'Net ($)',
      'System Notes',
      'Book Keeping Notes',
      'Counted By',
      'Last Updated',
    ];
    const body = records.map((r) => [
      formatMDY(r.date),
      r.registerName,
      r.employeeName ?? '',
      r.systemReported.toFixed(2),
      r.actualReported != null ? r.actualReported.toFixed(2) : '',
      r.net != null ? r.net.toFixed(2) : '',
      statusLabel(r.status),
      r.bookKeepingNotes ?? '',
      r.countedByName ?? '',
      r.countUpdatedAt ? formatPosDateTime(r.countUpdatedAt, reportTz) : '',
    ]);
    downloadCsv(reportFilename('cash_records', selectedLocationName), [header, ...body]);
  };

  const noLocation = !selectedLocationId;
  const netTone = (n: number | null) =>
    n == null || n === 0 ? 'text-muted-foreground' : n < 0 ? 'text-red-600' : 'text-green-600';

  return (
    <div className="space-y-4">
      <PageHeader
        title="Reconciled Cash Records"
        description="Daily cash counts per POS station — system-reported cash against the amount actually counted."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={records.length === 0} onClick={exportRows}>
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Whole business days · Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">POS Station</label>
            <div className="min-w-[200px]">
              <MultiSelect
                options={stationOptions}
                selected={stationInput}
                onChange={setStationInput}
                triggerLabel="POS Station"
                searchPlaceholder="Search stations..."
                emptyMessage="No stations"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">System Notes</label>
            <div className="min-w-[180px]">
              <MultiSelect
                options={CASH_RECORD_STATUS_OPTIONS}
                selected={statusInput}
                onChange={setStatusInput}
                triggerLabel="System Notes"
                searchPlaceholder="Search..."
                emptyMessage="No options"
              />
            </div>
          </div>
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {/* Summary */}
      {!noLocation && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground">Summary</h3>
            <span className="text-xs text-muted-foreground">{rangeLabel}</span>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              className="p-4"
              title="Cash - System Reported"
              value={isLoading ? '...' : formatCurrency(summary.system)}
              subtitle={`${summary.records} record${summary.records === 1 ? '' : 's'}`}
            />
            <StatCard
              className="p-4"
              title="Cash - Actually Reported"
              value={isLoading ? '...' : formatCurrency(summary.actual)}
              subtitle={`${summary.counted} of ${summary.records} counted`}
            />
            <StatCard
              className="p-4"
              title="Net"
              value={isLoading ? '...' : formatCurrency(summary.net)}
              subtitle={`Short ${formatCurrency(summary.short)} · Excess ${formatCurrency(summary.excess)}`}
            />
            <StatCard
              className="p-4"
              title="System Notes"
              value={isLoading ? '...' : `${summary.tallied} tallied`}
              subtitle={`${summary.shortCount} short · ${summary.excessCount} excess · ${summary.notCounted} not counted`}
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">
                  <button
                    type="button"
                    onClick={() => {
                      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                      setPage(1);
                    }}
                    className="inline-flex items-center gap-1 font-medium uppercase hover:text-foreground"
                  >
                    Date
                    {sortDir === 'asc' ? (
                      <ChevronUp className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5" />
                    )}
                  </button>
                </th>
                <th className="px-4 py-3 font-medium">POS Station</th>
                <th className="px-4 py-3 font-medium">Employee</th>
                <th className="px-4 py-3 text-right font-medium">Cash - System Reported ($)</th>
                <th className="px-4 py-3 text-right font-medium">Cash - Actually Reported ($)</th>
                <th className="px-4 py-3 text-right font-medium">Net ($)</th>
                <th className="px-4 py-3 font-medium">System Notes</th>
                <th className="px-4 py-3 font-medium">Book Keeping Notes</th>
                <th className="px-4 py-3 font-medium">Counted By</th>
                <th className="px-4 py-3 font-medium">Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {noLocation ? (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">
                    Select a location to view cash records.
                  </td>
                </tr>
              ) : isLoading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={10}>
                    <EmptyState icon={Banknote} title="No cash records" description="Try a different date range." />
                  </td>
                </tr>
              ) : (
                visible.map((r) => (
                  <tr key={`${r.date}|${r.registerId ?? ''}`} className="border-b border-border/60 last:border-b-0">
                    <td className="whitespace-nowrap px-4 py-3">{formatMDY(r.date)}</td>
                    <td className="px-4 py-3">{r.registerName}</td>
                    <td className="whitespace-nowrap px-4 py-3">{r.employeeName ?? '—'}</td>
                    <td className="px-4 py-3 text-right font-mono">{formatCurrencyPlain(r.systemReported)}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      {r.actualReported != null ? formatCurrencyPlain(r.actualReported) : '—'}
                    </td>
                    <td className={cn('px-4 py-3 text-right font-mono font-medium', netTone(r.net))}>
                      {r.net != null ? formatCurrencyPlain(r.net) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {r.net != null ? (
                        <Badge variant={NOTE_VARIANT[systemNote(r.net)]}>{systemNote(r.net)}</Badge>
                      ) : (
                        <Badge variant="default">Not Counted</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{r.bookKeepingNotes || '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {/* A count saved by a user who no longer resolves still shows it was counted. */}
                      {r.countedByName ?? (r.countedByUserId ? 'Unknown user' : '—')}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                      {r.countUpdatedAt ? formatPosDateTime(r.countUpdatedAt, reportTz) : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {records.length > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={page}
              totalPages={totalPages}
              total={records.length}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function PosDigitalTransactionsReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const locationLabel = selectedLocationName || 'All locations';
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const { data: stationsData } = usePosStations({ locationId: selectedLocationId || undefined });
  const stationOptions = (stationsData?.data ?? []).map((s) => ({ value: s.registerId, label: s.stationName }));

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [stationInput, setStationInput] = useState<string[]>([]);
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string; registerIds: string[] }>({
    ...initialRange,
    registerIds: [],
  });
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [sortBy, setSortBy] = useState<PosDigitalTxnSortBy>('orderDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Debounce search.
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const params = {
    page,
    perPage,
    locationId: selectedLocationId || undefined,
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    registerIds: applied.registerIds.length > 0 ? applied.registerIds.join(',') : undefined,
    search: appliedSearch || undefined,
    sortBy,
    sortDir,
  };
  const { data, isLoading } = usePosDigitalTransactions(params);
  const rows: PosDigitalTransactionRow[] = data?.data ?? [];
  const pagination = data?.pagination;
  const totals = data?.totals;

  const handleApply = () => {
    setApplied({
      dateFrom: dateFromInput,
      dateTo: dateToInput,
      registerIds: stationInput,
    });
    setPage(1);
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setStationInput([]);
    setApplied({ ...r, registerIds: [] });
    setSearch('');
    setAppliedSearch('');
    setSortBy('orderDate');
    setSortDir('desc');
    setPage(1);
  };
  const handleSortApply = (field: PosDigitalTxnSortBy, dir: 'asc' | 'desc') => {
    setSortBy(field);
    setSortDir(dir);
    setPage(1);
  };
  const sortActive = !(sortBy === 'orderDate' && sortDir === 'desc');

  const exportRows = async () => {
    setExporting(true);
    try {
      const all: PosDigitalTransactionRow[] = [];
      const batch = 500;
      for (let p = 1; ; p++) {
        const res = await api.get<{
          data: PosDigitalTransactionRow[];
          pagination: { page: number; perPage: number; total: number; totalPages: number };
        }>(
          '/pos/report/digital-transactions',
          withLocalDateRange({
            ...params,
            page: p,
            perPage: batch,
          }) as Record<string, string | number | undefined>,
        );
        all.push(...res.data);
        if (res.data.length < batch || p >= (res.pagination?.totalPages ?? 1)) break;
      }
      const header = [
        'Order ID',
        'Order Date',
        'Amount ($)',
        'Location',
        'Station',
        'Transaction Type',
        'Card Brand',
        'Card Ending',
        'Auth Code',
      ];
      const body = all.map((r) => [
        r.dailyOrderNumber ?? r.transactionId,
        formatPosDateTime(r.createdAt),
        r.amount.toFixed(2),
        r.locationName ?? locationLabel,
        r.stationName,
        r.transactionType === 'sale' ? 'Sale' : 'Refund',
        r.cardBrand ?? '',
        r.cardLastFour ? `**** ${r.cardLastFour}` : '',
        r.authCode ?? '',
      ]);
      downloadCsv(reportFilename('electronic_statement_report', selectedLocationName), [header, ...body]);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Electronic Statement Report"
        description="Track digital payments and refunds processed through your terminals."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={!pagination?.total || exporting} onClick={exportRows}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware) */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">POS Station</label>
            <div className="min-w-[200px]">
              <MultiSelect
                options={stationOptions}
                selected={stationInput}
                onChange={setStationInput}
                triggerLabel="POS Station"
                searchPlaceholder="Search stations..."
                emptyMessage="No stations"
              />
            </div>
          </div>
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-2 lg:grid-cols-3">
          <DigitalTxnSummaryStat label="Total Digital Sales" marker="A" value={totals?.salesTotal ?? 0} />
          <DigitalTxnSummaryStat label="Total Digital Refunds" marker="B" value={totals?.refundsTotal ?? 0} />
          <DigitalTxnSummaryStat label="Net Digital Amount" marker="C = A - B" value={totals?.netTotal ?? 0} accent />
        </div>
      </div>

      {/* Search + Sort */}
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex w-full max-w-sm items-center gap-2 rounded-md border border-input bg-white px-2.5 py-1.5 text-sm">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Order ID, amount, card last 4 or auth code"
              aria-label="Search transactions"
              className="w-full bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
            />
          </div>
          <DigitalTxnSortPopover sortBy={sortBy} sortDir={sortDir} isActive={sortActive} onApply={handleSortApply} />
        </div>
        <p className="text-xs text-muted-foreground">
          Search by Order ID, Amount (exact, e.g. 25.50), Card Ending (last 4) or Auth Code.
        </p>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Order ID</th>
                <th className="px-4 py-3 font-medium">Amount ($)</th>
                <th className="px-4 py-3 font-medium">Location &amp; Station</th>
                <th className="px-4 py-3 font-medium">Transaction Type</th>
                <th className="px-4 py-3 font-medium">Card Ending</th>
                <th className="px-4 py-3 font-medium">Auth Code</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState
                      icon={CreditCard}
                      title="No digital transactions"
                      description="Try a different date range."
                    />
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const isRefund = r.transactionType === 'refund';
                  return (
                    <tr key={r.id} className="border-b border-border/60 last:border-b-0">
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setDetailOrderId(r.transactionId)}
                          className="font-medium text-primary hover:underline"
                        >
                          {r.dailyOrderNumber ?? r.transactionId.slice(0, 8)}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-mono">{formatCurrencyPlain(r.amount)}</div>
                        <div className="text-xs text-muted-foreground">On {formatPosDateTime(r.createdAt)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div>{r.locationName ?? '—'}</div>
                        <div className="text-xs text-muted-foreground">{r.stationName}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                            isRefund ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700',
                          )}
                        >
                          {isRefund ? 'Refund' : 'Sale'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-mono">**** {r.cardLastFour ?? '----'}</div>
                        {r.cardBrand && <div className="text-xs text-muted-foreground">({r.cardBrand})</div>}
                      </td>
                      <td className="px-4 py-3 font-mono">{r.authCode ?? '—'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={pagination.perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>

      <OrderDetailsModal
        key={detailOrderId ?? 'closed'}
        open={detailOrderId !== null}
        orderId={detailOrderId}
        onClose={() => setDetailOrderId(null)}
      />
    </div>
  );
}

function DigitalTxnSummaryStat({
  label,
  marker,
  value,
  accent = false,
}: {
  label: string;
  marker?: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground">
        {label} {marker && <span className="text-[10px]">({marker})</span>}
      </div>
      <div className={cn('mt-1 text-xl font-semibold', accent ? 'text-foreground' : 'text-foreground')}>
        ${formatCurrencyPlain(value)}
      </div>
    </div>
  );
}

function DigitalTxnSortPopover({
  sortBy,
  sortDir,
  isActive,
  onApply,
}: {
  sortBy: PosDigitalTxnSortBy;
  sortDir: 'asc' | 'desc';
  isActive: boolean;
  onApply: (field: PosDigitalTxnSortBy, dir: 'asc' | 'desc') => void;
}) {
  const [open, setOpen] = useState(false);
  const [draftField, setDraftField] = useState<PosDigitalTxnSortBy>(sortBy);
  const [draftDir, setDraftDir] = useState<'asc' | 'desc'>(sortDir);
  const ref = useRef<HTMLDivElement | null>(null);

  // React-recommended "adjust state on prop change" pattern: re-sync drafts
  // to the current sortBy/sortDir whenever the popover opens. Done during
  // render (not in an effect) to avoid cascading renders.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDraftField(sortBy);
      setDraftDir(sortDir);
    }
  }

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-2 rounded-lg border border-input bg-white px-3 text-sm hover:bg-muted/40"
      >
        <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
        Sort
        {isActive && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-amber-500" aria-label="Custom sort active" />}
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-lg border border-border bg-white p-2 shadow-lg">
          <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sort by</div>
          <div className="space-y-0.5">
            {DIGITAL_TXN_SORT_OPTIONS.map((opt) => {
              const checked = draftField === opt.value;
              return (
                <label
                  key={opt.value}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                >
                  <input
                    type="radio"
                    name="digital-txn-sort-by"
                    checked={checked}
                    onChange={() => setDraftField(opt.value)}
                    className="h-4 w-4 text-primary focus:ring-primary"
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
          <div className="my-2 border-t border-border" />
          <button
            type="button"
            onClick={() => setDraftDir('asc')}
            className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted ${
              draftDir === 'asc' ? 'text-primary' : ''
            }`}
          >
            <span className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 rotate-180 text-muted-foreground" />A – Z
            </span>
            {draftDir === 'asc' && <span className="h-2 w-2 rounded-full bg-primary" />}
          </button>
          <button
            type="button"
            onClick={() => setDraftDir('desc')}
            className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted ${
              draftDir === 'desc' ? 'text-primary' : ''
            }`}
          >
            <span className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground" />Z – A
            </span>
            {draftDir === 'desc' && <span className="h-2 w-2 rounded-full bg-primary" />}
          </button>
          <div className="mt-2 flex justify-end gap-2 border-t border-border pt-2">
            <Button size="sm" variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                onApply(draftField, draftDir);
                setOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tills report (Reports → POS → Tills) ────────────────────────────────────
const TILLS_SORT_OPTIONS: { value: PosTillSortBy; label: string }[] = [
  { value: 'id', label: 'ID' },
  { value: 'posStation', label: 'POS Station' },
  { value: 'startingAmount', label: 'Starting Amount ($)' },
  { value: 'closingAmount', label: 'Closing Amount ($)' },
  { value: 'bankDropAmount', label: 'Bank Drop Amount ($)' },
  { value: 'status', label: 'Status' },
  { value: 'difference', label: 'Difference ($)' },
  { value: 'createdAt', label: 'Created At' },
];

// Cash-drawer session report. Location comes from the global header (useLocationStore),
// never a local dropdown. Mirrors PosDigitalTransactionsReport for filters, search,
// sort, pagination and empty/loading states. Backend is a scaffold today (returns
// no rows) — see GET /pos/report/tills.
/**
 * Shared date-range filter card for the POS item reports. Location comes
 * from the global header selector, so the card carries only Start/End date and
 * the Submit/Reset pair.
 */
function ReportDateRangeCard({
  reportTz,
  reportTzLabel,
  dateFromInput,
  dateToInput,
  onFromChange,
  onToChange,
  onSubmit,
  onReset,
}: {
  reportTz: string;
  reportTzLabel: string;
  dateFromInput: string;
  dateToInput: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  onSubmit: () => void;
  onReset: () => void;
}) {
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
        <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <ReportRangeDatePicker
          reportTz={reportTz}
          dateFromInput={dateFromInput}
          dateToInput={dateToInput}
          onFromChange={onFromChange}
          onToChange={onToChange}
        />
        <Button size="sm" variant="primary" onClick={onSubmit}>
          Submit
        </Button>
        <Button size="sm" variant="destructive-outline" onClick={onReset}>
          Reset
        </Button>
      </div>
    </div>
  );
}

/** Shared state for the POS item reports (date range + debounced search). */
function useItemReportFilters() {
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();
  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string }>({ ...initialRange });
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const handleSubmit = () => {
    setApplied({ dateFrom: dateFromInput, dateTo: dateToInput });
    setPage(1);
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setApplied({ ...r });
    setSearch('');
    setAppliedSearch('');
    setPage(1);
  };

  return {
    reportTz,
    reportTzLabel,
    dateFromInput,
    setDateFromInput,
    dateToInput,
    setDateToInput,
    applied,
    search,
    setSearch,
    appliedSearch,
    page,
    setPage,
    perPage,
    setPerPage,
    handleSubmit,
    handleReset,
  };
}

/** MISC Item — lines a cashier keyed in manually at checkout. */
function PosExtraItemReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const f = useItemReportFilters();

  const { data, isLoading } = usePosExtraItems({
    page: f.page,
    perPage: f.perPage,
    locationId: selectedLocationId || undefined,
    dateFrom: f.applied.dateFrom || undefined,
    dateTo: f.applied.dateTo || undefined,
    search: f.appliedSearch || undefined,
  });
  const rows: PosExtraItemRow[] = data?.data ?? [];
  const pagination = data?.pagination;
  // Export walks every page of the filtered result, so it can take a moment —
  // disable the button while it runs rather than let a second click start a
  // duplicate download.
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    // Export the WHOLE filtered result, not just the page on screen.
    setExporting(true);
    try {
      const all = await fetchAllReportPages<PosExtraItemRow>(
        '/pos/report/extra-items',
        {
          locationId: selectedLocationId || undefined,
          dateFrom: f.applied.dateFrom || undefined,
          dateTo: f.applied.dateTo || undefined,
          search: f.appliedSearch || undefined,
        },
        // No tz override: this report's hook doesn't pass one either, so both
        // fall back to the same getReportingTimeZone() default.
      );
      const header = [
        'Order ID',
        'Location',
        'Order Date',
        'MISC Item Name',
        'Quantity',
        'Sale Price ($)',
        'Total ($)',
        'Employee Name',
      ];
      const body = all.map((r) => [
        r.orderNumber,
        r.locationName,
        formatDateTime(r.orderDate),
        r.itemName,
        r.quantity,
        r.salePrice.toFixed(2),
        r.lineTotal.toFixed(2),
        r.employeeName,
      ]);
      downloadCsv(reportFilename('misc_item_report', selectedLocationName), [header, ...body]);
    } catch {
      toast.error('Could not export the report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="MISC Item" description="Manually added items logged during checkout." onBack={onBack}>
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={rows.length === 0 || exporting} onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      <ReportDateRangeCard
        reportTz={f.reportTz}
        reportTzLabel={f.reportTzLabel}
        dateFromInput={f.dateFromInput}
        dateToInput={f.dateToInput}
        onFromChange={f.setDateFromInput}
        onToChange={f.setDateToInput}
        onSubmit={f.handleSubmit}
        onReset={f.handleReset}
      />

      <div className="inline-flex max-w-sm items-center gap-2 rounded-md border border-input bg-white px-2.5 py-1.5 text-sm">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          value={f.search}
          onChange={(e) => f.setSearch(e.target.value)}
          placeholder="Search item name or employee"
          className="w-72 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Order ID</th>
                <th className="px-4 py-3 font-medium">Location</th>
                <th className="px-4 py-3 font-medium">Order Date</th>
                <th className="px-4 py-3 font-medium">MISC Item Name</th>
                <th className="px-4 py-3 text-right font-medium">Quantity</th>
                <th className="px-4 py-3 text-right font-medium">Sale Price ($)</th>
                <th className="px-4 py-3 text-right font-medium">Total ($)</th>
                <th className="px-4 py-3 font-medium">Employee Name</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                    No MISC items for this date range.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={`${r.transactionId}-${r.itemName}-${r.orderDate}`}
                    className="border-b border-border last:border-0"
                  >
                    <td className="px-4 py-2.5 font-medium text-foreground">{r.orderNumber || '—'}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.locationName || '—'}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{formatDateTime(r.orderDate)}</td>
                    <td className="px-4 py-2.5 text-foreground">{r.itemName || '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{r.quantity}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-foreground">{r.salePrice.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-foreground">{r.lineTotal.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.employeeName || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={pagination.perPage}
              onPageChange={f.setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                f.setPerPage(n);
                f.setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Variable Item — sales of products flagged Variable Price. */
function PosVariableItemReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const f = useItemReportFilters();

  const { data, isLoading } = usePosVariableItems({
    page: f.page,
    perPage: f.perPage,
    locationId: selectedLocationId || undefined,
    dateFrom: f.applied.dateFrom || undefined,
    dateTo: f.applied.dateTo || undefined,
    search: f.appliedSearch || undefined,
  });
  const rows: PosVariableItemRow[] = data?.data ?? [];
  const pagination = data?.pagination;
  // Export walks every page of the filtered result, so it can take a moment —
  // disable the button while it runs rather than let a second click start a
  // duplicate download.
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    // Export the WHOLE filtered result, not just the page on screen.
    setExporting(true);
    try {
      const all = await fetchAllReportPages<PosVariableItemRow>(
        '/pos/report/variable-items',
        {
          locationId: selectedLocationId || undefined,
          dateFrom: f.applied.dateFrom || undefined,
          dateTo: f.applied.dateTo || undefined,
          search: f.appliedSearch || undefined,
        },
        // No tz override: this report's hook doesn't pass one either, so both
        // fall back to the same getReportingTimeZone() default.
      );
      const header = [
        'Order ID',
        'Location',
        'Product Name',
        'Department',
        'Tags',
        'Description',
        'Quantity',
        'Sale Price ($)',
        'Total ($)',
        'Employee Name',
        'Order Date',
      ];
      const body = all.map((r) => [
        r.orderNumber,
        r.locationName,
        r.productName,
        r.departmentName,
        r.tags,
        r.description,
        r.quantity,
        r.salePrice.toFixed(2),
        r.lineTotal.toFixed(2),
        r.employeeName,
        formatDateTime(r.orderDate),
      ]);
      downloadCsv(reportFilename('variable_item_report', selectedLocationName), [header, ...body]);
    } catch {
      toast.error('Could not export the report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Variable Item" description="Sales of products flagged Variable Price." onBack={onBack}>
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={rows.length === 0 || exporting} onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      <ReportDateRangeCard
        reportTz={f.reportTz}
        reportTzLabel={f.reportTzLabel}
        dateFromInput={f.dateFromInput}
        dateToInput={f.dateToInput}
        onFromChange={f.setDateFromInput}
        onToChange={f.setDateToInput}
        onSubmit={f.handleSubmit}
        onReset={f.handleReset}
      />

      <div className="inline-flex max-w-sm items-center gap-2 rounded-md border border-input bg-white px-2.5 py-1.5 text-sm">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          value={f.search}
          onChange={(e) => f.setSearch(e.target.value)}
          placeholder="Search product or employee"
          className="w-72 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Order ID</th>
                <th className="px-4 py-3 font-medium">Location</th>
                <th className="px-4 py-3 font-medium">Product Name</th>
                <th className="px-4 py-3 font-medium">Department</th>
                <th className="px-4 py-3 font-medium">Tags</th>
                <th className="px-4 py-3 font-medium">Description</th>
                <th className="px-4 py-3 text-right font-medium">Quantity</th>
                <th className="px-4 py-3 text-right font-medium">Sale Price ($)</th>
                <th className="px-4 py-3 text-right font-medium">Total ($)</th>
                <th className="px-4 py-3 font-medium">Employee Name</th>
                <th className="px-4 py-3 font-medium">Order Date</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={11}>
                    <EmptyState
                      icon={Scale}
                      title="No variable-price items sold"
                      description="Try a different date range."
                    />
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={`${r.transactionId}-${r.productName}-${r.orderDate}`}
                    className="border-b border-border last:border-0"
                  >
                    <td className="px-4 py-2.5 font-medium text-foreground">{r.orderNumber || '—'}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.locationName || '—'}</td>
                    <td className="px-4 py-2.5 text-foreground">{r.productName || '—'}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.departmentName || 'N.A.'}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.tags || 'N.A.'}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.description || 'N.A.'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{r.quantity}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-foreground">{r.salePrice.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-foreground">{r.lineTotal.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.employeeName || '—'}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{formatDateTime(r.orderDate)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={pagination.perPage}
              onPageChange={f.setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                f.setPerPage(n);
                f.setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Cash In / Cash Out — the manual paid-in and paid-out drawer events, laid out
 * like the Tills report next to it: location comes from the global header
 * selector, the page filters only on a date range, and Submit/Reset apply it.
 */
function PosCashMovementReport({ direction, onBack }: { direction: 'in' | 'out'; onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string }>({ ...initialRange });
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  // Debounce search (POS Station / Employee / Reason).
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = usePosCashMovements({
    direction,
    page,
    perPage,
    locationId: selectedLocationId || undefined,
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    search: appliedSearch || undefined,
  });
  const rows: PosCashMovementRow[] = data?.data ?? [];
  const pagination = data?.pagination;
  // Export walks every page of the filtered result, so it can take a moment —
  // disable the button while it runs rather than let a second click start a
  // duplicate download.
  const [exporting, setExporting] = useState(false);

  const title = direction === 'in' ? 'Cash In' : 'Cash Out';
  const description =
    direction === 'in'
      ? 'Times when extra cash was added to the till.'
      : 'Times when cash was taken out of the till (e.g. for supplier payments).';

  const handleSubmit = () => {
    setApplied({ dateFrom: dateFromInput, dateTo: dateToInput });
    setPage(1);
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setApplied({ ...r });
    setSearch('');
    setAppliedSearch('');
    setPage(1);
  };

  const total = rows.reduce((sum, r) => sum + r.amount, 0);

  const handleExport = async () => {
    // Export the WHOLE filtered result, not just the page on screen.
    setExporting(true);
    try {
      const all = await fetchAllReportPages<PosCashMovementRow>(
        '/pos/report/cash-movements',
        {
          direction,
          locationId: selectedLocationId || undefined,
          dateFrom: applied.dateFrom || undefined,
          dateTo: applied.dateTo || undefined,
          search: appliedSearch || undefined,
        },
        { tz: reportTz },
      );
      const header = ['POS Station', 'Location', 'Employee', 'Amount ($)', `${title} Date`, 'Reason'];
      const body = all.map((r) => [
        r.stationName,
        r.locationName,
        r.employeeName,
        r.amount.toFixed(2),
        formatDateTime(r.eventTimestamp),
        r.note,
      ]);
      downloadCsv(reportFilename(`${direction === 'in' ? 'cash_in' : 'cash_out'}_report`, selectedLocationName), [
        header,
        ...body,
      ]);
    } catch {
      toast.error('Could not export the report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title={title} description={description} onBack={onBack}>
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={rows.length === 0 || exporting} onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — date range only. No location dropdown: the global header
          selector drives locationId, same as the Tills report. */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <Button size="sm" variant="primary" onClick={handleSubmit}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex max-w-sm items-center gap-2 rounded-md border border-input bg-white px-2.5 py-1.5 text-sm">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search POS Station, Employee, Reason"
            className="w-72 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
          />
        </div>
        {rows.length > 0 && (
          <span className="text-xs text-muted-foreground">
            Page total: <span className="font-medium text-foreground">${total.toFixed(2)}</span>
          </span>
        )}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">POS Station</th>
                <th className="px-4 py-3 font-medium">Location</th>
                <th className="px-4 py-3 font-medium">Employee</th>
                <th className="px-4 py-3 text-right font-medium">Amount ($)</th>
                <th className="px-4 py-3 font-medium">{title} Date</th>
                <th className="px-4 py-3 font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    No {title.toLowerCase()} records for this date range.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2.5 font-medium text-foreground">{r.stationName || '—'}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.locationName || '—'}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.employeeName || '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-foreground">{r.amount.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{formatDateTime(r.eventTimestamp)}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.note || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={pagination.perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function PosTillsReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId } = useLocationStore();
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string }>({ ...initialRange });
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [sortBy, setSortBy] = useState<PosTillSortBy>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  // Debounce search (Till ID / POS Station / Employee / Status).
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const params = {
    page,
    perPage,
    locationId: selectedLocationId || undefined,
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    search: appliedSearch || undefined,
    sortBy,
    sortDir,
  };
  const { data, isLoading } = usePosTills(params);
  const rows: PosTillRow[] = data?.data ?? [];
  const pagination = data?.pagination;

  const handleApply = () => {
    setApplied({ dateFrom: dateFromInput, dateTo: dateToInput });
    setPage(1);
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setApplied({ ...r });
    setSearch('');
    setAppliedSearch('');
    setSortBy('createdAt');
    setSortDir('desc');
    setPage(1);
  };
  const handleSortApply = (field: PosTillSortBy, dir: 'asc' | 'desc') => {
    setSortBy(field);
    setSortDir(dir);
    setPage(1);
  };
  const sortActive = !(sortBy === 'createdAt' && sortDir === 'desc');

  return (
    <div className="space-y-4">
      <PageHeader title="Tills" description="Cash-drawer sessions per POS station." onBack={onBack} />

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware). No
          location dropdown: the global header selector drives locationId. */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {/* Search + Sort */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex max-w-sm items-center gap-2 rounded-md border border-input bg-white px-2.5 py-1.5 text-sm">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Till ID, POS Station, Employee, Status"
            className="w-72 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
          />
        </div>
        <TillsSortPopover sortBy={sortBy} sortDir={sortDir} isActive={sortActive} onApply={handleSortApply} />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">ID</th>
                <th className="px-4 py-3 font-medium">POS Station</th>
                <th className="px-4 py-3 font-medium">Starting Amount ($)</th>
                <th className="px-4 py-3 font-medium">Closing Amount ($)</th>
                <th className="px-4 py-3 font-medium">Bank Drop Amount ($)</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Difference ($)</th>
                <th className="px-4 py-3 font-medium">Created At</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <EmptyState
                      icon={Wallet}
                      title="No tills found"
                      description="Try changing the filters or date range."
                    />
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const diffCls =
                    r.difference < 0 ? 'text-red-600' : r.difference > 0 ? 'text-green-600' : 'text-muted-foreground';
                  return (
                    <tr key={r.id} className="border-b border-border/60 last:border-b-0">
                      <td className="px-4 py-3 font-medium text-foreground">{r.tillNumber ?? r.id.slice(0, 8)}</td>
                      <td className="px-4 py-3">
                        <div>{r.stationName ?? '—'}</div>
                        {r.locationName && <div className="text-xs text-muted-foreground">{r.locationName}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-mono">{formatCurrencyPlain(r.startingAmount)}</div>
                        {r.startingEmployeeName && (
                          <div className="text-xs text-muted-foreground">by {r.startingEmployeeName}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {r.closingAmount == null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <>
                            <div className="font-mono">{formatCurrencyPlain(r.closingAmount)}</div>
                            {r.closingEmployeeName && (
                              <div className="text-xs text-muted-foreground">by {r.closingEmployeeName}</div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono">{formatCurrency(r.bankDropAmount)}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className={cn('px-4 py-3 font-mono font-medium', diffCls)}>{formatCurrency(r.difference)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{formatPosDateTime(r.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <TillDenominationsCell row={r} />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={pagination.perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Opening/closing denomination breakdown for a till session (captured on the
// POS). Renders small "Open $" / "Close $" buttons that open a modal listing
// each denomination × count and the total.
function TillDenominationsCell({ row }: { row: PosTillRow }) {
  const [open, setOpen] = useState<null | 'start' | 'close'>(null);
  const hasStart = (row.startingDenominations?.length ?? 0) > 0;
  const hasClose = (row.closingDenominations?.length ?? 0) > 0;
  if (!hasStart && !hasClose) return null;
  const denoms = open === 'start' ? row.startingDenominations : open === 'close' ? row.closingDenominations : null;
  const total = (denoms ?? []).reduce((s, d) => s + d.value * d.count, 0);
  return (
    <>
      <div className="flex gap-1">
        {hasStart && (
          <button
            onClick={() => setOpen('start')}
            className="rounded border border-border px-2 py-0.5 text-xs hover:bg-muted"
          >
            Open $
          </button>
        )}
        {hasClose && (
          <button
            onClick={() => setOpen('close')}
            className="rounded border border-border px-2 py-0.5 text-xs hover:bg-muted"
          >
            Close $
          </button>
        )}
      </div>
      {open && denoms && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setOpen(null)}>
          <div className="w-80 rounded-lg bg-white p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-3 text-sm font-semibold">{open === 'start' ? 'Opening' : 'Closing'} denominations</h3>
            <div className="space-y-1">
              {denoms.map((d, i) => (
                <div key={i} className="flex items-center justify-between gap-2 text-sm">
                  <span className="font-medium">{d.label}</span>
                  <span className="text-muted-foreground">
                    {d.count} × {formatCurrency(d.value)}
                  </span>
                  <span className="w-16 text-right font-semibold">{formatCurrency(d.value * d.count)}</span>
                </div>
              ))}
              <div className="mt-2 flex justify-between border-t border-border pt-2 text-sm font-bold">
                <span>Total</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>
            <button
              onClick={() => setOpen(null)}
              className="mt-4 w-full rounded-md border border-border py-1.5 text-sm hover:bg-muted"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function TillsSortPopover({
  sortBy,
  sortDir,
  isActive,
  onApply,
}: {
  sortBy: PosTillSortBy;
  sortDir: 'asc' | 'desc';
  isActive: boolean;
  onApply: (field: PosTillSortBy, dir: 'asc' | 'desc') => void;
}) {
  const [open, setOpen] = useState(false);
  const [draftField, setDraftField] = useState<PosTillSortBy>(sortBy);
  const [draftDir, setDraftDir] = useState<'asc' | 'desc'>(sortDir);
  const ref = useRef<HTMLDivElement | null>(null);

  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDraftField(sortBy);
      setDraftDir(sortDir);
    }
  }

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-2 rounded-lg border border-input bg-white px-3 text-sm hover:bg-muted/40"
      >
        <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
        Sort
        {isActive && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-amber-500" aria-label="Custom sort active" />}
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-lg border border-border bg-white p-2 shadow-lg">
          <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sort by</div>
          <div className="space-y-0.5">
            {TILLS_SORT_OPTIONS.map((opt) => {
              const checked = draftField === opt.value;
              return (
                <label
                  key={opt.value}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                >
                  <input
                    type="radio"
                    name="tills-sort-by"
                    checked={checked}
                    onChange={() => setDraftField(opt.value)}
                    className="h-4 w-4 text-primary focus:ring-primary"
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
          <div className="my-2 border-t border-border" />
          <button
            type="button"
            onClick={() => setDraftDir('asc')}
            className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted ${
              draftDir === 'asc' ? 'text-primary' : ''
            }`}
          >
            <span className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 rotate-180 text-muted-foreground" />A – Z
            </span>
            {draftDir === 'asc' && <span className="h-2 w-2 rounded-full bg-primary" />}
          </button>
          <button
            type="button"
            onClick={() => setDraftDir('desc')}
            className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted ${
              draftDir === 'desc' ? 'text-primary' : ''
            }`}
          >
            <span className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground" />Z – A
            </span>
            {draftDir === 'desc' && <span className="h-2 w-2 rounded-full bg-primary" />}
          </button>
          <div className="mt-2 flex justify-end gap-2 border-t border-border pt-2">
            <Button size="sm" variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                onApply(draftField, draftDir);
                setOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const DISCOUNT_SORT_OPTIONS: { value: PosDiscountSortBy; label: string }[] = [
  { value: 'id', label: 'ID' },
  { value: 'orderId', label: 'Order ID' },
  { value: 'location', label: 'Location' },
  { value: 'orderDate', label: 'Order Date' },
  { value: 'orderDiscount', label: 'Order Discount ($)' },
  { value: 'itemDiscount', label: 'Item Discount ($)' },
  { value: 'employeeName', label: 'Employee Name' },
  { value: 'orderDiscountReason', label: 'Order Discount Reason' },
];

const DISCOUNT_TYPE_OPTIONS: { value: '' | PosDiscountType; label: string }[] = [
  // "All" = order OR item (the default view — shows every discounted order).
  // "Order" / "Item" narrow to a single discount kind. (The old "Both" meant
  // orders that had BOTH kinds, which almost never matched and looked empty.)
  { value: '', label: 'All' },
  { value: 'order', label: 'Order' },
  { value: 'item', label: 'Item' },
];

function discountTypeLabel(type: PosDiscountType): string {
  switch (type) {
    case 'order':
      return 'Order';
    case 'item':
      return 'Item';
    case 'both':
      return 'Both';
  }
}

function PosDiscountReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const locationLabel = selectedLocationName || 'All locations';
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [discountTypeInput, setDiscountTypeInput] = useState<'' | PosDiscountType>('');
  const [applied, setApplied] = useState<{
    dateFrom: string;
    dateTo: string;
    discountType: '' | PosDiscountType;
  }>({
    ...initialRange,
    discountType: '',
  });
  const [sortBy, setSortBy] = useState<PosDiscountSortBy>('orderDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const params = {
    page,
    perPage,
    locationId: selectedLocationId || undefined,
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    discountType: applied.discountType || undefined,
    sortBy,
    sortDir,
  };
  const { data, isLoading } = usePosDiscountReport(params);
  const rows: PosDiscountRow[] = data?.data ?? [];
  const pagination = data?.pagination;

  const handleApply = () => {
    setApplied({
      dateFrom: dateFromInput,
      dateTo: dateToInput,
      discountType: discountTypeInput,
    });
    setPage(1);
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setDiscountTypeInput('');
    setApplied({ ...r, discountType: '' });
    setSortBy('orderDate');
    setSortDir('desc');
    setPage(1);
  };
  const handleSortApply = (field: PosDiscountSortBy, dir: 'asc' | 'desc') => {
    setSortBy(field);
    setSortDir(dir);
    setPage(1);
  };
  const sortActive = !(sortBy === 'orderDate' && sortDir === 'desc');

  const exportRows = async () => {
    setExporting(true);
    try {
      const all: PosDiscountRow[] = [];
      const batch = 500;
      for (let p = 1; ; p++) {
        const res = await api.get<PaginatedResponse<PosDiscountRow>>(
          '/pos/report/discounts',
          withLocalDateRange({
            ...params,
            page: p,
            perPage: batch,
          }) as Record<string, string | number | undefined>,
        );
        all.push(...res.data);
        if (res.data.length < batch || p >= (res.pagination?.totalPages ?? 1)) break;
      }
      const header = [
        'Order ID',
        'Location',
        'Order Date',
        'Order Discount ($)',
        'Item Discount ($)',
        'Item Name',
        'Discount Type',
        'Predefined',
        'Employee Name',
        'Order Discount Reason',
      ];
      const body = all.map((r) => [
        r.dailyOrderNumber ?? r.id,
        r.locationName ?? locationLabel,
        formatPosDateTime(r.createdAt),
        r.orderDiscount.toFixed(2),
        r.itemDiscount.toFixed(2),
        r.itemNames ?? '-',
        discountTypeLabel(r.discountType),
        r.predefined ? 'Yes' : 'No',
        r.employeeName ?? '',
        r.orderDiscountReason ?? 'N.A.',
      ]);
      downloadCsv(reportFilename('discount_report', selectedLocationName), [header, ...body]);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Discount Report"
        description="Monitor all discounts applied — by item, order, or cashier."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={!pagination?.total || exporting} onClick={exportRows}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware) */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Discount Type</label>
            <select
              value={discountTypeInput}
              onChange={(e) => setDiscountTypeInput(e.target.value as '' | PosDiscountType)}
              className="h-9 rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {DISCOUNT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {/* Sort */}
      <div>
        <DiscountSortPopover sortBy={sortBy} sortDir={sortDir} isActive={sortActive} onApply={handleSortApply} />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Order ID</th>
                <th className="px-4 py-3 font-medium">Location</th>
                <th className="px-4 py-3 font-medium">Order Discount ($)</th>
                <th className="px-4 py-3 font-medium">Item Discount ($)</th>
                <th className="px-4 py-3 font-medium">Item Name</th>
                <th className="px-4 py-3 font-medium">Discount Type</th>
                <th className="px-4 py-3 font-medium">Employee Name</th>
                <th className="px-4 py-3 font-medium">Order Discount Reason</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState icon={Percent} title="No discounts" description="Try a different date range." />
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 last:border-b-0">
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setDetailOrderId(r.id)}
                        className="font-medium text-primary hover:underline"
                      >
                        {r.dailyOrderNumber ?? r.id.slice(0, 8)}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div>{r.locationName ?? '—'}</div>
                      <div className="text-xs text-muted-foreground">on {formatPosDateTime(r.createdAt)}</div>
                    </td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(r.orderDiscount)}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(r.itemDiscount)}</td>
                    <td className="px-4 py-3">{r.itemNames ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{discountTypeLabel(r.discountType)}</div>
                      <div className="text-xs text-muted-foreground">Predefined: {r.predefined ? 'Yes' : 'No'}</div>
                    </td>
                    <td className="px-4 py-3">{r.employeeName ?? '—'}</td>
                    <td className="px-4 py-3">{r.orderDiscountReason ?? 'N.A.'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={pagination.perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>

      <OrderDetailsModal
        key={detailOrderId ?? 'closed'}
        open={detailOrderId !== null}
        orderId={detailOrderId}
        onClose={() => setDetailOrderId(null)}
      />
    </div>
  );
}

function DiscountSortPopover({
  sortBy,
  sortDir,
  isActive,
  onApply,
}: {
  sortBy: PosDiscountSortBy;
  sortDir: 'asc' | 'desc';
  isActive: boolean;
  onApply: (field: PosDiscountSortBy, dir: 'asc' | 'desc') => void;
}) {
  const [open, setOpen] = useState(false);
  const [draftField, setDraftField] = useState<PosDiscountSortBy>(sortBy);
  const [draftDir, setDraftDir] = useState<'asc' | 'desc'>(sortDir);
  const ref = useRef<HTMLDivElement | null>(null);

  // React-recommended "adjust state on prop change" pattern: re-sync drafts
  // to the current sortBy/sortDir whenever the popover opens. Done during
  // render (not in an effect) to avoid cascading renders.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDraftField(sortBy);
      setDraftDir(sortDir);
    }
  }

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-2 rounded-lg border border-input bg-white px-3 text-sm hover:bg-muted/40"
      >
        <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
        Sort
        {isActive && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-amber-500" aria-label="Custom sort active" />}
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-lg border border-border bg-white p-2 shadow-lg">
          <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sort by</div>
          <div className="space-y-0.5">
            {DISCOUNT_SORT_OPTIONS.map((opt) => {
              const checked = draftField === opt.value;
              return (
                <label
                  key={opt.value}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                >
                  <input
                    type="radio"
                    name="discount-sort-by"
                    checked={checked}
                    onChange={() => setDraftField(opt.value)}
                    className="h-4 w-4 text-primary focus:ring-primary"
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
          <div className="my-2 border-t border-border" />
          <button
            type="button"
            onClick={() => setDraftDir('asc')}
            className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted ${
              draftDir === 'asc' ? 'text-primary' : ''
            }`}
          >
            <span className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 rotate-180 text-muted-foreground" />A – Z
            </span>
            {draftDir === 'asc' && <span className="h-2 w-2 rounded-full bg-primary" />}
          </button>
          <button
            type="button"
            onClick={() => setDraftDir('desc')}
            className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted ${
              draftDir === 'desc' ? 'text-primary' : ''
            }`}
          >
            <span className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground" />Z – A
            </span>
            {draftDir === 'desc' && <span className="h-2 w-2 rounded-full bg-primary" />}
          </button>
          <div className="mt-2 flex justify-end gap-2 border-t border-border pt-2">
            <Button size="sm" variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                onApply(draftField, draftDir);
                setOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function PosSalesByCashierReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const locationLabel = selectedLocationName || 'All locations';
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const { data: employeesData } = usePosReportEmployees();
  const { data: deptsData } = usePosReportDepartments();
  const { data: categoriesData } = usePosReportCategories();
  const allEmployees = employeesData?.data ?? [];
  const allDepartments = deptsData?.data ?? [];
  const allCategories = categoriesData?.data ?? [];

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [selectedDeptIds, setSelectedDeptIds] = useState<string[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [applied, setApplied] = useState<{
    dateFrom: string;
    dateTo: string;
    employeeIds: string[];
    departmentIds: string[];
    categoryIds: string[];
  }>({
    ...initialRange,
    employeeIds: [],
    departmentIds: [],
    categoryIds: [],
  });
  const [activeEmployeeId, setActiveEmployeeId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Default-select all employees on first load so the chip shows "Employees (42)"
  // like the screenshot.
  const [hasInitEmployees, setHasInitEmployees] = useState(false);
  if (!hasInitEmployees && allEmployees.length > 0) {
    const ids = allEmployees.map((e) => e.id);
    setSelectedEmployeeIds(ids);
    setApplied((prev) => ({ ...prev, employeeIds: ids }));
    setHasInitEmployees(true);
  }

  const cashierFilterParams = {
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    locationId: selectedLocationId || undefined,
    employeeIds: applied.employeeIds.length > 0 ? applied.employeeIds.join(',') : undefined,
    departmentIds: applied.departmentIds.length > 0 ? applied.departmentIds.join(',') : undefined,
    categoryIds: applied.categoryIds.length > 0 ? applied.categoryIds.join(',') : undefined,
  };

  const { data: cashiersData, isLoading: cashiersLoading } = usePosSalesByCashierCashiers(cashierFilterParams);
  const cashiers: PosSalesByCashierCashier[] = cashiersData?.data ?? [];

  // Auto-select the first cashier when the list changes and the current
  // selection is gone.
  useEffect(() => {
    if (cashiers.length === 0) {
      if (activeEmployeeId !== null) setActiveEmployeeId(null);
      return;
    }
    if (!activeEmployeeId || !cashiers.find((c) => c.employeeId === activeEmployeeId)) {
      setActiveEmployeeId(cashiers[0].employeeId);
      setPage(1);
    }
  }, [cashiers, activeEmployeeId]);

  const { data: ordersData, isLoading: ordersLoading } = usePosSalesByCashierOrders({
    employeeId: activeEmployeeId || undefined,
    page,
    perPage,
    ...cashierFilterParams,
  });
  const orders: PosSalesByCashierOrder[] = ordersData?.data ?? [];
  const total = ordersData?.pagination?.total ?? 0;
  const totalPages = ordersData?.pagination?.totalPages ?? 1;

  const activeCashier = cashiers.find((c) => c.employeeId === activeEmployeeId);

  const handleApply = () => {
    setApplied({
      dateFrom: dateFromInput,
      dateTo: dateToInput,
      employeeIds: selectedEmployeeIds,
      departmentIds: selectedDeptIds,
      categoryIds: selectedCategoryIds,
    });
    setPage(1);
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    const empIds = allEmployees.map((e) => e.id);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setSelectedEmployeeIds(empIds);
    setSelectedDeptIds([]);
    setSelectedCategoryIds([]);
    setApplied({ ...r, employeeIds: empIds, departmentIds: [], categoryIds: [] });
    setPage(1);
  };

  const employeeOptions = allEmployees.map((e) => ({ value: e.id, label: e.name }));
  const deptOptions = allDepartments.map((d) => ({ value: d.id, label: d.name }));
  const categoryOptions = allCategories.map((c) => ({ value: c.id, label: c.name }));

  const exportOrders = async () => {
    if (!activeCashier) return;
    setExporting(true);
    try {
      const all: PosSalesByCashierOrder[] = [];
      const batch = 500;
      for (let p = 1; ; p++) {
        const res = await api.get<PaginatedResponse<PosSalesByCashierOrder>>(
          '/pos/report/sales-by-cashier/orders',
          withLocalDateRange({
            employeeId: activeCashier.employeeId,
            page: p,
            perPage: batch,
            ...cashierFilterParams,
          }) as Record<string, string | number | undefined>,
        );
        all.push(...res.data);
        if (res.data.length < batch || p >= (res.pagination?.totalPages ?? 1)) break;
      }
      const header = [
        'Location',
        'Cashier',
        'Order ID',
        'Local ID/Order No.',
        'Order Date',
        'POS Station',
        'Sales ($)',
      ];
      const body = all.map((o) => [
        locationLabel,
        activeCashier.employeeName,
        o.dailyOrderNumber ?? '',
        o.id,
        formatPosDate(o.createdAt),
        o.stationName,
        o.total.toFixed(2),
      ]);
      const slug = `sales_by_cashier_${activeCashier.employeeName}`;
      downloadCsv(reportFilename(slug, selectedLocationName), [header, ...body]);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Sales By Cashier"
        description="View how much each cashier has sold — helps identify your top performers."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={!activeCashier || exporting} onClick={exportOrders}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware) */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <div className="min-w-[200px]">
            <MultiSelect
              options={employeeOptions}
              selected={selectedEmployeeIds}
              onChange={setSelectedEmployeeIds}
              triggerLabel="Employees"
              searchPlaceholder="Search employees..."
              emptyMessage="No employees"
            />
          </div>
          <div className="min-w-[200px]">
            <MultiSelect
              options={deptOptions}
              selected={selectedDeptIds}
              onChange={setSelectedDeptIds}
              triggerLabel="Departments"
              searchPlaceholder="Search departments..."
              emptyMessage="No departments"
            />
          </div>
          <div className="min-w-[200px]">
            <MultiSelect
              options={categoryOptions}
              selected={selectedCategoryIds}
              onChange={setSelectedCategoryIds}
              triggerLabel="Categories"
              searchPlaceholder="Search categories..."
              emptyMessage="No categories"
            />
          </div>
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        {/* Cashier rail */}
        <div className="overflow-hidden rounded-xl border border-border bg-white">
          {cashiersLoading ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : cashiers.length === 0 ? (
            <EmptyState
              icon={User}
              title="No cashiers with sales"
              description="Try a different date range."
              className="py-8"
            />
          ) : (
            <ul role="tablist" aria-label="Cashiers">
              {cashiers.map((c) => {
                const isSelected = c.employeeId === activeEmployeeId;
                return (
                  <li key={c.employeeId} className="border-b border-border/60 last:border-b-0">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={isSelected}
                      onClick={() => {
                        setActiveEmployeeId(c.employeeId);
                        setPage(1);
                      }}
                      className={cn(
                        'flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors',
                        isSelected
                          ? 'border-l-4 border-primary bg-primary/5'
                          : 'border-l-4 border-transparent hover:bg-muted/40',
                      )}
                    >
                      <div>
                        <div className="text-sm font-medium text-foreground">{c.employeeName}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {c.ordersCount.toLocaleString()} orders
                        </div>
                      </div>
                      <div className="font-mono text-sm font-semibold text-foreground">
                        ${formatCurrencyPlain(c.totalSales)}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Orders pane */}
        <div className="overflow-hidden rounded-xl border border-border bg-white">
          <div className="border-b border-border px-5 py-3">
            <div className="text-sm font-semibold text-foreground">
              Cashier: <span className="font-normal">{activeCashier?.employeeName ?? '—'}</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Sr. No.</th>
                  <th className="px-4 py-3 font-medium">Order ID</th>
                  <th className="px-4 py-3 font-medium">Local ID/Order No.</th>
                  <th className="px-4 py-3 font-medium">Order Date</th>
                  <th className="px-4 py-3 font-medium">POS Station</th>
                  <th className="px-4 py-3 font-medium">Sales ($)</th>
                </tr>
              </thead>
              <tbody>
                {!activeCashier ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                      Select a cashier to view orders.
                    </td>
                  </tr>
                ) : ordersLoading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <EmptyState
                        icon={ShoppingCart}
                        title="No orders for this cashier"
                        description="Try changing the filters."
                      />
                    </td>
                  </tr>
                ) : (
                  orders.map((o, idx) => (
                    <tr key={o.id} className="border-b border-border/60 last:border-b-0">
                      <td className="px-4 py-3">{(page - 1) * perPage + idx + 1}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setDetailOrderId(o.id)}
                          className="font-medium text-primary hover:underline"
                        >
                          {o.dailyOrderNumber ?? o.id.slice(0, 8)}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{o.id}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {formatPosDate(o.createdAt)}
                      </td>
                      <td className="px-4 py-3">{o.stationName}</td>
                      <td className="px-4 py-3 font-mono">{formatCurrencyPlain(o.total)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div className="border-t border-border">
              <DataTablePagination
                page={page}
                totalPages={totalPages}
                total={total}
                perPage={perPage}
                onPageChange={setPage}
                perPageOptions={[10, 25, 50, 100]}
                onPerPageChange={(n) => {
                  setPerPage(n);
                  setPage(1);
                }}
              />
            </div>
          )}
        </div>
      </div>

      <OrderDetailsModal
        key={detailOrderId ?? 'closed'}
        open={detailOrderId !== null}
        orderId={detailOrderId}
        onClose={() => setDetailOrderId(null)}
      />
    </div>
  );
}

const PROMOTION_SORT_OPTIONS: { value: PosPromotionSortBy; label: string }[] = [
  { value: 'lastUsedAt', label: 'Last used' },
  { value: 'totalUses', label: 'Most used' },
  { value: 'totalDiscount', label: 'Highest discount given' },
  { value: 'grossSales', label: 'Highest gross sales' },
  { value: 'name', label: 'Promotion name (A→Z)' },
];

function PosPromotionReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const locationLabel = selectedLocationName || 'All locations';
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [searchInput, setSearchInput] = useState('');
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string; search: string }>({
    ...initialRange,
    search: '',
  });
  const [sortBy, setSortBy] = useState<PosPromotionSortBy>('lastUsedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [exporting, setExporting] = useState(false);

  const params = {
    page,
    perPage,
    locationId: selectedLocationId || undefined,
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    search: applied.search || undefined,
    sortBy,
    sortDir,
  };
  const { data, isLoading } = usePosPromotionReport(params);
  const rows: PosPromotionUsageRow[] = data?.data ?? [];
  const pagination = data?.pagination;

  const totalsAcrossPage = rows.reduce(
    (acc, r) => {
      acc.uses += r.totalUses;
      acc.orders += r.totalOrders;
      acc.discount += r.totalDiscount;
      acc.gross += r.grossSales;
      return acc;
    },
    { uses: 0, orders: 0, discount: 0, gross: 0 },
  );

  const handleApply = () => {
    setApplied({ dateFrom: dateFromInput, dateTo: dateToInput, search: searchInput });
    setPage(1);
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setSearchInput('');
    setApplied({ ...r, search: '' });
    setSortBy('lastUsedAt');
    setSortDir('desc');
    setPage(1);
  };

  const exportRows = async () => {
    setExporting(true);
    try {
      const all: PosPromotionUsageRow[] = [];
      const batch = 500;
      for (let p = 1; ; p++) {
        const res = await api.get<PaginatedResponse<PosPromotionUsageRow>>(
          '/pos/report/promotions',
          withLocalDateRange({
            ...params,
            page: p,
            perPage: batch,
          }) as Record<string, string | number | undefined>,
        );
        all.push(...res.data);
        if (res.data.length < batch || p >= (res.pagination?.totalPages ?? 1)) break;
      }
      const header = [
        'Promotion Name',
        'Promotion ID',
        'Times Applied',
        'Orders Using It',
        'Discount Given ($)',
        'Gross Sales on These Lines ($)',
        'First Used',
        'Last Used',
      ];
      const body = all.map((r) => [
        r.promotionName ?? r.promotionId,
        r.promotionId,
        String(r.totalUses),
        String(r.totalOrders),
        r.totalDiscount.toFixed(2),
        r.grossSales.toFixed(2),
        r.firstUsedAt ? new Date(r.firstUsedAt).toLocaleString() : '',
        r.lastUsedAt ? new Date(r.lastUsedAt).toLocaleString() : '',
      ]);
      downloadCsv(reportFilename('promotion_usage', selectedLocationName), [header, ...body]);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Promotion Usage"
        description={`How often each promotion fired and how much discount it gave. Scope: ${locationLabel}.`}
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={!pagination?.total || exporting} onClick={exportRows}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware) */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <div className="inline-flex items-center gap-2 self-end rounded-md border border-input bg-white px-2.5 py-1.5 text-sm">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search promotion name"
              className="bg-transparent text-sm focus:outline-none"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value as PosPromotionSortBy);
              setPage(1);
            }}
            className="h-9 rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            {PROMOTION_SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                Sort: {opt.label}
              </option>
            ))}
          </select>
          <select
            value={sortDir}
            onChange={(e) => {
              setSortDir(e.target.value as 'asc' | 'desc');
              setPage(1);
            }}
            className="h-9 rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {/* Summary strip (current page totals) */}
      {!isLoading && rows.length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <PromoUsageStat label="Times Applied (page)" value={totalsAcrossPage.uses.toLocaleString()} />
          <PromoUsageStat label="Orders Using It (page)" value={totalsAcrossPage.orders.toLocaleString()} />
          <PromoUsageStat label="Discount Given (page)" value={formatCurrencyPlain(totalsAcrossPage.discount)} />
          <PromoUsageStat label="Gross Sales (page)" value={formatCurrencyPlain(totalsAcrossPage.gross)} />
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Promotion</th>
                <th className="px-4 py-3 font-medium">Times Applied</th>
                <th className="px-4 py-3 font-medium">Orders</th>
                <th className="px-4 py-3 font-medium">Discount Given</th>
                <th className="px-4 py-3 font-medium">Gross Sales</th>
                <th className="px-4 py-3 font-medium">Last Used</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState icon={Sparkles} title="No promotions fired" description="Try a different date range." />
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.promotionId} className="border-b border-border/60 last:border-b-0">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{r.promotionName ?? 'Unnamed promotion'}</div>
                      <div className="text-xs text-muted-foreground">ID: {r.promotionId.slice(0, 8)}</div>
                    </td>
                    <td className="px-4 py-3 font-mono">{r.totalUses.toLocaleString()}</td>
                    <td className="px-4 py-3 font-mono">{r.totalOrders.toLocaleString()}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(r.totalDiscount)}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(r.grossSales)}</td>
                    <td className="px-4 py-3">
                      {r.lastUsedAt
                        ? new Date(r.lastUsedAt).toLocaleString('en-US', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={pagination.perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function PromoUsageStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-white px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold text-foreground">{value}</div>
    </div>
  );
}

function PosStoreCreditsReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();

  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Debounce search.
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const params = {
    page,
    perPage,
    locationId: selectedLocationId || undefined,
    search: appliedSearch || undefined,
  };
  const { data, isLoading } = usePosStoreCredits(params);
  const rows: PosStoreCreditRow[] = data?.data ?? [];
  const pagination = data?.pagination;

  const exportRows = async () => {
    setExporting(true);
    try {
      const all: PosStoreCreditRow[] = [];
      const batch = 500;
      for (let p = 1; ; p++) {
        const res = await api.get<PaginatedResponse<PosStoreCreditRow>>('/pos/report/store-credits', {
          ...params,
          page: p,
          perPage: batch,
        } as Record<string, string | number | undefined>);
        all.push(...res.data);
        if (res.data.length < batch || p >= (res.pagination?.totalPages ?? 1)) break;
      }
      const header = ['Code', 'Initial Amount ($)', 'Balance ($)', 'User'];
      const body = all.map((r) => [r.code, r.initialAmount.toFixed(2), r.balance.toFixed(2), r.createdByName ?? '']);
      downloadCsv(reportFilename('store_credits', selectedLocationName), [header, ...body]);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Store Credits"
        description="Track refunds issued as store credit instead of cash."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={!pagination?.total || exporting} onClick={exportRows}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Search */}
      <div className="inline-flex max-w-sm items-center gap-2 rounded-md border border-input bg-white px-2.5 py-1.5 text-sm">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search"
          className="w-full bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
        />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Initial Amount ($)</th>
                <th className="px-4 py-3 font-medium">Balance ($)</th>
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <EmptyState
                      icon={Wallet}
                      title="No store credits found"
                      description="Try a different search or filter."
                    />
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 last:border-b-0">
                    <td className="px-4 py-3 font-mono">{maskStoreCreditCode(r.code)}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(r.initialAmount)}</td>
                    <td className="px-4 py-3 font-mono">{formatCurrencyPlain(r.balance)}</td>
                    <td className="px-4 py-3">{r.createdByName ?? '—'}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setDetailId(r.id)}
                        aria-label="View store credit details"
                        className="rounded-md border border-border p-1.5 text-primary hover:bg-primary/5"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > 0 && (
          <div className="border-t border-border">
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={pagination.perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>

      <StoreCreditDetailsModal
        key={detailId ?? 'closed'}
        open={detailId !== null}
        storeCreditId={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
}

function maskStoreCreditCode(code: string): string {
  // Display only the last 4 characters, matching the screenshot's
  // "XXXXXXXX9500" formatting.
  if (!code) return '';
  const visible = code.slice(-4);
  const masked = 'X'.repeat(Math.max(0, code.length - 4));
  return `${masked}${visible}`;
}

function storeCreditAuditTypeBadge(type: string): { label: string; classes: string } {
  switch (type.toLowerCase()) {
    case 'credit':
      return { label: 'Credit', classes: 'bg-green-100 text-green-700' };
    case 'debit':
      return { label: 'Debit', classes: 'bg-red-100 text-red-700' };
    default:
      return { label: type, classes: 'bg-gray-100 text-gray-700' };
  }
}

function StoreCreditDetailsModal({
  open,
  storeCreditId,
  onClose,
}: {
  open: boolean;
  storeCreditId: string | null;
  onClose: () => void;
}) {
  const [auditPage, setAuditPage] = useState(1);
  const [auditPerPage, setAuditPerPage] = useState(10);
  const { data, isLoading } = usePosStoreCreditDetail(storeCreditId, {
    page: auditPage,
    perPage: auditPerPage,
  });
  const credit = data?.data;
  const audit: PosStoreCreditAuditRow[] = data?.audit ?? [];
  const pagination = data?.pagination;

  return (
    <Modal open={open} onClose={onClose} title="Store Credit Details" widthClass="max-w-[1100px]">
      {isLoading || !credit ? (
        <div className="flex h-40 items-center justify-center text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-5">
          {/* Top stats */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <OrderField label="Initial Amount" value={`$${formatCurrencyPlain(credit.initialAmount)}`} />
            <OrderField label="Current Balance" value={`$${formatCurrencyPlain(credit.balance)}`} />
            <OrderField label="Code" value={<span className="font-mono">{credit.code}</span>} />
            <OrderField label="Payment Type" value={storeCreditPaymentTypeLabel(credit.paymentType)} />
          </div>

          {/* Basic Details card */}
          <div className="rounded-xl border border-border bg-white">
            <div className="flex items-center gap-3 border-b border-border px-5 py-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <CreditCard className="h-5 w-5" />
              </span>
              <h3 className="text-sm font-semibold text-foreground">Basic Details</h3>
            </div>
            <div className="grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-2 lg:grid-cols-3">
              <OrderField label="Created by" value={credit.createdByName ?? '—'} />
              <OrderField label="Date Created (UTC)" value={formatUtcDateTime(credit.createdAt)} />
              <OrderField label="Payment Type" value={storeCreditPaymentTypeLabel(credit.paymentType)} />
            </div>
          </div>

          {/* Audit trail */}
          <div className="rounded-xl border border-border bg-white">
            <div className="flex items-center gap-3 border-b border-border px-5 py-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Search className="h-5 w-5" />
              </span>
              <h3 className="text-sm font-semibold text-foreground">Store Credit Audit Trails</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Sr. no.</th>
                    <th className="px-4 py-3 font-medium">Date (UTC)</th>
                    <th className="px-4 py-3 font-medium">Description</th>
                    <th className="px-4 py-3 font-medium">Amount ($)</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Balance ($)</th>
                    <th className="px-4 py-3 font-medium">User</th>
                    <th className="px-4 py-3 font-medium">Order Number</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.length === 0 ? (
                    <tr>
                      <td colSpan={8}>
                        <EmptyState
                          icon={History}
                          title="No audit entries"
                          description="Changes to this store credit will appear here."
                        />
                      </td>
                    </tr>
                  ) : (
                    audit.map((a, idx) => {
                      const typeBadge = storeCreditAuditTypeBadge(a.type);
                      return (
                        <tr key={a.id} className="border-b border-border/60 last:border-b-0">
                          <td className="px-4 py-3">
                            {(pagination ? (pagination.page - 1) * pagination.perPage : 0) + idx + 1}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                            {formatUtcDateTimeWithAt(a.occurredAt)}
                          </td>
                          <td className="px-4 py-3 max-w-xs">{a.description}</td>
                          <td className="px-4 py-3 font-mono">{formatCurrencyPlain(a.amount)}</td>
                          <td className="px-4 py-3">
                            <span
                              className={cn(
                                'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                                typeBadge.classes,
                              )}
                            >
                              {typeBadge.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-mono">{formatCurrencyPlain(a.balance)}</td>
                          <td className="px-4 py-3">{a.employeeName ?? '—'}</td>
                          <td className="px-4 py-3 font-mono">{a.orderNumber ?? 'N/A'}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {pagination && pagination.total > 0 && (
              <div className="border-t border-border">
                <DataTablePagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  total={pagination.total}
                  perPage={pagination.perPage}
                  onPageChange={setAuditPage}
                  perPageOptions={[10, 25, 50, 100]}
                  onPerPageChange={(n) => {
                    setAuditPerPage(n);
                    setAuditPage(1);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function storeCreditPaymentTypeLabel(value: string): string {
  if (!value) return 'Store Credit';
  return value
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ');
}

function formatUtcDateTime(iso: string): string {
  // "04 May, 2026 12:47 AM" — matches the screenshot's UTC format.
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatUtcDateTimeWithAt(iso: string): string {
  // "May 04, 2026 at 06:17 AM"
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const date = d.toLocaleString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
  const time = d.toLocaleString('en-US', {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${date} at ${time}`;
}

/**
 * Resolve a scanner's input to a single product. A barcode scanner ends its
 * burst with Enter, so on Enter we take an exact primary-barcode hit first and
 * fall back to a lone result — anything ambiguous is left for the user to pick
 * rather than guessing wrong on a cost-price report.
 */
function findScanMatch<T extends { id: string; name: string; primaryBarcode: string | null }>(
  results: T[],
  term: string,
): T | null {
  const t = term.trim().toLowerCase();
  if (!t) return null;
  const exact = results.find((p) => (p.primaryBarcode ?? '').toLowerCase() === t);
  if (exact) return exact;
  return results.length === 1 ? results[0] : null;
}

/** Savings cell: the order's total savings, with what made it up underneath. */
function SavingsCell({ order }: { order: PosPurchaseHistoryOrder }) {
  const b = order.savingsBreakdown;
  const parts = [
    { label: 'Promotions', value: b.promotions },
    { label: b.couponCode ? `Coupon (${b.couponCode})` : 'Coupon', value: b.coupon },
    { label: 'Rewards', value: b.rewards },
    { label: 'Order discount', value: b.manual },
  ].filter((p) => p.value > 0);
  if (order.savings <= 0) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="text-right">
      <div className="font-medium tabular-nums text-success">{formatCurrency(order.savings)}</div>
      {parts.length > 0 && (
        <div className="text-[11px] leading-snug text-muted-foreground">
          {parts.map((p) => `${p.label} ${formatCurrency(p.value)}`).join(' · ')}
        </div>
      )}
    </div>
  );
}

const PURCHASE_STATUS_LABELS: Record<string, string> = {
  paid: 'Paid',
  completed: 'Completed',
  refunded: 'Refunded',
  partial_refund: 'Partially refunded',
};

/**
 * Customer Purchase History — pick a customer (name, email or phone), pick a
 * range, and see every completed order they placed in it with what they saved,
 * each one opening the shared order details modal.
 */
function PosCustomerPurchaseHistoryReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selected, setSelected] = useState<PosPurchaseHistoryCustomer | null>(null);

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [applied, setApplied] = useState(initialRange);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Debounce the customer search so each keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: customersData, isLoading: customersLoading } = usePosPurchaseHistoryCustomers(
    dropdownOpen ? searchTerm : '',
  );
  const customerResults = customersData?.data ?? [];

  const baseParams = {
    locationId: selectedLocationId || undefined,
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
  };
  const { data: historyData, isLoading: historyLoading } = usePosCustomerPurchaseHistory(
    selected?.id,
    { ...baseParams, page, perPage },
    reportTz,
  );
  const history: PosPurchaseHistoryData | undefined = historyData?.data;
  const orders = history?.orders ?? [];
  const pagination = historyData?.pagination;

  const selectCustomer = (c: PosPurchaseHistoryCustomer) => {
    setSelected(c);
    setDropdownOpen(false);
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
  };

  const handleApply = () => {
    setApplied({ dateFrom: dateFromInput, dateTo: dateToInput });
    setPage(1);
  };
  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setApplied(r);
    setPage(1);
  };

  // Export every order in the range, not just the page on screen.
  const handleExport = async () => {
    if (!selected) return;
    setExporting(true);
    try {
      const res = await api.get<{ data: PosPurchaseHistoryData }>(
        '/pos/report/customer-purchase-history',
        withLocalDateRange({ customerId: selected.id, ...baseParams, page: 1, perPage: 1000 }, reportTz) as Record<
          string,
          string | number | undefined
        >,
      );
      const header = [
        'Customer',
        'Phone',
        'Email',
        'Order No.',
        'Order Date',
        'Location',
        'Cashier',
        'Items',
        'Payment Types',
        'Subtotal ($)',
        'Savings ($)',
        'Promotions ($)',
        'Coupon ($)',
        'Coupon Code',
        'Rewards ($)',
        'Order Discount ($)',
        'Tax ($)',
        'Tip ($)',
        'Total ($)',
        'Status',
      ];
      const body = res.data.orders.map((o) => [
        selected.name,
        selected.phone,
        selected.email ?? '',
        o.dailyOrderNumber ?? '',
        formatDateTime(o.createdAt),
        o.locationName ?? '',
        o.employeeName ?? '',
        String(o.itemCount),
        o.paymentMethods ?? '',
        o.subtotal.toFixed(2),
        o.savings.toFixed(2),
        o.savingsBreakdown.promotions.toFixed(2),
        o.savingsBreakdown.coupon.toFixed(2),
        o.savingsBreakdown.couponCode ?? '',
        o.savingsBreakdown.rewards.toFixed(2),
        o.savingsBreakdown.manual.toFixed(2),
        o.tax.toFixed(2),
        o.tip.toFixed(2),
        o.total.toFixed(2),
        PURCHASE_STATUS_LABELS[o.status] ?? o.status,
      ]);
      downloadCsv(reportFilename(`customer_purchase_history_${selected.name}`, selectedLocationName), [
        header,
        ...body,
      ]);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Customer Purchase History"
        description="Search a customer by name, email or phone to see their orders, savings and order details."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button
            variant="primary"
            size="sm"
            disabled={!selected || orders.length === 0 || exporting}
            onClick={handleExport}
          >
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Filters</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {/* Customer search — name, email or phone */}
          <div className="flex w-full flex-col gap-1 sm:w-80">
            <label className="text-xs text-muted-foreground">Customer</label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                placeholder={selected ? `${selected.name} — search to change` : 'Name, email or phone…'}
                className="h-9 w-full rounded-lg border border-input bg-white pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
              {dropdownOpen && searchTerm.length >= 1 && (
                <div className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-border bg-white shadow-lg">
                  {customersLoading ? (
                    <div className="px-3 py-3 text-sm text-muted-foreground">Searching…</div>
                  ) : customerResults.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-muted-foreground">No customers found.</div>
                  ) : (
                    customerResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectCustomer(c)}
                        className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-muted/50"
                      >
                        <span className="font-medium text-foreground">{c.name || c.businessName || c.phone}</span>
                        <span className="text-xs text-muted-foreground">
                          {[c.phone, c.email].filter(Boolean).join(' · ')}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {!selected ? (
        <div className="rounded-xl border border-border bg-white">
          <EmptyState
            icon={Users}
            title="Choose a customer"
            description="Search by name, email or phone to see their orders and savings for the selected dates."
          />
        </div>
      ) : (
        <>
          {/* Customer + period summary */}
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3 rounded-xl border border-border bg-white px-5 py-4">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-foreground">
                {selected.name || selected.businessName || selected.phone}
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {[selected.phone, selected.email, selected.businessName && selected.name ? selected.businessName : null]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <div className="ml-auto flex flex-wrap gap-x-8 gap-y-2">
              <div>
                <div className="text-xs text-muted-foreground">Orders</div>
                <div className="text-lg font-semibold tabular-nums text-foreground">
                  {history ? history.summary.orders.toLocaleString() : '—'}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Total Spent</div>
                <div className="text-lg font-semibold tabular-nums text-foreground">
                  {history ? formatCurrency(history.summary.totalSpent) : '—'}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Total Savings</div>
                <div className="text-lg font-semibold tabular-nums text-success">
                  {history ? formatCurrency(history.summary.totalSavings) : '—'}
                </div>
              </div>
              {history && (
                <div>
                  <div className="text-xs text-muted-foreground">Loyalty Points</div>
                  <div className="text-lg font-semibold tabular-nums text-foreground">
                    {history.customer.loyaltyPoints.toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-border bg-white">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-border bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">
                  <th className="px-4 py-3">Order No.</th>
                  <th className="px-4 py-3">Order Date</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Cashier</th>
                  <th className="px-4 py-3 text-right">Items</th>
                  <th className="px-4 py-3">Payment Types</th>
                  <th className="px-4 py-3 text-right">Subtotal</th>
                  <th className="px-4 py-3 text-right">Savings</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-muted-foreground">
                      Loading orders…
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={11}>
                      <EmptyState
                        icon={ShoppingCart}
                        title="No orders in this period"
                        description="This customer has no completed orders for the selected dates. Try a wider date range."
                      />
                    </td>
                  </tr>
                ) : (
                  orders.map((o) => (
                    <tr key={o.id} className="border-b border-border last:border-0 hover:bg-muted/40">
                      <td className="px-4 py-3 font-medium text-foreground">
                        #{o.dailyOrderNumber ?? o.id.slice(0, 8)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                        {formatDateTime(o.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{o.locationName || '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{o.employeeName || '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{o.itemCount.toLocaleString()}</td>
                      <td className="px-4 py-3 capitalize text-muted-foreground">{o.paymentMethods || '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(o.subtotal)}</td>
                      <td className="px-4 py-3">
                        <SavingsCell order={o} />
                      </td>
                      <td className="px-4 py-3 text-right font-medium tabular-nums text-foreground">
                        {formatCurrency(o.total)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                            o.status === 'refunded' || o.status === 'partial_refund'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-green-100 text-green-800',
                          )}
                        >
                          {PURCHASE_STATUS_LABELS[o.status] ?? o.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setDetailOrderId(o.id)}
                          aria-label="View order details"
                          title="View order details"
                          className="rounded-md border border-border p-1.5 text-primary hover:bg-primary/5"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {pagination && pagination.total > 0 && (
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={[10, 25, 50, 100]}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          )}
          {history && orders.some((o) => o.status === 'refunded') && (
            <p className="text-xs text-muted-foreground">
              Fully refunded orders are listed but left out of Orders, Total Spent and Total Savings.
            </p>
          )}
        </>
      )}

      <OrderDetailsModal
        key={detailOrderId ?? 'closed'}
        open={detailOrderId !== null}
        orderId={detailOrderId}
        onClose={() => setDetailOrderId(null)}
      />
    </div>
  );
}

function PosVendorByProductReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selected, setSelected] = useState<{ id: string; name: string; primaryBarcode: string | null } | null>(null);

  // Debounce the product search so each keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: productsData, isLoading: productsLoading } = useProducts(
    { search: searchTerm || undefined, perPage: 10 },
    { enabled: dropdownOpen && searchTerm.length >= 1 },
  );
  const productResults: Array<{ id: string; name: string; primaryBarcode: string | null }> = productsData?.data ?? [];

  const { data: vendorData, isLoading: vendorsLoading } = usePosVendorByProduct(selected?.id, {
    locationId: selectedLocationId || undefined,
  });
  const vendors: PosProductVendor[] = vendorData?.data?.vendors ?? [];

  // Selecting wipes the search box and dropdown, so a following scan starts
  // from a clean field instead of appending to the previous product's text.
  const selectProduct = (p: { id: string; name: string; primaryBarcode: string | null }) => {
    setSelected(p);
    setDropdownOpen(false);
    setSearchInput('');
    setSearchTerm('');
  };

  // Scanner terminator: resolve straight to the product when unambiguous.
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const match = findScanMatch(productResults, searchInput);
    if (match) selectProduct(match);
  };

  const handleExport = () => {
    if (!selected) return;
    const header = [
      'Vendor Name',
      'Vendor SKU Code',
      'Vendor Product Name',
      'Unit',
      'Cost Price ($)',
      'Last Price Update',
    ];
    const body = vendors.map((v) => [
      v.vendorName,
      v.vendorSku,
      v.vendorProductName,
      v.unitName,
      v.costPrice == null ? '' : v.costPrice.toFixed(2),
      v.lastPriceUpdate ? formatDateTime(v.lastPriceUpdate) : '',
    ]);
    downloadCsv(reportFilename(`vendor_by_product_${selected.name}`, selectedLocationName), [header, ...body]);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Vendor by Product"
        description="Search a product to see every vendor supplying it, with SKU code, cost price and last update."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={!selected || vendors.length === 0} onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Product search — name or barcode, scanner-friendly */}
      <div className="relative w-full max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            setDropdownOpen(true);
          }}
          onKeyDown={handleSearchKeyDown}
          onFocus={() => setDropdownOpen(true)}
          placeholder={
            selected
              ? `${selected.name} — search or scan to change product`
              : 'Search or scan a product by name or barcode…'
          }
          className="h-10 w-full rounded-lg border border-input bg-white pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
        {dropdownOpen && searchTerm.length >= 1 && (
          <div className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-border bg-white shadow-lg">
            {productsLoading ? (
              <div className="px-3 py-3 text-sm text-muted-foreground">Searching…</div>
            ) : productResults.length === 0 ? (
              <div className="px-3 py-3 text-sm text-muted-foreground">No products found.</div>
            ) : (
              productResults.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => selectProduct(p)}
                  className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-muted/50"
                >
                  <span className="font-medium text-foreground">{p.name}</span>
                  {p.primaryBarcode && <span className="text-xs text-muted-foreground">{p.primaryBarcode}</span>}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {!selected ? (
        <div className="rounded-xl border border-dashed border-border bg-white p-12 text-center text-sm text-muted-foreground">
          Search or scan a product above to view its vendors, cost prices and last price update.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-border bg-white px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-foreground">{selected.name}</div>
              {selected.primaryBarcode && (
                <div className="text-xs text-muted-foreground">{selected.primaryBarcode}</div>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{vendors.length}</span> vendor
              {vendors.length !== 1 ? 's' : ''}
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-border bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                  <th className="px-4 py-2.5 font-medium">Vendor Name</th>
                  <th className="px-4 py-2.5 font-medium">Vendor SKU Code</th>
                  <th className="px-4 py-2.5 font-medium">Vendor Product Name</th>
                  <th className="px-4 py-2.5 font-medium">Unit</th>
                  <th className="px-4 py-2.5 text-right font-medium">Cost Price</th>
                  <th className="px-4 py-2.5 font-medium">Last Price Update</th>
                </tr>
              </thead>
              <tbody>
                {vendorsLoading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      Loading vendors…
                    </td>
                  </tr>
                ) : vendors.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <EmptyState
                        icon={Truck}
                        title="No linked vendors"
                        description={`No vendors are linked to this product${selectedLocationName ? ` at ${selectedLocationName}` : ''}.`}
                      />
                    </td>
                  </tr>
                ) : (
                  vendors.map((v) => (
                    <tr key={`${v.payeeId}-${v.locationId ?? 'all'}`} className="border-b border-border last:border-0">
                      <td className="px-4 py-2.5 font-medium text-foreground">{v.vendorName}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{v.vendorSku || '—'}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{v.vendorProductName || '—'}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{v.unitName || '—'}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-foreground">
                        {v.costPrice == null ? '—' : `$${v.costPrice.toFixed(2)}`}
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">
                        {v.lastPriceUpdate ? formatDateTime(v.lastPriceUpdate) : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function PosPriceHistoryReport({ onBack }: { onBack: () => void }) {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selected, setSelected] = useState<{ id: string; name: string; primaryBarcode: string | null } | null>(null);
  const [priceTypeFilter, setPriceTypeFilter] = useState<'' | 'cost' | 'selling'>('');

  // Debounce the product search so each keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: productsData, isLoading: productsLoading } = useProducts(
    { search: searchTerm || undefined, perPage: 10 },
    { enabled: dropdownOpen && searchTerm.length >= 1 },
  );
  const productResults: Array<{ id: string; name: string; primaryBarcode: string | null }> = productsData?.data ?? [];

  const { data: historyData, isLoading: historyLoading } = useProductPriceHistory(selected?.id, { perPage: 1000 });
  const { data: usersData } = useUsers({ perPage: 100 });
  const { data: locationsData } = useLocations();

  const userMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of usersData?.data ?? []) map.set(u.id, u.name || u.email);
    return map;
  }, [usersData]);
  const locationNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of locationsData?.data ?? []) map.set(l.id, l.nickname || l.name);
    return map;
  }, [locationsData]);

  const allHistory: Array<Record<string, unknown>> = historyData?.data ?? [];
  const history = useMemo(() => {
    if (!priceTypeFilter) return allHistory;
    return allHistory.filter((e) => e.priceType === priceTypeFilter);
  }, [allHistory, priceTypeFilter]);

  const costCount = allHistory.filter((e) => e.priceType === 'cost').length;
  const sellingCount = allHistory.filter((e) => e.priceType === 'selling').length;

  const selectProduct = (p: { id: string; name: string; primaryBarcode: string | null }) => {
    setSelected(p);
    setDropdownOpen(false);
    setSearchInput('');
    setSearchTerm('');
    setPriceTypeFilter('');
  };

  const handleExport = () => {
    if (!selected) return;
    const header = [
      'Modified At',
      'Type',
      'Unit',
      'Location',
      'Old Price ($)',
      'New Price ($)',
      'Change ($)',
      'Changed By',
    ];
    const body = history.map((e) => {
      const hasOld = e.oldValue != null && e.oldValue !== '';
      const oldVal = hasOld ? parseFloat(String(e.oldValue)) : 0;
      const newVal = parseFloat(String(e.newValue)) || 0;
      const diff = newVal - oldVal;
      return [
        e.changedAt ? formatDateTime(String(e.changedAt)) : '',
        e.priceType === 'cost' ? 'Cost' : 'Selling',
        (e.unitName as string) ?? 'product',
        e.locationId ? (locationNames.get(String(e.locationId)) ?? '') : '',
        hasOld ? oldVal.toFixed(2) : '',
        newVal.toFixed(2),
        diff.toFixed(2),
        e.changedByName ? String(e.changedByName) : e.changedBy ? (userMap.get(String(e.changedBy)) ?? '') : '',
      ];
    });
    downloadCsv(reportFilename(`price_history_${selected.name}`, undefined), [header, ...body]);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Price History"
        description="Search a product to see its full cost and selling price change history."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={!selected || history.length === 0} onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Product search */}
      <div className="relative w-full max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            setDropdownOpen(true);
          }}
          onFocus={() => setDropdownOpen(true)}
          placeholder={
            selected ? `${selected.name} — search to change product` : 'Search a product by name or barcode…'
          }
          className="h-10 w-full rounded-lg border border-input bg-white pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
        {dropdownOpen && searchTerm.length >= 1 && (
          <div className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-border bg-white shadow-lg">
            {productsLoading ? (
              <div className="px-3 py-3 text-sm text-muted-foreground">Searching…</div>
            ) : productResults.length === 0 ? (
              <div className="px-3 py-3 text-sm text-muted-foreground">No products found.</div>
            ) : (
              productResults.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => selectProduct(p)}
                  className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-muted/50"
                >
                  <span className="font-medium text-foreground">{p.name}</span>
                  {p.primaryBarcode && <span className="text-xs text-muted-foreground">{p.primaryBarcode}</span>}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {!selected ? (
        <div className="rounded-xl border border-dashed border-border bg-white p-12 text-center text-sm text-muted-foreground">
          Search and select a product above to view its cost and selling price history.
        </div>
      ) : (
        <>
          {/* Selected product + summary */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-border bg-white px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-foreground">{selected.name}</div>
              {selected.primaryBarcode && (
                <div className="text-xs text-muted-foreground">{selected.primaryBarcode}</div>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              <span className="font-medium text-purple-700">{costCount}</span> cost change
              {costCount !== 1 ? 's' : ''}
            </div>
            <div className="text-xs text-muted-foreground">
              <span className="font-medium text-blue-700">{sellingCount}</span> selling change
              {sellingCount !== 1 ? 's' : ''}
            </div>
            <select
              value={priceTypeFilter}
              onChange={(e) => setPriceTypeFilter(e.target.value as '' | 'cost' | 'selling')}
              className="ml-auto h-9 rounded-lg border border-input bg-background px-3 text-sm"
            >
              <option value="">All Changes</option>
              <option value="cost">Cost Price Only</option>
              <option value="selling">Selling Price Only</option>
            </select>
          </div>

          {/* Change log */}
          <div className="overflow-hidden rounded-xl border border-border bg-white">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[840px] text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Modified At</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Unit / Location</th>
                    <th className="px-4 py-3 font-medium text-right">Old Price ($)</th>
                    <th className="px-4 py-3 font-medium text-right">New Price ($)</th>
                    <th className="px-4 py-3 font-medium text-right">Change</th>
                    <th className="px-4 py-3 font-medium">Changed By</th>
                  </tr>
                </thead>
                <tbody>
                  {historyLoading ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                        Loading…
                      </td>
                    </tr>
                  ) : history.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <EmptyState
                          icon={History}
                          title="No price changes"
                          description="Changes to this product's price will appear here."
                        />
                      </td>
                    </tr>
                  ) : (
                    history.map((entry) => {
                      const hasOld = entry.oldValue != null && entry.oldValue !== '';
                      const oldVal = hasOld ? parseFloat(String(entry.oldValue)) : 0;
                      const newVal = parseFloat(String(entry.newValue)) || 0;
                      const diff = newVal - oldVal;
                      const isIncrease = diff > 0;
                      const isCost = entry.priceType === 'cost';
                      // Cost ↓ good / ↑ bad; Selling ↑ good / ↓ bad; first entry = green; equal = blue.
                      const changeColor = !hasOld
                        ? 'text-green-600'
                        : diff === 0
                          ? 'text-blue-600'
                          : isCost
                            ? isIncrease
                              ? 'text-red-600'
                              : 'text-green-600'
                            : isIncrease
                              ? 'text-green-600'
                              : 'text-red-600';
                      const locId = entry.locationId ? String(entry.locationId) : null;
                      return (
                        <tr key={String(entry.id)} className="border-b border-border last:border-0 hover:bg-muted/20">
                          <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                            {entry.changedAt ? formatDateTime(String(entry.changedAt)) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={cn(
                                'inline-flex rounded-full px-2 py-0.5 text-xs font-medium border',
                                isCost
                                  ? 'bg-purple-50 text-purple-700 border-purple-200'
                                  : 'bg-blue-50 text-blue-700 border-blue-200',
                              )}
                            >
                              {isCost ? 'Cost' : 'Selling'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs">
                            {entry.unitName ? (
                              <span className="inline-flex rounded-full bg-muted px-2 py-0.5 font-medium text-foreground">
                                {String(entry.unitName)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">product</span>
                            )}
                            {locId && (
                              <span className="ml-1 text-muted-foreground">
                                · {locationNames.get(locId) ?? 'location'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-mono">
                            {hasOld ? formatCurrency(String(entry.oldValue)) : '—'}
                          </td>
                          <td className="px-4 py-3 text-right font-mono">{formatCurrency(String(entry.newValue))}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={cn('font-medium', changeColor)}>
                              {isIncrease ? '+' : ''}
                              {formatCurrency(Math.abs(diff).toFixed(2))}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {entry.changedByName
                              ? String(entry.changedByName)
                              : entry.changedBy
                                ? (userMap.get(String(entry.changedBy)) ?? `${String(entry.changedBy).slice(0, 8)}…`)
                                : '—'}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function PosCustomersByProductReport({ onBack }: { onBack: () => void }) {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const initialRange = initialDailyRange(reportTz);
  const [dateFromInput, setDateFromInput] = useState(initialRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(initialRange.dateTo);
  const [searchInput, setSearchInput] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const searchRef = useRef<HTMLInputElement>(null);

  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string; search: string }>({
    ...initialRange,
    search: '',
  });

  // Debounce search so each keystroke doesn't fire a new request.
  useEffect(() => {
    const t = setTimeout(() => {
      setApplied((prev) => ({ ...prev, search: searchInput.trim() }));
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading } = usePosCustomersByProduct({
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    locationId: selectedLocationId || undefined,
    search: applied.search || undefined,
    page,
    perPage,
  });

  const rows: PosCustomersByProductRow[] = data?.data ?? [];
  const pagination = data?.pagination;
  // Export walks every page of the filtered result, so it can take a moment —
  // disable the button while it runs rather than let a second click start a
  // duplicate download.
  const [exporting, setExporting] = useState(false);

  // Typeahead: live product suggestions as the user types (same UX as Price History).
  const { data: productsData, isLoading: productsLoading } = useProducts(
    { search: applied.search || undefined, perPage: 10 },
    { enabled: dropdownOpen && applied.search.length >= 1 },
  );
  const productResults: Array<{ id: string; name: string; primaryBarcode: string | null }> = productsData?.data ?? [];

  // This box doubles as the report's filter, so the chosen product's name has
  // to stay visible. Selecting the text means a scanner's next burst replaces
  // it outright instead of appending to it (which matched nothing before).
  const selectProduct = (p: { id: string; name: string; primaryBarcode: string | null }) => {
    setSearchInput(p.name);
    setApplied((prev) => ({ ...prev, search: p.name }));
    setPage(1);
    setDropdownOpen(false);
    requestAnimationFrame(() => searchRef.current?.select());
  };

  // Scanner terminator: commit at once rather than waiting out the debounce,
  // and drop the previous product so only the scanned one is listed.
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const term = searchInput.trim();
    const match = findScanMatch(productResults, term);
    if (match) {
      selectProduct(match);
      return;
    }
    setApplied((prev) => ({ ...prev, search: term }));
    setPage(1);
    setDropdownOpen(false);
    requestAnimationFrame(() => searchRef.current?.select());
  };

  const toggleRow = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApply = () => {
    setApplied((prev) => ({ ...prev, dateFrom: dateFromInput, dateTo: dateToInput }));
    setPage(1);
  };

  const handleReset = () => {
    const r = defaultDailyRangeWithTime(reportTz);
    setDateFromInput(r.dateFrom);
    setDateToInput(r.dateTo);
    setSearchInput('');
    setApplied({ ...r, search: '' });
    setDropdownOpen(false);
    setPage(1);
  };

  const handleExport = async () => {
    // Export the WHOLE filtered result, not just the page on screen.
    setExporting(true);
    try {
      const all = await fetchAllReportPages<PosCustomersByProductRow>(
        '/pos/report/customers-by-product',
        {
          dateFrom: applied.dateFrom || undefined,
          dateTo: applied.dateTo || undefined,
          locationId: selectedLocationId || undefined,
          search: applied.search || undefined,
        },
        { tz: reportTz },
      );
      const header = [
        'Product Name',
        'Primary Barcode',
        'Department',
        'Customer Name',
        'Customer Phone',
        'Quantity Bought',
        'Total Spent ($)',
        'Last Purchase',
      ];
      const body: (string | number)[][] = [];
      for (const p of all) {
        if (p.customers.length === 0) {
          body.push([p.name, p.primaryBarcode ?? '', p.departmentName, '', '', '', '', '']);
          continue;
        }
        for (const c of p.customers) {
          body.push([
            p.name,
            p.primaryBarcode ?? '',
            p.departmentName,
            c.name,
            c.phone,
            c.quantity.toFixed(2),
            c.totalSpent.toFixed(2),
            c.lastPurchase ? formatDateTime(c.lastPurchase) : '',
          ]);
        }
      }
      downloadCsv(reportFilename('customers_by_product_report', selectedLocationName), [header, ...body]);
    } catch {
      toast.error('Could not export the report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Customers by Product"
        description="See which customers purchased each product, with quantities and total spend."
        onBack={onBack}
      >
        <PosExportOnly>
          <Button variant="primary" size="sm" disabled={rows.length === 0 || exporting} onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </PosExportOnly>
      </PageHeader>

      {/* Filter card — reusable DatePicker (datetime, reporting-zone aware) */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <ReportRangeDatePicker
            reportTz={reportTz}
            dateFromInput={dateFromInput}
            dateToInput={dateToInput}
            onFromChange={setDateFromInput}
            onToChange={setDateToInput}
          />
          <Button size="sm" variant="primary" onClick={handleApply}>
            Submit
          </Button>
          <Button size="sm" variant="destructive-outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {/* Search row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-72 max-w-full">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            ref={searchRef}
            type="text"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setDropdownOpen(true);
            }}
            onKeyDown={handleSearchKeyDown}
            onFocus={() => setDropdownOpen(true)}
            placeholder="Search or scan products"
            className="h-9 w-full rounded-lg border border-input bg-white pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
          {dropdownOpen && applied.search.length >= 1 && (
            <div className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-border bg-white shadow-lg">
              {productsLoading ? (
                <div className="px-3 py-3 text-sm text-muted-foreground">Searching…</div>
              ) : productResults.length === 0 ? (
                <div className="px-3 py-3 text-sm text-muted-foreground">No products found.</div>
              ) : (
                productResults.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => selectProduct(p)}
                    className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-muted/50"
                  >
                    <span className="font-medium text-foreground">{p.name}</span>
                    {p.primaryBarcode && <span className="text-xs text-muted-foreground">{p.primaryBarcode}</span>}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                <th className="w-10 px-4 py-3" />
                <th className="px-4 py-3 font-medium">Product Name</th>
                <th className="px-4 py-3 font-medium">Department</th>
                <th className="px-4 py-3 font-medium text-right">Customers</th>
                <th className="px-4 py-3 font-medium text-right">Quantity Sold</th>
                <th className="px-4 py-3 font-medium text-right">Total Revenue ($)</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState
                      icon={Users}
                      title="No product sales with customers"
                      description="Try a different date range."
                    />
                  </td>
                </tr>
              ) : (
                rows.map((p) => {
                  const isOpen = expanded.has(p.id);
                  return (
                    <Fragment key={p.id}>
                      <tr
                        className="cursor-pointer border-b border-border hover:bg-muted/30 transition-colors"
                        onClick={() => toggleRow(p.id)}
                      >
                        <td className="px-4 py-3 text-muted-foreground">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </td>
                        <td className="px-4 py-3 font-medium">
                          {p.name}
                          {p.primaryBarcode && (
                            <span className="ml-2 text-xs text-muted-foreground">{p.primaryBarcode}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{p.departmentName || '—'}</td>
                        <td className="px-4 py-3 text-right font-mono">{p.uniqueCustomers.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-mono">{p.quantitySold.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-mono font-semibold">
                          {formatCurrency(p.totalRevenue)}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-muted/20">
                          <td />
                          <td colSpan={5} className="px-4 py-3">
                            {p.customers.length === 0 ? (
                              <p className="text-sm text-muted-foreground">No identified customers.</p>
                            ) : (
                              <div className="overflow-hidden rounded-lg border border-border bg-white">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b border-border bg-gray-50 text-left text-xs uppercase text-muted-foreground">
                                      <th className="px-3 py-2 font-medium">Customer</th>
                                      <th className="px-3 py-2 font-medium">Phone</th>
                                      <th className="px-3 py-2 font-medium text-right">Qty Bought</th>
                                      <th className="px-3 py-2 font-medium text-right">Total Spent</th>
                                      <th className="px-3 py-2 font-medium">Last Purchase</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-border">
                                    {p.customers.map((c) => (
                                      <tr key={c.customerId}>
                                        <td className="px-3 py-2 font-medium">{c.name}</td>
                                        <td className="px-3 py-2 text-muted-foreground">{c.phone || '—'}</td>
                                        <td className="px-3 py-2 text-right font-mono">
                                          {c.quantity.toLocaleString()}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono font-semibold">
                                          {formatCurrency(c.totalSpent)}
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                                          {c.lastPurchase ? formatDateTime(c.lastPurchase) : '—'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination && (
        <DataTablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          perPage={pagination.perPage}
          onPageChange={setPage}
          perPageOptions={[10, 25, 50, 100]}
          onPerPageChange={(val) => {
            setPerPage(val);
            setPage(1);
          }}
        />
      )}
    </div>
  );
}

export function PosReportCardItem({ card, onClick }: { card: PosReportCard; onClick: () => void }) {
  const Icon = card.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-start gap-3 rounded-xl border border-border bg-white p-4 text-left transition-colors hover:border-primary/40 hover:bg-primary/5"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">{card.title}</h3>
          {card.isNew && (
            <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
              <Sparkles className="h-3 w-3" />
              New
            </span>
          )}
        </div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{card.description}</p>
      </div>
    </button>
  );
}

// Roles configurable in the report-visibility matrix. super_admin is omitted —
// it always sees every report and can never be hidden.
const REPORT_VISIBILITY_ROLES: { id: string; label: string }[] = [
  { id: 'owner', label: 'Owner' },
  { id: 'admin', label: 'Admin' },
  { id: 'manager', label: 'Manager' },
  { id: 'employee', label: 'Employee' },
];

/**
 * True if `role` clears a card's `roles` floor. Cards without one are open to
 * everyone; super_admin always passes. Cosmetic only — the API enforces the
 * same floor, so hiding the card is defence in depth, not the control itself.
 */
function canViewReportCard(card: PosReportCard, role: string | undefined): boolean {
  if (!card.roles) return true;
  if (role === 'super_admin') return true;
  return card.roles.includes(role ?? '');
}

const REPORT_VISIBILITY_ROWS: { category: string; categoryId: PosReportCategory; reportId: string; title: string }[] =
  POS_REPORT_CATEGORIES.flatMap((cat) =>
    POS_REPORT_CARDS[cat.id].map((card) => ({
      category: cat.label,
      categoryId: cat.id,
      reportId: card.id,
      title: card.title,
    })),
  );

const cellKey = (reportId: string, role: string) => `${reportId}::${role}`;

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

function ReportVisibilitySettings() {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const { data, isLoading } = useReportVisibilityMatrix(selectedLocationId);
  const update = useUpdateReportVisibility();

  const initialHidden = useMemo(() => {
    const s = new Set<string>();
    (data?.data?.hidden ?? []).forEach((e) => s.add(cellKey(e.reportId, e.role)));
    return s;
  }, [data]);

  const [draft, setDraft] = useState<Set<string> | null>(null);
  const hidden = draft ?? initialHidden;
  const dirty = draft !== null && !setsEqual(draft, initialHidden);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  const visibleRoles =
    roleFilter === 'all' ? REPORT_VISIBILITY_ROLES : REPORT_VISIBILITY_ROLES.filter((r) => r.id === roleFilter);

  const filteredRows = REPORT_VISIBILITY_ROWS.filter((row) => {
    if (categoryFilter !== 'all' && row.categoryId !== categoryFilter) return false;
    if (search.trim() && !row.title.toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  });

  const isVisible = (reportId: string, role: string) => !hidden.has(cellKey(reportId, role));

  const mutate = (fn: (next: Set<string>) => void) => {
    setDraft((prev) => {
      const next = new Set(prev ?? initialHidden);
      fn(next);
      return next;
    });
  };

  const toggleCell = (reportId: string, role: string) => {
    mutate((next) => {
      const k = cellKey(reportId, role);
      if (next.has(k)) next.delete(k);
      else next.add(k);
    });
  };

  // Bulk: show/hide every (filtered row × visible role) cell at once.
  const bulkSet = (visible: boolean) => {
    mutate((next) => {
      for (const row of filteredRows) {
        for (const role of visibleRoles) {
          const k = cellKey(row.reportId, role.id);
          if (visible) next.delete(k);
          else next.add(k);
        }
      }
    });
  };

  const save = async () => {
    if (!selectedLocationId || !draft) return;
    const payload = Array.from(draft).map((k) => {
      const [reportId, role] = k.split('::');
      return { reportId, role };
    });
    try {
      await update.mutateAsync({ locationId: selectedLocationId, hidden: payload });
      setDraft(null);
      toast.success('Report permissions saved');
    } catch (err) {
      const message = (err as { error?: { message?: string } })?.error?.message;
      toast.error(message || 'Failed to save report permissions');
    }
  };

  if (!selectedLocationId) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-white p-12 text-center text-sm text-muted-foreground">
        Select a location to configure report permissions.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-foreground">Report Visibility</h3>
        <p className="text-xs text-muted-foreground">
          Control which roles can see each POS report
          {selectedLocationName ? ` at ${selectedLocationName}` : ''}. Reports are visible by default; uncheck a role to
          hide a report from it. Super Admin always has full access.
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search reports..."
            className="h-9 w-56 rounded-lg border border-input pl-8 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="h-9 rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        >
          <option value="all">All categories</option>
          {POS_REPORT_CATEGORIES.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="h-9 rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        >
          <option value="all">All roles</option>
          {REPORT_VISIBILITY_ROLES.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </select>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => bulkSet(true)}
            className="h-9 rounded-lg border border-border px-3 text-xs font-medium text-foreground hover:bg-muted"
          >
            Show all
          </button>
          <button
            type="button"
            onClick={() => bulkSet(false)}
            className="h-9 rounded-lg border border-border px-3 text-xs font-medium text-foreground hover:bg-muted"
          >
            Hide all
          </button>
        </div>
      </div>

      {/* Matrix */}
      <div className="overflow-x-auto rounded-lg border border-border bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Category</th>
              <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Report</th>
              {visibleRoles.map((r) => (
                <th key={r.id} className="px-3 py-2.5 text-center text-xs font-medium text-muted-foreground">
                  {r.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={2 + visibleRoles.length} className="px-3 py-8 text-center text-muted-foreground">
                  Loading...
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={2 + visibleRoles.length}>
                  <EmptyState
                    icon={SearchX}
                    title="No matching reports"
                    description="Try changing the search or filters."
                  />
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => (
                <tr key={row.reportId} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="px-3 py-2 text-xs text-muted-foreground">{row.category}</td>
                  <td className="px-3 py-2 font-medium text-foreground">{row.title}</td>
                  {visibleRoles.map((role) => {
                    const on = isVisible(row.reportId, role.id);
                    return (
                      <td key={role.id} className="px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => toggleCell(row.reportId, role.id)}
                          aria-label={`${on ? 'Hide' : 'Show'} ${row.title} for ${role.label}`}
                          className={cn(
                            'inline-flex h-7 w-7 items-center justify-center rounded-md border transition-colors',
                            on
                              ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                              : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100',
                          )}
                        >
                          {on ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Save bar */}
      <div className="flex items-center justify-end gap-3">
        {dirty && (
          <button
            type="button"
            onClick={() => setDraft(null)}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={save}
          disabled={!dirty || update.isPending}
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <Save className="h-3.5 w-3.5" />
          {update.isPending ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

/** Which category owns a report card — lets a deep link land on the right tab
 *  without the caller having to know the grouping. */
function categoryForReportId(id: string | null): PosReportCategory | null {
  if (!id) return null;
  for (const [cat, cards] of Object.entries(POS_REPORT_CARDS)) {
    if (cards.some((c) => c.id === id)) return cat as PosReportCategory;
  }
  return null;
}

function PosReport() {
  // The selected report lives in the URL (`#/reports?tab=pos&report=<id>`) so
  // other pages — the home page's "View Details" links — can open a report
  // directly, and a refresh keeps you where you were.
  const [activeReportId, setActiveReportId] = useHashParam('report');
  const [category, setCategory] = useState<PosReportCategory | 'settings'>(
    () => categoryForReportId(readInitialReportId()) ?? 'sales',
  );

  // Promotions sub-tab is per-location super-admin gated via
  // location_pos_config.promotions_enabled (same model as the Promotions
  // tab in POS Management). The category disappears entirely when the
  // toggle is off, mirroring how the merchant-facing tab is hidden.
  const { selectedLocationId } = useLocationStore();
  const { data: posConfig } = useProductsPosConfig(selectedLocationId);
  const promotionsEnabled = posConfig?.promotionsEnabled === true;

  // Report-visibility admin gate + the current user's hidden report ids. The
  // visibility matrix (Settings) stays Owner only (plus platform super admins),
  // by role rather than a key.
  const { user } = useAuthStore();
  const isReportAdmin = usePermissions().atLeast('owner');
  const { data: effectiveHidden } = useEffectiveReportHidden(selectedLocationId);
  const hiddenReportIds = effectiveHidden?.data?.hiddenReportIds ?? [];

  // Reconciliation follows the sidebar's Reconciliation item: a super-admin
  // module gate with no merchant toggle, so it keys on superAdminEnabled.
  const { data: modulesData } = useModules(selectedLocationId);
  const reconciliationEnabled =
    modulesData?.data?.find((m) => m.moduleName === 'reconciliation')?.superAdminEnabled === true;

  const visibleCategories = POS_REPORT_CATEGORIES.filter((c) => c.id !== 'promotions' || promotionsEnabled);

  // Settings is a synthetic admin-only tab handled outside POS_REPORT_CARDS.
  // Non-admins can never land on it (tab hidden + fall back to Sales).
  const showSettings = category === 'settings' && isReportAdmin;

  // Derive the effective category instead of bouncing via setState — if
  // promotions was selected and is now hidden (e.g. a super admin just
  // flipped it OFF), render Sales instead. The stored `category` state
  // catches up the next time the user clicks any tab.
  const effectiveCategory: PosReportCategory =
    category === 'settings' || (!promotionsEnabled && category === 'promotions') ? 'sales' : category;

  // Hide report cards the current user's role isn't allowed to see — first the
  // card's own role floor (sensitive reports), then the per-location matrix.
  const cards = showSettings
    ? []
    : POS_REPORT_CARDS[effectiveCategory].filter(
        (c) =>
          canViewReportCard(c, user?.role) &&
          !hiddenReportIds.includes(c.id) &&
          (!c.requiresReconciliation || reconciliationEnabled),
      );
  const activeReport = activeReportId ? (cards.find((c) => c.id === activeReportId) ?? null) : null;

  // Drill-down view: render the underlying report below a back button.
  if (activeReport) {
    if (activeReport.id === 'sales-trends') {
      return <PosSalesTrendsReport onBack={() => setActiveReportId(null)} />;
    }
    // The Total Daily Sales screen ships its own header + back button,
    // so render it without the generic wrapper.
    if (activeReport.id === 'total-daily-sales') {
      return <PosTotalDailySalesReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'total-daily-department-sales') {
      return <PosTotalDailyDepartmentSalesReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'total-daily-category-sales') {
      return <PosTotalDailyCategorySalesReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'inventory-movement-profitability') {
      return <PosInventoryMovementReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'daily-item-movements') {
      return <PosDailyItemMovementsReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'inventory-valuation-summary') {
      return <PosInventoryValuationReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'inventory-trail') {
      return <PosInventoryTrailReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'price-updates') {
      return <PosPriceUpdatesReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'customers-by-product') {
      return <PosCustomersByProductReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'vendor-by-product') {
      return <PosVendorByProductReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'price-history') {
      return <PosPriceHistoryReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'tax-report') {
      return <PosTaxReportScreen onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'top-items-by-sales-volume') {
      return <PosTopItemsReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'sales-by-vendor') {
      return <PosSalesByVendorReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'all-orders') {
      return <PosAllOrdersReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'pos-card-attempts') {
      return <PosCardAttemptsReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'pos-price-changes') {
      return <PosPriceChangesReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'cash-records') {
      return <PosCashRecordsReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'refunds') {
      return <PosRefundsReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'store-credit') {
      return <PosStoreCreditsReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'employee-work-time') {
      return <PosEmployeeWorkTimeReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'sales-by-cashier') {
      return <PosSalesByCashierReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'discount-report') {
      return <PosDiscountReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'digital-transactions-report') {
      return <PosDigitalTransactionsReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'tills-report') {
      return <PosTillsReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'cash-in') {
      return <PosCashMovementReport key="cash-in" direction="in" onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'cash-out') {
      return <PosCashMovementReport key="cash-out" direction="out" onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'extra-item') {
      return <PosExtraItemReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'variable-item') {
      return <PosVariableItemReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'coupon-usage') {
      return <PosCouponUsageReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'customer-purchase-history') {
      return <PosCustomerPurchaseHistoryReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'promotion-usage') {
      return <PosPromotionReport onBack={() => setActiveReportId(null)} />;
    }
    if (activeReport.id === 'credit-accounts') {
      // Owns its own header so the statement sub-view can replace it entirely
      // (otherwise the generic wrapper below would stack a second "Credit
      // Accounts" header + back button above the statement's own header).
      return (
        <PosCreditAccountsTab
          title={activeReport.title}
          description={activeReport.description}
          onBack={() => setActiveReportId(null)}
        />
      );
    }
    return (
      <div className="space-y-4">
        <PageHeader
          title={activeReport.title}
          description={activeReport.description}
          onBack={() => setActiveReportId(null)}
        />
        {activeReport.id === 'sales-summary-current-location' && <PosSummaryTab />}
        {activeReport.id === 'daily-sales-by-payment-and-station' && <PosPaymentsPerStationTab />}
        {/* key by segment so switching reports remounts and clears page/search
            state instead of carrying a deep page into a smaller result set. */}
        {activeReport.id === 'credit-customers' && <PosCustomersTab key="credit" segment="credit" />}
        {activeReport.id === 'retail-customers' && <PosCustomersTab key="retail" segment="retail" />}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Category tab bar */}
      <div className="border-b border-border overflow-x-auto scrollbar-hide">
        <div className="flex w-full min-w-0" role="tablist" aria-label="POS report categories">
          {visibleCategories.map((tab) => {
            const isSelected = !showSettings && effectiveCategory === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setCategory(tab.id);
                  setActiveReportId(null);
                }}
                className={cn(
                  'relative shrink-0 px-4 py-2.5 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap text-center',
                  isSelected ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                )}
                aria-selected={isSelected}
                role="tab"
              >
                {tab.label}
                {isSelected && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
              </button>
            );
          })}
          {isReportAdmin && (
            <button
              type="button"
              onClick={() => {
                setCategory('settings');
                setActiveReportId(null);
              }}
              className={cn(
                'relative shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap text-center',
                showSettings ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
              aria-selected={showSettings}
              role="tab"
            >
              <Settings className="h-3.5 w-3.5" />
              Settings
              {showSettings && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
            </button>
          )}
        </div>
      </div>

      {showSettings ? (
        <ReportVisibilitySettings />
      ) : cards.length > 0 ? (
        /* Report cards grid */
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {cards.map((card) => (
            <PosReportCardItem key={card.id} card={card} onClick={() => setActiveReportId(card.id)} />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border bg-white p-12 text-center text-sm text-muted-foreground">
          Reports for this category are coming soon.
        </div>
      )}
    </div>
  );
}

export function ReportsPage() {
  const { selectedLocationId } = useLocationStore();
  const { data: modulesData } = useModules();
  const { data: spData, isLoading: spLoading } = useSPDashboard({
    locationId: selectedLocationId || undefined,
  });
  const { data: rpData, isLoading: rpLoading } = useRPDashboard({
    locationId: selectedLocationId || undefined,
  });

  const modules = modulesData?.data ?? [];
  const sp = spData?.data;
  const rp = rpData?.data;

  const payablesMod = modules.find((m) => m.moduleName === 'payables');
  const collectionsMod = modules.find((m) => m.moduleName === 'collections');

  const isPayablesActive = payablesMod?.isActive ?? false;
  const isCollectionsActive = collectionsMod?.isActive ?? false;

  const showPayables = payablesMod !== undefined || (sp != null && sp.payments.total > 0);
  const showCollections = collectionsMod !== undefined || (rp != null && rp.invoices.total > 0);

  const [activeTab, setActiveTab] = usePersistentTab<ReportTab>(REPORT_TABS, 'pos');
  const permissions = usePermissions();

  const tabs = [
    { id: 'receive' as const, label: 'Receive Payments', icon: ArrowDownToLine },
    { id: 'pos' as const, label: 'POS', icon: ShoppingCart },
    { id: 'send' as const, label: 'Send Payments', icon: Send },
  ];

  // Each tab opens with its own View; a saved tab the user can't view falls
  // back to the first one they can.
  const visibleTabs = tabs.filter((t) => permissions.has(REPORT_TAB_VIEW[t.id]));
  const effectiveTab: ReportTab | undefined = visibleTabs.some((t) => t.id === activeTab)
    ? activeTab
    : visibleTabs[0]?.id;

  return (
    <div className="space-y-6">
      <PageHeader title="Reports" description="Module analytics and performance overview">
        <LocationBadge />
      </PageHeader>

      {/* Reporting timezone control — anchors every report's date filter,
          timestamps, and export to one zone (defaults to the business's Pacific
          zone; switchable per user, persisted locally). */}
      <ReportingTimeZoneBar />

      {/* Empty state when no modules were ever activated */}
      {!spLoading && !rpLoading && !showPayables && !showCollections && (
        <div className="rounded-xl border border-border bg-white p-12 text-center">
          <p className="text-muted-foreground">
            No module reports available yet. Activate the <strong>Receive Payments</strong> or{' '}
            <strong>Send Payments</strong> module in Settings to start seeing reports here.
          </p>
        </div>
      )}

      {/* Tabs + Content */}
      {visibleTabs.length > 0 && (
        <>
          {/* Tab bar */}
          <div className="border-b border-border overflow-x-auto scrollbar-hide">
            <div className="flex w-full min-w-0" role="tablist" aria-label="Report tabs">
              {visibleTabs.map((tab) => {
                const isSelected = effectiveTab === tab.id;
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'relative shrink-0 px-4 py-2.5 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap text-center flex items-center gap-2',
                      isSelected ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                    )}
                    aria-selected={isSelected}
                    role="tab"
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                    {isSelected && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab content */}
          <div>
            {effectiveTab === 'send' && (
              <SendPaymentsReport sp={sp} isLoading={spLoading} isActive={isPayablesActive} />
            )}
            {effectiveTab === 'receive' && (
              <ReceivePaymentsReport rp={rp} isLoading={rpLoading} isActive={isCollectionsActive} />
            )}
            {effectiveTab === 'pos' && <PosReport />}
          </div>
        </>
      )}
    </div>
  );
}
